import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { sanitizeReturnTo } from '../utils/navigationReturnTo'
import YouTube from 'react-youtube'
import ChordSelectorModal, { CADENCES } from '../ChordSelectorModal'
import VideoCockpit from '../VideoCockpit'
import { getExerciseById, getExerciseDominantMode } from '../services/exerciseService'
import { getAssignmentById } from '../services/assignmentService'
import { saveAttempt, getAllUserAttempts, getAttemptsByLiveSessionExercise, getLatestUserAttemptByLiveSessionExercise } from '../services/attemptService'
import {
  ackLiveRuntimeCommand,
  advanceLiveSessionExercise,
  getLiveSessionAggregate,
  publishLiveProjectionRuntimeHeartbeat,
  rewindLiveSessionExercise,
  setLiveProjectionPlaybackState,
  setLiveSessionStatus,
  setLiveSessionTimer,
  subscribeLiveRuntimeStateBySessionId,
  subscribeLiveSessionById
} from '../services/liveSessionService'
import { checkAndUnlockBadges } from '../services/badgeService'
import { useAuth } from '../contexts/AuthContext'
import { validateAnswerWithFunctions, DEGREE_TO_FUNCTIONS, normalizeCadence, PRIMARY_DEGREES, getDefaultFunctionForChord, buildChordContext } from '../utils/riemannFunctions'
import { getUserProgress, updateNodeProgress, calculateNodeScore } from '../services/progressionService'
import { getRelevantChordIndices, getChordFunction as getChordFunctionForLock } from '../utils/nodeCriteria'
import { formatChordDetailed, userAnswerToLabel, parseChordDisplayString, getFigureValueFromParsedFigure } from '../utils/chordFormatter'
import { getDegreeLabelForMode } from '../utils/modalScales'
import { formatChordString as formatChordStringQcm } from '../utils/qcmOptions'
import ChordLabel from '../components/ChordLabel'
import { getNodePhase, getEnabledFunctions, getUnlockedChordKeys, getPrecisionFunctions, isCadenceAvailableForNode, getUnlockedCadenceValues, PHASE_INTUITION, PHASE_PRECISION, PHASE_MAITRISE } from '../data/parcoursTree'
import { getCodexEntriesForCorrection } from '../utils/codexHelpers'
import { getCodexContextLinksForExercise, getCodexUrlForEntry } from '../utils/codexContextLinks'
import { getExerciseDisplayTitle, getComposerDisplayName } from '../utils/exerciseDisplay'
import { computePedalGroups } from '../utils/pedalContinuity'
import ConfirmExitModal from '../components/ConfirmExitModal'
import { Play, Pause, SkipBack, SkipForward, Pencil, RotateCcw, Home, ExternalLink, Eye, Smile } from 'lucide-react'
import { LIVE_REMOTE_ENABLED, LIVE_REMOTE_HEARTBEAT_INTERVAL_MS } from '../config/liveFeatures'
import { canonicalizeQcmAnswerString, computeSingleMarkerValidation, getImplicitBorrowedLocalKeyForMarker, qcmAnswerMatchesChordStructurally } from '../utils/exerciseMarkerValidation'
import { resolveTimeFromTimelineX, computePlayheadPositionPercent } from '../utils/timelineSeek'
import {
  buildEmotionCast,
  collectEmotionKeysFromSolutions,
  resolveChordEmotionWithCast,
} from '../utils/resolveChordEmotion'
import './Player.css'

const EMOTION_FEEDBACK_STORAGE_KEY = 'player.emotionFeedback.enabled'

function EmotionFace({ emotionResolved }) {
  const [failed, setFailed] = useState(false)
  const [displaySrc, setDisplaySrc] = useState(emotionResolved?.assetSrc || null)
  const [fadeSrc, setFadeSrc] = useState(null)
  const displaySrcRef = useRef(emotionResolved?.assetSrc || null)
  const src = emotionResolved?.assetSrc || null

  useEffect(() => {
    setFailed(false)
    if (!src) {
      displaySrcRef.current = null
      setDisplaySrc(null)
      setFadeSrc(null)
      return
    }
    if (src === displaySrcRef.current) return
    if (!displaySrcRef.current) {
      displaySrcRef.current = src
      setDisplaySrc(src)
      return
    }
    setFadeSrc(src)
  }, [src])

  const handleFadeEnd = () => {
    setFadeSrc((current) => {
      if (current) {
        displaySrcRef.current = current
        setDisplaySrc(current)
      }
      return null
    })
  }

  if (!emotionResolved) return null
  const showFallback = (!src && !displaySrc) || failed

  return (
    <div
      className={`player-emotion-film-face${emotionResolved.isBorrowed ? ' player-emotion-film-face--borrowed' : ''}${showFallback ? ' player-emotion-film-face--fallback' : ''}`}
      style={{ borderColor: emotionResolved.borderColor }}
    >
      {displaySrc && !failed ? (
        <img
          src={displaySrc}
          alt=""
          className="player-emotion-film-img player-emotion-film-img--base"
          draggable={false}
          onError={() => setFailed(true)}
        />
      ) : null}
      {fadeSrc && !failed ? (
        <img
          key={fadeSrc}
          src={fadeSrc}
          alt=""
          className="player-emotion-film-img player-emotion-film-img--fade"
          draggable={false}
          onAnimationEnd={handleFadeEnd}
          onError={() => {
            setFadeSrc(null)
            setFailed(true)
          }}
        />
      ) : null}
      {showFallback ? (
        <div className="player-emotion-film-neutral" aria-hidden>
          <span className="player-emotion-film-neutral-face" />
        </div>
      ) : null}
    </div>
  )
}

function normalizeQcmAnswerToChordLike(chord) {
  if (!chord || typeof chord !== 'object') return chord
  if (chord.degree || chord.root || chord.specialRoot || chord.pedalDegree || chord.displayLabel) return chord
  const qcm = typeof chord.qcmAnswer === 'string' ? chord.qcmAnswer.trim() : ''
  if (!qcm) return chord
  const parsed = parseChordDisplayString(qcm)
  const leading = (parsed.leading || '').trim()
  const romanMatch = leading.match(/^(?:[♭#♯b]\s*)?(VII|VI|IV|III|II|I|V)(?:°)?$/i)
  const degree = romanMatch ? romanMatch[1].toUpperCase() : undefined
  const figure = getFigureValueFromParsedFigure(parsed.figure) || undefined
  const pedalDegree = parsed.pedalDegree || undefined
  if (!degree && !pedalDegree) return chord
  return { ...chord, degree, figure, pedalDegree }
}

function inferPedalFromChordLabel(chord) {
  if (!chord || typeof chord !== 'object' || chord.pedalBlocked === true) return null
  const explicitPedal = chord.pedalDegree != null ? String(chord.pedalDegree).trim().toUpperCase() : ''
  if (explicitPedal) return explicitPedal
  const secondaryTarget = chord.borrowedFromDegree ?? chord.ofDegree
  if (secondaryTarget != null && String(secondaryTarget).trim() !== '') return null
  const str = typeof chord.displayLabel === 'string' && chord.displayLabel.trim() !== ''
    ? chord.displayLabel.trim()
    : formatChordDetailed(chord)
  const parsed = parseChordDisplayString(str)
  const parsedPedal = parsed?.pedalDegree != null ? String(parsed.pedalDegree).trim().toUpperCase() : ''
  // #region agent log
  if (typeof fetch !== 'undefined' && (str.includes('/') || chord.borrowedFromDegree || chord.ofDegree)) {
    fetch('http://127.0.0.1:7245/ingest/35ff180e-d8aa-4991-b134-f4ff94edefe8', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '393bcc' }, body: JSON.stringify({ sessionId: '393bcc', hypothesisId: 'H2', location: 'Player.jsx:inferPedalFromChordLabel', message: 'infer pedal from label', data: { degree: chord.degree, figure: chord.figure, borrowedFromDegree: chord.borrowedFromDegree ?? null, ofDegree: chord.ofDegree ?? null, str, parsedPedal: parsedPedal || null }, timestamp: Date.now() }) }).catch(() => {})
  }
  // #endregion
  return parsedPedal || null
}

function buildPedalAwareCorrectAnswers(markers = [], exercise = null) {
  const base = Array.isArray(markers)
    ? markers.map((marker) => (typeof marker === 'object' && marker?.chord ? marker.chord : null))
    : []
  if (base.length === 0) return []

  const seeded = base.map((chord) => {
    if (!chord || typeof chord !== 'object' || chord.pedalBlocked === true) return chord
    const pedal = inferPedalFromChordLabel(chord)
    return pedal ? { ...chord, pedalDegree: pedal } : chord
  })

  const tonality = exercise?.metadata?.tonality || 'C'
  const mode = getExerciseDominantMode(exercise) || 'major'
  const { effectiveDegrees } = computePedalGroups(seeded, { tonality, mode })
  return seeded.map((chord, index) => {
    if (!chord || typeof chord !== 'object' || chord.pedalBlocked === true) return chord
    const explicitPedal = chord.pedalDegree != null ? String(chord.pedalDegree).trim().toUpperCase() : ''
    if (explicitPedal) return chord
    const inferred = effectiveDegrees?.[index] != null ? String(effectiveDegrees[index]).trim().toUpperCase() : ''
    return inferred ? { ...chord, pedalDegree: inferred } : chord
  })
}

function stripTrailingPedalSlash(text) {
  if (typeof text !== 'string') return text
  return text.replace(/\s*\/\s*(I|II|III|IV|V|VI|VII)\s*$/i, '').trim()
}

function buildQcmDebugSignature(value) {
  if (typeof value !== 'string') return null
  const chars = Array.from(value)
  return {
    raw: value,
    trimmed: value.trim(),
    lower: value.trim().toLowerCase(),
    length: value.length,
    codes: chars.map((char) => char.charCodeAt(0))
  }
}

function getLiveWaitingStorageKey(userId, liveSessionId, exerciseId) {
  return `opus_live_waiting:${String(userId || '')}:${String(liveSessionId || '')}:${String(exerciseId || '')}`
}

function normalizeLiveInputMode(mode) {
  const value = String(mode || '').trim()
  return ['functions', 'qcm', 'full'].includes(value) ? value : 'full'
}

function normalizeFreeInputMode(mode) {
  const value = String(mode || '').trim()
  // Défaut app / invité : guidé (QCM). Seul 'full' force le mode complet.
  return value === 'full' ? 'full' : 'qcm'
}

/** Son « correction / solution » au clic « Voir la correction » (aperçu prof). Court motif de résolution : tension → réponse. */
function playRevealSound() {
  try {
    const Ctx = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext)
    if (!Ctx) return
    const ctx = new Ctx()
    const play = () => {
      const now = ctx.currentTime
      // Tension (courte) puis résolution sur la tonique = effet "voici la solution"
      const tension = 659.25   // E5
      const resolution = 523.25 // C5 (tonique)
      const gainNode = ctx.createGain()
      gainNode.connect(ctx.destination)
      gainNode.gain.setValueAtTime(0, now)

      const osc1 = ctx.createOscillator()
      osc1.type = 'sine'
      osc1.frequency.setValueAtTime(tension, now)
      osc1.connect(gainNode)
      osc1.start(now)
      osc1.stop(now + 0.09)
      gainNode.gain.linearRampToValueAtTime(0.09, now + 0.02)
      gainNode.gain.linearRampToValueAtTime(0.001, now + 0.09)

      const osc2 = ctx.createOscillator()
      osc2.type = 'sine'
      osc2.frequency.setValueAtTime(resolution, now + 0.06)
      osc2.connect(gainNode)
      osc2.start(now + 0.06)
      osc2.stop(now + 0.26)
      gainNode.gain.setValueAtTime(0.1, now + 0.06)
      gainNode.gain.linearRampToValueAtTime(0.05, now + 0.16)
      gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.26)
    }
    if (ctx.state === 'suspended') ctx.resume().then(play)
    else play()
  } catch (_) { /* ignoré si AudioContext indisponible */ }
}

/** Fondu de sortie : si getCurrentTime saute un tick (>YouTube, pas 50ms), timeUntilEnd peut passer de >0.5 à ≤0 sans jamais être dans ]0,0.5] — il faut traiter timeUntilEnd ≤ 0 comme volume minimal avant pause. */
function applyYoutubeFadeOutVolume(player, timeUntilEnd, fadeDuration) {
  if (!player || typeof player.setVolume !== 'function') return
  if (timeUntilEnd > fadeDuration) return
  const effectiveT = Math.max(0, timeUntilEnd)
  const fadeOutProgress = 1 - effectiveT / fadeDuration
  const volume = Math.max(0, 100 - fadeOutProgress * 100)
  try {
    player.setVolume(volume)
    // #region agent log
    if (typeof window !== 'undefined' && !window.__opusFadeOutVolLogged) {
      window.__opusFadeOutVolLogged = true
      let gv = null
      try { gv = typeof player.getVolume === 'function' ? player.getVolume() : null } catch (_) { gv = 'err' }
      fetch('http://127.0.0.1:7245/ingest/35ff180e-d8aa-4991-b134-f4ff94edefe8', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'cfe28f' }, body: JSON.stringify({ sessionId: 'cfe28f', runId: 'vol-api', hypothesisId: 'H_VOL', location: 'Player.jsx:applyYoutubeFadeOutVolume', message: 'fade-out setVolume', data: { timeUntilEnd, appliedVolume: volume, getVolumeAfter: gv }, timestamp: Date.now() }) }).catch(() => {})
    }
    // #endregion
  } catch (_) { /* API YouTube */ }
}

/** Une fois par lecture : confirme qu’on reçoit des ticks dans ]0, fadeDuration] avant la fin (H4/H5). */
function logYoutubeFadeWindowEnteredOnce(timeUntilEnd, fadeDuration, currentTime, endTime, source) {
  if (typeof window === 'undefined') return
  if (timeUntilEnd > fadeDuration || timeUntilEnd <= 0) return
  if (window.__opusFadeWindowEntered) return
  window.__opusFadeWindowEntered = true
  // #region agent log
  fetch('http://127.0.0.1:7245/ingest/35ff180e-d8aa-4991-b134-f4ff94edefe8', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'cfe28f' }, body: JSON.stringify({ sessionId: 'cfe28f', runId: 'fade-debug', hypothesisId: 'H4_H5', location: 'Player.jsx:logYoutubeFadeWindowEnteredOnce', message: 'Entered fade-out time window', data: { source, timeUntilEnd, fadeDuration, currentTime, endTime }, timestamp: Date.now() }) }).catch(() => {})
  // #endregion
}

function reassertYoutubeVolumeZeroAfterPlay(playerRef) {
  requestAnimationFrame(() => {
    try {
      if (playerRef?.current && typeof playerRef.current.setVolume === 'function') {
        playerRef.current.setVolume(0)
      }
    } catch (_) { /* ignoré */ }
  })
}

function Player() {
  const buildAggregateFromAttempts = useCallback((attempts = []) => {
    const list = Array.isArray(attempts) ? attempts : []
    if (list.length === 0) return { questions: [] }
    const sample = list[0] || {}
    const totalQuestions = Array.isArray(sample.correctAnswers) ? sample.correctAnswers.length : 0
    const questions = []
    for (let i = 0; i < totalQuestions; i += 1) {
      const counts = new Map()
      let answered = 0
      list.forEach((attempt) => {
        const answer = Array.isArray(attempt.userAnswers) ? attempt.userAnswers[i] : null
        const label = userAnswerToLabel(answer)
        counts.set(label, (counts.get(label) || 0) + 1)
        if (answer) answered += 1
      })
      const total = list.length || 1
      const topAnswers = Array.from(counts.entries())
        .map(([label, count]) => ({ label, count, percent: Math.round((count / total) * 100) }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5)
      const correct = Array.isArray(sample.correctAnswers) ? sample.correctAnswers[i] : null
      const correctLabel = correct ? (typeof correct === 'string' ? correct : formatChordDetailed(correct)) : 'Non répondu'
      questions.push({
        index: i + 1,
        correctLabel,
        topAnswers,
        responseRate: Math.round((answered / total) * 100)
      })
    }
    return { questions }
  }, [])

  const { exerciseId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const location = useLocation()
  const returnTo = sanitizeReturnTo(location.state?.returnTo, null)
  const isTeacherPreview = location.state?.teacherPreview === true
  const assignmentIdFromState = location.state?.assignmentId || searchParams.get('assignment') || null
  const liveSessionIdFromState = location.state?.liveSessionId || searchParams.get('liveSession') || null
  const liveCorrectionReleased = location.state?.liveCorrectionReleased === true
  const shouldMuteForLiveStudent = Boolean(liveSessionIdFromState && !isTeacherPreview)
  const { user, userData, isGuest, betaPrivateActive, hasFullCloudAccess } = useAuth()
  const [exercise, setExercise] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  
  // Détecter le mode depuis l'URL
  const exerciseMode = searchParams.get('mode') || 'libre' // 'parcours' | 'libre'
  const nodeId = searchParams.get('node') || null
  
  // États de l'exercice
  const [mode, setMode] = useState('exercise') // 'exercise' | 'review'
  const [currentMarkerIndex, setCurrentMarkerIndex] = useState(0)
  const [selectedMarkerId, setSelectedMarkerId] = useState(null) // Marqueur actuellement sélectionné pour navigation
  const [userAnswers, setUserAnswers] = useState({}) // { markerIndex: chordData }
  const [answerValidations, setAnswerValidations] = useState({}) // { markerIndex: { level, score, feedback } }
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [showOnboarding, setShowOnboarding] = useState(true)
  const [isValidating, setIsValidating] = useState(false) // État pour l'animation de validation
  const [animatedChords, setAnimatedChords] = useState(new Set()) // Accords déjà animés
  const [selectedSegmentIndex, setSelectedSegmentIndex] = useState(null) // Segment sélectionné en mode review
  const [segmentTooltipIndex, setSegmentTooltipIndex] = useState(null) // Index du segment dont on affiche le tooltip (accord + feedback)
  const [emotionFeedbackEnabled, setEmotionFeedbackEnabled] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem(EMOTION_FEEDBACK_STORAGE_KEY) === '1'
  })
  const [emotionCast, setEmotionCast] = useState(null) // Record<emotionKey, fileName> figé pour l'exercice
  const emotionCastExerciseIdRef = useRef(null)

  // États pour le mode parcours
  const [progressionMode, setProgressionMode] = useState('qcm') // 'functions' | 'qcm' | 'full'
  const freeInputMode = useMemo(() => {
    const fromQuery = searchParams.get('inputMode') || searchParams.get('input')
    if (fromQuery) return normalizeFreeInputMode(fromQuery)
    if (typeof window === 'undefined') return 'qcm'
    return normalizeFreeInputMode(window.localStorage.getItem('opus:free-input-mode'))
  }, [searchParams]) // 'qcm' | 'full' (mode libre uniquement)
  const [parcoursContext, setParcoursContext] = useState(null)
  const [nodeStatsForQCM, setNodeStatsForQCM] = useState(null) // nodeStats[nodeId] pour QCM adaptatif
  const [relevantChordIndices, setRelevantChordIndices] = useState([])
  const [newlyUnlockedBadges, setNewlyUnlockedBadges] = useState([]) // badges débloqués à l'issue de cet exercice (affichés en bannière)
  const [lastSaveFeedback, setLastSaveFeedback] = useState(null)
  const [assignmentContext, setAssignmentContext] = useState(null)
  const [liveSessionMeta, setLiveSessionMeta] = useState(null)
  const [liveRuntimeState, setLiveRuntimeState] = useState(null)
  const [liveTimerNowMs, setLiveTimerNowMs] = useState(Date.now())
  const [liveTimerControlSec, setLiveTimerControlSec] = useState(120)
  const [liveTimerDurationInput, setLiveTimerDurationInput] = useState('120')
  const [liveTimerBusy, setLiveTimerBusy] = useState(false)
  const [liveExerciseAggregate, setLiveExerciseAggregate] = useState(null)
  const [liveAdvanceBusy, setLiveAdvanceBusy] = useState(false)
  const pedalAwareCorrectAnswers = useMemo(
    () => buildPedalAwareCorrectAnswers(exercise?.markers ?? [], exercise),
    [exercise?.markers, exercise]
  )

  const exercisePedalOptions = useMemo(
    () => ({
      tonality: exercise?.metadata?.tonality || 'C',
      mode: getExerciseDominantMode(exercise) || 'major'
    }),
    [exercise]
  )
  
  const playerRef = useRef(null)
  /** Derniers playerVars passés à react-youtube (pour logs onReady vs iframe réelle). */
  const ytOptsDebugRef = useRef(null)
  const playbackRafRef = useRef(null)
  const currentTimeRef = useRef(0)
  const lastSetStateTimeRef = useRef(0)
  const playheadRef = useRef(null)
  const reviewPlayheadRef = useRef(null)
  const playheadTimelineRef = useRef({ isMobile: false, chordSegments: [], desktopSegments: [], startTime: 0, endTime: 60 })
  const lastHandledRemoteSeqRef = useRef(0)
  const skipNextPlayNormalizationRef = useRef(false)
  const suppressPlayStateSyncRef = useRef(false)
  const isPlayingRef = useRef(false)
  const timelineOverlayRef = useRef(null)
  const scrubDragRef = useRef({ active: false, pointerId: null, startX: 0, startY: 0, moved: false })
  /** Position à appliquer au prochain onReady (remontage du lecteur YouTube, ex. passage en mode review). */
  const playerSyncOnReadyRef = useRef(null)
  const prevModeRef = useRef(mode)
  /** Évite de casser les fondus en nettoyant les intervalles quand isPlaying est déjà false sans transition lecture → pause. */
  const wasPlayingForFadeCleanupRef = useRef(false)
  const timelineViewportRef = useRef(null)
  const timelineRef = useRef(null)
  const videoZoneOverlayRef = useRef(null)
  const quitButtonRef = useRef(null)
  /** En aperçu prof : rects des cases en mode exercice (avant passage en correction) pour animation FLIP. */
  const exerciseSegmentRectsRef = useRef(null)
  /** Transforms FLIP par segment (scale + translate) pour lier tailles exercice → correction. */
  const [segmentFlipTransforms, setSegmentFlipTransforms] = useState(null)
  const [isQuitConfirmOpen, setIsQuitConfirmOpen] = useState(false)
  const DESKTOP_MIN_SEGMENT_PX = 72
  const isIOSDevice = useMemo(() => {
    if (typeof navigator === 'undefined') return false
    const ua = navigator.userAgent || ''
    // Windows / Android / Chrome OS : jamais la branche « iOS » (sinon pas de fondu + comportements audio incorrects sur PC)
    if (/Windows NT|Win64|Win32|Android|CrOS/i.test(ua)) return false
    try {
      const ch = navigator.userAgentData?.platform
      if (typeof ch === 'string' && /windows|android|chrome os/i.test(ch)) return false
    } catch (_) { /* ignore */ }
    const platform = navigator.platform || ''
    const maxTouchPoints = navigator.maxTouchPoints || 0
    return /iPad|iPhone|iPod/.test(ua) || (platform === 'MacIntel' && maxTouchPoints > 1)
  }, [])

  /** Fondus YouTube : désactivés sur iOS élève (fiabilité) ; conservés sur la projection live (aperçu prof). */
  const allowYoutubeAudioFade = useMemo(
    () => !isIOSDevice || Boolean(isTeacherPreview && liveSessionIdFromState),
    [isIOSDevice, isTeacherPreview, liveSessionIdFromState]
  )

  const ensureIOSAudiblePlayback = useCallback(() => {
    if (!isIOSDevice || !playerRef.current || shouldMuteForLiveStudent) return
    try {
      playerRef.current.unMute?.()
      playerRef.current.setVolume?.(100)
    } catch (error) {
      console.warn('Player iOS: impossible de forcer un état audio audible', error?.message)
    }
  }, [isIOSDevice, shouldMuteForLiveStudent])

  // Détection mobile pour timeline en bandes égales (breakpoint aligné avec le CSS)
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches)
  const [timelineViewportWidth, setTimelineViewportWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 0))
  const getTimerRemainingSeconds = useCallback((timer, nowMs = Date.now()) => {
    const durationSec = Number(timer?.durationSec || 120)
    const baseRemaining = Number.isFinite(timer?.remainingSec) ? Number(timer.remainingSec) : durationSec
    if (!timer?.startedAt || timer?.paused) return Math.max(0, baseRemaining)
    const startedMs = timer.startedAt?.toDate?.()?.getTime?.() || (typeof timer.startedAt === 'number' ? timer.startedAt : new Date(timer.startedAt).getTime())
    if (!startedMs || Number.isNaN(startedMs)) return Math.max(0, baseRemaining)
    const elapsed = Math.max(0, Math.floor((nowMs - startedMs) / 1000))
    return Math.max(0, baseRemaining - elapsed)
  }, [])
  const liveProjectionTimerLabel = useMemo(() => {
    const remain = getTimerRemainingSeconds(liveSessionMeta?.timer, liveTimerNowMs)
    const mm = String(Math.floor(remain / 60)).padStart(2, '0')
    const ss = String(remain % 60).padStart(2, '0')
    return `${mm}:${ss}`
  }, [getTimerRemainingSeconds, liveSessionMeta?.timer, liveTimerNowMs])
  const liveInputMode = useMemo(() => {
    if (!liveSessionIdFromState) return null
    return normalizeLiveInputMode(liveSessionMeta?.inputMode)
  }, [liveSessionIdFromState, liveSessionMeta?.inputMode])

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)')
    const handler = () => setIsMobile(mq.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  useEffect(() => {
    if (isMobile) return undefined
    const viewportEl = timelineViewportRef.current
    if (!viewportEl) return undefined

    const updateTimelineWidth = () => {
      const width = viewportEl.clientWidth || viewportEl.getBoundingClientRect().width || 0
      setTimelineViewportWidth(width)
    }

    updateTimelineWidth()
    let observer = null
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(updateTimelineWidth)
      observer.observe(viewportEl)
    }
    window.addEventListener('resize', updateTimelineWidth)

    return () => {
      if (observer) observer.disconnect()
      window.removeEventListener('resize', updateTimelineWidth)
    }
  }, [isMobile, mode, exerciseId, exercise?.markers?.length])

  useEffect(() => {
    let cancelled = false

    if (!assignmentIdFromState || !user) {
      setAssignmentContext(null)
      return () => { cancelled = true }
    }

    getAssignmentById(assignmentIdFromState)
      .then((assignment) => {
        if (cancelled || !assignment) return
        const canUseAsTeacher = assignment.teacherId === user.uid
        const canUseAsStudent = userData?.role === 'student' && assignment.teacherClassId === userData?.teacherClassId
        if (!canUseAsTeacher && !canUseAsStudent) {
          setAssignmentContext(null)
          return
        }
        setAssignmentContext({
          id: assignment.id,
          teacherId: assignment.teacherId || null,
          teacherClassId: assignment.teacherClassId || null
        })
      })
      .catch(() => {
        if (!cancelled) setAssignmentContext(null)
      })

    return () => { cancelled = true }
  }, [assignmentIdFromState, user, userData?.role, userData?.teacherClassId])

  useEffect(() => {
    if (!liveSessionIdFromState) {
      setLiveSessionMeta(null)
      setLiveRuntimeState(null)
      return undefined
    }
    return subscribeLiveSessionById(
      liveSessionIdFromState,
      (session) => setLiveSessionMeta(session || null),
      () => setLiveSessionMeta(null)
    )
  }, [liveSessionIdFromState, liveCorrectionReleased, isTeacherPreview])

  useEffect(() => {
    if (!LIVE_REMOTE_ENABLED || !isTeacherPreview || !liveSessionIdFromState) {
      setLiveRuntimeState(null)
      return undefined
    }
    return subscribeLiveRuntimeStateBySessionId(
      liveSessionIdFromState,
      (runtime) => setLiveRuntimeState(runtime || null),
      () => setLiveRuntimeState(null)
    )
  }, [isTeacherPreview, liveSessionIdFromState])

  useEffect(() => {
    lastHandledRemoteSeqRef.current = 0
  }, [liveSessionMeta?.id])

  useEffect(() => {
    if (!LIVE_REMOTE_ENABLED) return undefined
    if (!isTeacherPreview || !liveSessionMeta?.id || !user?.uid) return undefined
    let cancelled = false

    const publish = () => {
      publishLiveProjectionRuntimeHeartbeat(liveSessionMeta.id, user.uid).catch(() => {})
    }
    publish()
    const id = setInterval(() => {
      if (!cancelled) publish()
    }, LIVE_REMOTE_HEARTBEAT_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [isTeacherPreview, liveSessionMeta?.id, user?.uid])

  useEffect(() => {
    if (!LIVE_REMOTE_ENABLED) return
    if (!isTeacherPreview || !liveSessionMeta?.id || !user?.uid) return
    setLiveProjectionPlaybackState(liveSessionMeta.id, user.uid, isPlaying).catch(() => {})
  }, [isTeacherPreview, isPlaying, liveSessionMeta?.id, user?.uid])

  useEffect(() => {
    if (!liveInputMode) return
    setProgressionMode(liveInputMode)
    setParcoursContext(null)
    setNodeStatsForQCM(null)
    setRelevantChordIndices([])
  }, [liveInputMode])

  useEffect(() => {
    const nextDuration = Number(liveSessionMeta?.timer?.durationSec || 120)
    setLiveTimerControlSec(Math.max(10, nextDuration))
  }, [liveSessionMeta?.timer?.durationSec])

  useEffect(() => {
    setLiveTimerDurationInput(String(liveTimerControlSec))
  }, [liveTimerControlSec])

  useEffect(() => {
    let cancelled = false
    if (!isTeacherPreview || !liveSessionIdFromState || !exerciseId || liveSessionMeta?.status !== 'correction_released') {
      setLiveExerciseAggregate(null)
      return () => { cancelled = true }
    }
    getLiveSessionAggregate(liveSessionIdFromState, exerciseId)
      .then(async (aggregate) => {
        if (cancelled) return
        if (aggregate && Array.isArray(aggregate.questions) && aggregate.questions.length > 0) {
          setLiveExerciseAggregate(aggregate)
          return
        }
        const attempts = await getAttemptsByLiveSessionExercise(liveSessionIdFromState, exerciseId, 600)
        if (!cancelled) setLiveExerciseAggregate(buildAggregateFromAttempts(attempts))
      })
      .catch(() => {
        if (!cancelled) setLiveExerciseAggregate(null)
      })
    return () => { cancelled = true }
  }, [isTeacherPreview, liveSessionIdFromState, exerciseId, liveSessionMeta?.status, buildAggregateFromAttempts])

  useEffect(() => {
    if (!isTeacherPreview || !liveSessionMeta?.timer || liveSessionMeta.timer.paused) return undefined
    const id = setInterval(() => setLiveTimerNowMs(Date.now()), 1000)
    return () => clearInterval(id)
  }, [isTeacherPreview, liveSessionMeta?.timer?.startedAt, liveSessionMeta?.timer?.paused])

  useEffect(() => {
    if (mode !== 'live_waiting') return
    if (liveCorrectionReleased || liveSessionMeta?.status === 'correction_released') {
      const safeChordCount = Array.isArray(exercise?.markers) ? exercise.markers.length : 0
      setMode('review')
      if (safeChordCount > 0) setSelectedSegmentIndex(0)
    }
  }, [mode, liveCorrectionReleased, liveSessionMeta?.status, exercise?.markers?.length])

  useEffect(() => {
    const isLiveStudent = Boolean(liveSessionIdFromState && !isTeacherPreview)
    const correctionIsReleased = liveSessionMeta?.status === 'correction_released'
    if (!isLiveStudent || !correctionIsReleased) return
    setIsModalOpen(false)
    if (mode !== 'review') {
      setMode('review')
      if (Array.isArray(exercise?.markers) && exercise.markers.length > 0) {
        setSelectedSegmentIndex((prev) => (prev == null ? 0 : prev))
      }
    }
  }, [liveSessionIdFromState, isTeacherPreview, liveSessionMeta?.status, mode, exercise?.markers])

  useEffect(() => {
    const correctionIsReleased = Boolean(liveCorrectionReleased || liveSessionMeta?.status === 'correction_released')
    if (!correctionIsReleased) {
      setMode('exercise')
      setSelectedSegmentIndex(null)
      setSegmentTooltipIndex(null)
      setIsModalOpen(false)
    }
  }, [exerciseId, liveCorrectionReleased, liveSessionMeta?.status])

  const liveQueue = Array.isArray(liveSessionMeta?.exerciseQueue) ? liveSessionMeta.exerciseQueue : []
  const liveCurrentIndex = Number(liveSessionMeta?.currentExerciseIndex || 0)
  const liveCurrentExercise = liveQueue[liveCurrentIndex] || null
  const liveCurrentExerciseId = String(liveCurrentExercise?.exerciseId || '').trim()
  const liveNextExercise = liveQueue[liveCurrentIndex + 1] || null
  const liveNextExerciseId = String(liveNextExercise?.exerciseId || '').trim()
  const liveHasNextExercise = Boolean(liveNextExerciseId)
  const isViewingCurrentLiveExercise = Boolean(
    liveSessionIdFromState
    && liveCurrentExerciseId
    && String(exerciseId || '').trim() === liveCurrentExerciseId
  )
  const liveSessionMovedToAnotherExercise = Boolean(
    liveSessionIdFromState
    && liveCurrentExerciseId
    && String(exerciseId || '').trim()
    && String(exerciseId || '').trim() !== liveCurrentExerciseId
  )
  const liveWaitingStorageKey = useMemo(() => {
    if (!user?.uid || !liveSessionIdFromState || !exerciseId || isTeacherPreview) return ''
    return getLiveWaitingStorageKey(user.uid, liveSessionIdFromState, exerciseId)
  }, [user?.uid, liveSessionIdFromState, exerciseId, isTeacherPreview])

  const normalizeAttemptAnswersMap = useCallback((rawAnswers) => {
    if (Array.isArray(rawAnswers)) {
      return rawAnswers.reduce((acc, answer, index) => {
        acc[index] = answer ?? null
        return acc
      }, {})
    }
    if (rawAnswers && typeof rawAnswers === 'object') {
      return Object.entries(rawAnswers).reduce((acc, [key, answer]) => {
        const numericKey = Number(key)
        if (!Number.isInteger(numericKey) || numericKey < 0) return acc
        acc[numericKey] = answer ?? null
        return acc
      }, {})
    }
    return {}
  }, [])

  const hasAnyAnsweredChord = useCallback((answersMap) => (
    Object.values(answersMap || {}).some((value) => value != null)
  ), [])

  useEffect(() => {
    if (isTeacherPreview) return
    if (!liveSessionMovedToAnotherExercise || !liveCurrentExerciseId) return
    openLiveExercise(liveCurrentExerciseId, { forceCorrectionReleased: false })
  }, [isTeacherPreview, liveSessionMovedToAnotherExercise, liveCurrentExerciseId, exerciseId, liveSessionMeta?.status, mode])

  useEffect(() => {
    const isLiveStudentSession = Boolean(liveSessionIdFromState && !isTeacherPreview)
    if (!isLiveStudentSession) return
    if (!liveSessionMeta || liveSessionMeta.status !== 'closed') return
    if (liveWaitingStorageKey) {
      try {
        if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(liveWaitingStorageKey)
      } catch {}
    }
    setIsModalOpen(false)
    navigate(returnTo || '/student-dashboard', { replace: true })
  }, [
    liveSessionIdFromState,
    isTeacherPreview,
    liveSessionMeta,
    liveWaitingStorageKey,
    navigate,
    returnTo
  ])

  useEffect(() => {
    if (!liveWaitingStorageKey) return
    try {
      if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(liveWaitingStorageKey) === '1') {
        const correctionReleasedNow = Boolean(liveCorrectionReleased || liveSessionMeta?.status === 'correction_released')
        if (!correctionReleasedNow) setMode('live_waiting')
      }
    } catch {}
  }, [liveWaitingStorageKey, liveCorrectionReleased, liveSessionMeta?.status])

  useEffect(() => {
    if (!liveWaitingStorageKey) return
    const correctionReleasedNow = Boolean(liveCorrectionReleased || liveSessionMeta?.status === 'correction_released')
    if (!correctionReleasedNow) return
    try {
      if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(liveWaitingStorageKey)
    } catch {}
  }, [liveWaitingStorageKey, liveCorrectionReleased, liveSessionMeta?.status])

  const updateLiveTimer = useCallback(async (timerPatch) => {
    if (!isTeacherPreview || !liveSessionMeta?.id || !user?.uid || liveTimerBusy) return
    setLiveTimerBusy(true)
    try {
      await setLiveSessionTimer(liveSessionMeta.id, user.uid, timerPatch)
    } catch (error) {
      console.error('Timer live: mise à jour impossible', error)
    } finally {
      setLiveTimerBusy(false)
    }
  }, [isTeacherPreview, liveSessionMeta?.id, user?.uid, liveTimerBusy])

  const getLiveErrorsForSegment = useCallback((segmentIndex) => {
    const questions = Array.isArray(liveExerciseAggregate?.questions) ? liveExerciseAggregate.questions : []
    const q = questions[segmentIndex]
    if (!q) return null
    const correctLabel = String(q.correctLabel || '').trim()
    const topAnswers = Array.isArray(q.topAnswers) ? q.topAnswers : []
    const wrong = topAnswers
      .filter((row) => {
        const label = String(row?.label || '').trim()
        return label && label !== correctLabel && label !== 'Non répondu'
      })
      .map((row) => ({
        label: String(row.label || ''),
        count: Number(row.count || 0),
        percent: Number(row.percent || 0)
      }))
      .filter((row) => row.count > 0)
      .sort((a, b) => b.count - a.count)
    if (wrong.length === 0) return { wrong: [], correctLabel, responseRate: Number(q.responseRate || 0) }
    const maxCount = wrong.reduce((acc, row) => Math.max(acc, row.count), 1)
    return {
      wrong,
      correctLabel,
      responseRate: Number(q.responseRate || 0),
      maxCount
    }
  }, [liveExerciseAggregate])

  // Auto-dismiss bannière badges débloqués après 5 secondes
  useEffect(() => {
    if (newlyUnlockedBadges.length === 0) return
    const t = setTimeout(() => setNewlyUnlockedBadges([]), 5000)
    return () => clearTimeout(t)
  }, [newlyUnlockedBadges.length])

  /** Convertit un accord attendu en clé parcours (ex. I, I6, V7, N6, V64, cad64) pour comparer à unlockedChordKeys */
  const chordToParcoursKey = useCallback((chord) => {
    if (!chord) return null
    if (chord.specialRoot === 'N') return 'N6'
    if (chord.specialRoot === 'It' || chord.specialRoot === 'Fr' || chord.specialRoot === 'Gr') return chord.specialRoot
    const degree = (chord.degree || '').toUpperCase()
    if (!degree) return null
    const fig = chord.figure && chord.figure !== '5' ? chord.figure : ''
    // I64 dépendant du contexte : passage → V64, cadence → cad64, avancé → I64
    if (degree === 'I' && fig === '64') {
      if (chord.sixFourVariant === 'passing') return 'V64'
      if (chord.sixFourVariant === 'cadential') return 'cad64'
      return 'I64'
    }
    return degree + fig
  }, [])

  /** En mode parcours, true si la case à l'index donnée a un accord attendu non débloqué (à griser / non remplissable) */
  const isSegmentLockedByParcours = useCallback((markerIndex) => {
    if (exerciseMode !== 'parcours' || !parcoursContext || !exercise?.markers?.[markerIndex]) {
      return false
    }
    const marker = exercise.markers[markerIndex]
    const expectedChord = typeof marker === 'object' && marker.chord ? marker.chord : null
    if (!expectedChord) {
      return false
    }

    const chordContext = buildChordContext(
      exercise.markers.map((m) => (typeof m === 'object' && m.chord ? m.chord : null)),
      markerIndex
    )

    // Phase Intuition (niveau 1, cadence parfaite, etc.) : on verrouille uniquement si la *fonction* (T/SD/D) n'est pas autorisée
    if (parcoursContext.phase === PHASE_INTUITION && parcoursContext.enabledFunctions?.length) {
      const chordFunction = getChordFunctionForLock(expectedChord, chordContext)
      const locked = chordFunction != null && !parcoursContext.enabledFunctions.includes(chordFunction)
      return locked
    }

    // Phase Précision / Maîtrise : pour les fonctions en "validation simple", on verrouille seulement si la fonction n'est pas autorisée ; pour les autres, on verrouille si la clé précise n'est pas débloquée
    const chordFunction = getChordFunctionForLock(expectedChord, chordContext)
    const precisionFunctions = parcoursContext.precisionFunctions ?? ['T', 'SD', 'D']
    if (chordFunction != null && !precisionFunctions.includes(chordFunction)) {
      const locked = !parcoursContext.enabledFunctions?.includes(chordFunction)
      return locked
    }
    if (!parcoursContext.unlockedChordKeys?.length) {
      return false
    }
    const key = chordToParcoursKey(expectedChord)
    if (!key) {
      return false
    }
    const locked = !parcoursContext.unlockedChordKeys.includes(key)
    return locked
  }, [exerciseMode, parcoursContext, exercise, chordToParcoursKey])

  useEffect(() => {
    if (exerciseMode === 'parcours') return
    if (!exercise?.markers?.length) return
    const nextVal = {}
    exercise.markers.forEach((marker, index) => {
      const ua = userAnswers[index]
      const correct = pedalAwareCorrectAnswers[index] || (typeof marker === 'object' && marker.chord ? marker.chord : null)
      if (!ua || !correct) return
      const locked = isSegmentLockedByParcours(index)
      const implicitBor =
        exercise.markers.length > 0
          ? getImplicitBorrowedLocalKeyForMarker(exercise.markers, index, pedalAwareCorrectAnswers, userAnswers)
          : null
      const v = computeSingleMarkerValidation({
        userAnswer: ua,
        correctAnswer: correct,
        index,
        progressionMode,
        exerciseMode,
        parcoursContext,
        cachedValidation: null,
        isLocked: locked,
        tonality: exercise?.metadata?.tonality || 'C',
        dominantMode: getExerciseDominantMode(exercise) || 'major',
        implicitBorrowedFromDegree: implicitBor
      })
      if (v) nextVal[index] = v
    })
    setAnswerValidations(nextVal)
  }, [
    userAnswers,
    exercise,
    exercise?.markers,
    pedalAwareCorrectAnswers,
    progressionMode,
    exerciseMode,
    parcoursContext,
    isSegmentLockedByParcours
  ])

  useEffect(() => {
    let cancelled = false
    const isLiveStudentSession = Boolean(liveSessionIdFromState && !isTeacherPreview && user?.uid && exerciseId)
    if (!isLiveStudentSession) return () => { cancelled = true }

    getLatestUserAttemptByLiveSessionExercise(user.uid, liveSessionIdFromState, exerciseId)
      .then((attempt) => {
        if (cancelled || !attempt) return
        const hydratedAnswers = normalizeAttemptAnswersMap(attempt.userAnswers)
        if (!hasAnyAnsweredChord(hydratedAnswers)) return

        setUserAnswers((prev) => {
          if (hasAnyAnsweredChord(prev)) return prev
          return hydratedAnswers
        })
        setAnswerValidations((prev) => {
          if (Object.keys(prev || {}).length > 0) return prev
          if (!Array.isArray(exercise?.markers) || exercise.markers.length === 0) return prev
          const nextValidations = {}
          exercise.markers.forEach((marker, index) => {
            const correct = pedalAwareCorrectAnswers[index] || (marker && typeof marker === 'object' && marker.chord ? marker.chord : null)
            const userAnswer = hydratedAnswers[index] || null
            const locked = isSegmentLockedByParcours(index)
            const implicitBor =
              exercise?.markers?.length > 0
                ? getImplicitBorrowedLocalKeyForMarker(exercise.markers, index, pedalAwareCorrectAnswers, hydratedAnswers)
                : null
            const v = computeSingleMarkerValidation({
              userAnswer,
              correctAnswer: correct,
              index,
              progressionMode,
              exerciseMode,
              parcoursContext,
              cachedValidation: null,
              isLocked: locked,
              tonality: exercise?.metadata?.tonality || 'C',
              dominantMode: getExerciseDominantMode(exercise) || 'major',
              implicitBorrowedFromDegree: implicitBor
            })
            if (v) nextValidations[index] = v
          })
          return nextValidations
        })

        const correctionReleasedNow = Boolean(liveCorrectionReleased || liveSessionMeta?.status === 'correction_released')
        if (correctionReleasedNow) return
        if (liveWaitingStorageKey) {
          try {
            if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(liveWaitingStorageKey, '1')
          } catch {}
        }
        setMode('live_waiting')
      })
      .catch(() => {})

    return () => { cancelled = true }
  }, [
    user?.uid,
    liveSessionIdFromState,
    exerciseId,
    isTeacherPreview,
    liveCorrectionReleased,
    liveSessionMeta?.status,
    progressionMode,
    exercise,
    pedalAwareCorrectAnswers,
    liveWaitingStorageKey,
    normalizeAttemptAnswersMap,
    hasAnyAnsweredChord,
    exerciseMode,
    parcoursContext,
    isSegmentLockedByParcours
  ])

  // Charger l'exercice
  useEffect(() => {
    loadExercise()
  }, [exerciseId])

  // Charger la progression et déterminer le mode si on est en mode parcours
  useEffect(() => {
    if (liveInputMode) {
      setProgressionMode(liveInputMode)
      setParcoursContext(null)
      setNodeStatsForQCM(null)
      setRelevantChordIndices([])
      return
    }
    if (exercise && exerciseMode === 'parcours' && nodeId && user && !isGuest) {
      loadProgressionMode()
    } else if (exerciseMode === 'libre') {
      setProgressionMode(freeInputMode)
      setParcoursContext(null)
      setNodeStatsForQCM(null)
      setRelevantChordIndices([])
    }
  }, [exercise, exerciseMode, nodeId, user, isGuest, liveInputMode, freeInputMode])

  const loadProgressionMode = async () => {
    try {
      const relevantIndices = getRelevantChordIndices(nodeId, exercise)
      setRelevantChordIndices(relevantIndices)

      const progress = await getUserProgress(user.uid)
      const nodeStats = progress?.nodeStats || {}
      const unlockedNodes = progress?.unlockedNodes || []
      setNodeStatsForQCM(nodeStats[nodeId] || null)
      const phase = getNodePhase(nodeId, nodeStats)
      const enabledFunctions = getEnabledFunctions(nodeId)
      const unlockedChordKeys = getUnlockedChordKeys(nodeId)
      const precisionFunctions = getPrecisionFunctions(nodeId)
      const unlockedCadenceValues = getUnlockedCadenceValues(unlockedNodes, nodeId)
      const cadenceAvailable = isCadenceAvailableForNode(nodeId, unlockedNodes)
      setParcoursContext({ nodeId, phase, enabledFunctions, unlockedChordKeys, precisionFunctions, cadenceAvailable, unlockedCadenceValues })

      // En parcours : seul l'intuition utilise le mode "functions" ; précision et maîtrise = QCM. Le mode "full" (sélecteur complet) est réservé au mode libre.
      if (phase === PHASE_INTUITION) setProgressionMode('functions')
      else setProgressionMode('qcm')
    } catch (error) {
      console.error('Erreur lors du chargement de la progression:', error)
      setProgressionMode('functions')
      setParcoursContext(null)
    }
  }

  const loadExercise = async () => {
    try {
      setLoading(true)
      const data = await getExerciseById(exerciseId)
      
      if (!data) {
        setError('Exercice introuvable')
        return
      }

      // Vérifier que l'exercice est publié ou accessible
      if (data.status !== 'published' && data.status !== 'draft') {
        setError('Cet exercice n\'est pas accessible')
        return
      }

      setExercise(data)
      
      // Initialiser les réponses vides
      const answers = {}
      if (data.markers) {
        data.markers.forEach((_, index) => {
          answers[index] = null
        })
      }
      setUserAnswers(answers)
    } catch (err) {
      console.error('Erreur lors du chargement de l\'exercice:', err)
      if (err?.code === 'permission-denied') {
        setError('BETA_RESTRICTED')
      } else {
        setError('Erreur lors du chargement de l\'exercice')
      }
    } finally {
      setLoading(false)
    }
  }

  const readYoutubeCurrentTime = () => {
    try {
      const t = playerRef.current?.getCurrentTime?.()
      if (typeof t === 'number' && Number.isFinite(t)) return t
    } catch (_) { /* ignoré si API YouTube indisponible */ }
    const remembered = Number(currentTimeRef.current ?? currentTime)
    return Number.isFinite(remembered) ? remembered : 0
  }

  const syncPlayerToTime = (targetTime) => {
    if (!playerRef.current || !exercise?.settings) return targetTime
    const start = Number(exercise.settings.startTime || 0)
    const end = Number(exercise.settings.endTime || 0)
    const endExclusive = end > start ? Math.max(start, end - 0.05) : start
    const t = Number(targetTime)
    const bounded = Number.isFinite(t) ? Math.max(start, Math.min(endExclusive, t)) : start
    try {
      playerRef.current.seekTo(bounded, true)
    } catch (_) { /* ignoré si API YouTube indisponible */ }
    currentTimeRef.current = bounded
    setCurrentTime(bounded)
    return bounded
  }

  // Gestion du lecteur YouTube
  const handleReady = (event) => {
    playerRef.current = event.target
    // #region agent log
    fetch('http://127.0.0.1:7245/ingest/35ff180e-d8aa-4991-b134-f4ff94edefe8', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'cfe28f' }, body: JSON.stringify({ sessionId: 'cfe28f', runId: 'vol-api', hypothesisId: 'H_ENABLE', location: 'Player.jsx:handleReady', message: 'YT ready', data: { hasSetVolume: typeof event.target?.setVolume === 'function', hasGetVolume: typeof event.target?.getVolume === 'function', playerVarsEnableJsapi: ytOptsDebugRef.current?.enablejsapi ?? null, playerVarsHasOrigin: Boolean(ytOptsDebugRef.current?.origin) }, timestamp: Date.now() }) }).catch(() => {})
    // #endregion
    if (shouldMuteForLiveStudent) {
      try {
        event.target.mute?.()
        event.target.setVolume?.(0)
      } catch (_) { /* ignoré si API YouTube indisponible */ }
    }
    if (typeof playerSyncOnReadyRef.current === 'number' && Number.isFinite(playerSyncOnReadyRef.current)) {
      const target = playerSyncOnReadyRef.current
      playerSyncOnReadyRef.current = null
      try {
        event.target.seekTo(target, true)
        currentTimeRef.current = target
        setCurrentTime(target)
      } catch (_) { /* ignoré si API YouTube indisponible */ }
    }
  }

  // Synchroniser l'état React avec l'état réel du lecteur YouTube
  const handleStateChange = (event) => {
    if (!playerRef.current || !exercise) return
    
    const state = event.data
    // YouTube.PlayerState.PLAYING = 1
    // YouTube.PlayerState.PAUSED = 2
    // YouTube.PlayerState.ENDED = 0
    
    if (state === 1) { // PLAYING
      if (shouldMuteForLiveStudent) {
        try {
          playerRef.current.mute?.()
          playerRef.current.setVolume?.(0)
          playerRef.current.pauseVideo?.()
          setIsPlaying(false)
        } catch (_) { /* ignoré si API YouTube indisponible */ }
        return
      }
      const startTime = exercise.settings.startTime
      const endTime = exercise.settings.endTime
      const skipNormalization = skipNextPlayNormalizationRef.current
      skipNextPlayNormalizationRef.current = false

      if (!isPlaying && !suppressPlayStateSyncRef.current) {
        setIsPlaying(true)
      }

      // Toujours recaler sur l'extrait selon la position réelle du lecteur (pas seulement currentTimeRef).
      try {
        const ytCurrentTime = playerRef.current.getCurrentTime()
        if (!skipNormalization && (ytCurrentTime < startTime || ytCurrentTime >= endTime)) {
          playerRef.current.seekTo(startTime, true)
          setCurrentTime(startTime)
          currentTimeRef.current = startTime
        } else {
          setCurrentTime(ytCurrentTime)
          currentTimeRef.current = ytCurrentTime
        }
      } catch (error) {
        console.error('Erreur lors de la mise à jour du temps:', error)
      }
    } else if (state === 2 || state === 0) { // PAUSED ou ENDED
      // Si la lecture s'arrête, synchroniser l'état React
      if (isPlaying) {
        // #region agent log
        fetch('http://127.0.0.1:7245/ingest/35ff180e-d8aa-4991-b134-f4ff94edefe8', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'cfe28f' }, body: JSON.stringify({ sessionId: 'cfe28f', runId: 'fade-debug', hypothesisId: 'H4', location: 'Player.jsx:handleStateChange:pauseOrEnd', message: 'YT state PAUSED/ENDED while isPlaying true', data: { ytState: state, fadeIntervalsLen: window.playerFadeIntervals?.length ?? 0 }, timestamp: Date.now() }) }).catch(() => {})
        // #endregion
        setIsPlaying(false)
      }
      
      // Si la vidéo est terminée, s'assurer qu'on est à la fin de l'extrait
      if (state === 0) {
        const endTime = exercise.settings.endTime
        if (playerRef.current) {
          try {
            const currentTime = playerRef.current.getCurrentTime()
            // Si on est après la fin de l'extrait, revenir à la fin
            if (currentTime > endTime) {
              playerRef.current.seekTo(endTime, true)
              setCurrentTime(endTime)
              currentTimeRef.current = endTime
            }
          } catch (error) {
            console.error('Erreur lors de la vérification de fin:', error)
          }
        }
      }
    }
  }

  useEffect(() => {
    if (!playerRef.current) return
    try {
      if (shouldMuteForLiveStudent) {
        playerRef.current.mute?.()
        playerRef.current.setVolume?.(0)
        playerRef.current.pauseVideo?.()
        setIsPlaying(false)
      } else {
        playerRef.current.unMute?.()
      }
    } catch (_) { /* ignoré si API YouTube indisponible */ }
  }, [shouldMuteForLiveStudent, exerciseId])

  // Nettoyer les intervalles de fade seulement en transition lecture → arrêt (évite courses avec l’enregistrement des intervalles)
  useEffect(() => {
    // #region agent log
    const _fadeLen = window.playerFadeIntervals?.length ?? 0
    const _wasP = wasPlayingForFadeCleanupRef.current
    const _willClear = !isPlaying && _wasP && _fadeLen > 0
    fetch('http://127.0.0.1:7245/ingest/35ff180e-d8aa-4991-b134-f4ff94edefe8', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'cfe28f' }, body: JSON.stringify({ sessionId: 'cfe28f', runId: 'fade-debug', hypothesisId: 'H2', location: 'Player.jsx:fadeCleanupEffect', message: 'isPlaying effect', data: { isPlaying, wasPlayingBefore: _wasP, fadeIntervalsLen: _fadeLen, willClearIntervals: _willClear }, timestamp: Date.now() }) }).catch(() => {})
    // #endregion
    if (!isPlaying && wasPlayingForFadeCleanupRef.current && window.playerFadeIntervals?.length) {
      // #region agent log
      fetch('http://127.0.0.1:7245/ingest/35ff180e-d8aa-4991-b134-f4ff94edefe8', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'cfe28f' }, body: JSON.stringify({ sessionId: 'cfe28f', runId: 'fade-debug', hypothesisId: 'H2', location: 'Player.jsx:fadeCleanupEffect:clear', message: 'Clearing fade intervals (play→stop transition)', data: { intervalCount: window.playerFadeIntervals.length }, timestamp: Date.now() }) }).catch(() => {})
      // #endregion
      window.playerFadeIntervals.forEach(({ cleanup }) => {
        if (cleanup) cleanup()
      })
      window.playerFadeIntervals = []
    }
    wasPlayingForFadeCleanupRef.current = isPlaying
  }, [isPlaying])

  // Gérer le scroll du body pour le mode plein écran (doit être avant tous les returns)
  useEffect(() => {
    // Ne gérer le scroll que si l'exercice est chargé
    if (!loading && exercise) {
      document.body.classList.add('player-active')
      return () => {
        document.body.classList.remove('player-active')
      }
    }
  }, [mode, loading, exercise])

  // Hauteur visible sur mobile (Visual Viewport) : évite que timeline + contrôles soient coupés sous la barre d’URL
  useEffect(() => {
    if (!exercise) return
    const setVvh = () => {
      const h = window.visualViewport?.height ?? window.innerHeight
      document.documentElement.style.setProperty('--vvh', `${Math.round(h)}px`)
    }
    setVvh()
    const vv = window.visualViewport
    if (vv) {
      vv.addEventListener('resize', setVvh)
      vv.addEventListener('scroll', setVvh)
      return () => {
        vv.removeEventListener('resize', setVvh)
        vv.removeEventListener('scroll', setVvh)
        document.documentElement.style.removeProperty('--vvh')
      }
    }
    return () => document.documentElement.style.removeProperty('--vvh')
  }, [exercise])

  const dismissOnboarding = useCallback(() => {
    setShowOnboarding(false)
  }, [])

  // Masquer le coachmark rapidement (ou plus tôt via interactions)
  useEffect(() => {
    if (showOnboarding) {
      const timer = setTimeout(() => {
        setShowOnboarding(false)
      }, 2500)
      
      return () => clearTimeout(timer)
    }
  }, [showOnboarding])
  
  // Masquer aussi dès qu'une réponse est validée
  useEffect(() => {
    if (showOnboarding && exercise && Object.values(userAnswers).some(answer => answer !== null)) {
      setShowOnboarding(false)
    }
  }, [userAnswers, showOnboarding, exercise])

  // Fonction pour jouer l'extrait complet avec fondus
  const handlePlayFullExtract = () => {
    if (!playerRef.current || !exercise) return
    
    const startTime = exercise.settings.startTime
    const endTime = exercise.settings.endTime
    const fadeDuration = 0.5 // 0.5 secondes

    if (isIOSDevice && !allowYoutubeAudioFade) {
      playerRef.current.seekTo(startTime, true)
      ensureIOSAudiblePlayback()
      playerRef.current.playVideo()
      setIsPlaying(true)

      const checkEndInterval = setInterval(() => {
        if (!playerRef.current) {
          clearInterval(checkEndInterval)
          return
        }
        try {
          const currentTime = playerRef.current.getCurrentTime()
          if (currentTime >= endTime) {
            playerRef.current.pauseVideo()
            setIsPlaying(false)
            clearInterval(checkEndInterval)
          }
        } catch (error) {
          console.error('Erreur iOS lors du suivi de fin:', error)
          clearInterval(checkEndInterval)
        }
      }, 50)

      const cleanup = () => {
        clearInterval(checkEndInterval)
      }
      if (!window.playerFadeIntervals) {
        window.playerFadeIntervals = []
      }
      window.playerFadeIntervals.push({ checkEndInterval, cleanup })
      return
    }

    // Aller au début et lancer la lecture (avec fondus : desktop ou projection live sur iOS)
    playerRef.current.seekTo(startTime, true)
    if (typeof window !== 'undefined') {
      window.__opusFadeOutVolLogged = false
      window.__opusFadeWindowEntered = false
    }
    if (isIOSDevice) ensureIOSAudiblePlayback()
    playerRef.current.setVolume(0) // Commencer à volume 0 pour le fade-in
    playerRef.current.playVideo()
    reassertYoutubeVolumeZeroAfterPlay(playerRef)
    setIsPlaying(true)

    // Fade-in progressif sur 0.5s
    const fadeInSteps = 10
    const fadeInInterval = (fadeDuration * 1000) / fadeInSteps
    let fadeInStep = 0

    const fadeInIntervalId = setInterval(() => {
      fadeInStep++
      const volume = Math.min(100, (fadeInStep / fadeInSteps) * 100)
      if (playerRef.current) {
        playerRef.current.setVolume(volume)
      }
      if (fadeInStep >= fadeInSteps) {
        clearInterval(fadeInIntervalId)
      }
    }, fadeInInterval)

    // Surveiller la fin pour le fade-out
    const checkEndInterval = setInterval(() => {
      if (!playerRef.current) {
        clearInterval(checkEndInterval)
        return
      }

      try {
        const currentTime = playerRef.current.getCurrentTime()
        const timeUntilEnd = endTime - currentTime

        logYoutubeFadeWindowEnteredOnce(timeUntilEnd, fadeDuration, currentTime, endTime, 'handlePlayFullExtract')
        applyYoutubeFadeOutVolume(playerRef.current, timeUntilEnd, fadeDuration)

        // Arrêter à la fin
        if (currentTime >= endTime) {
          playerRef.current.pauseVideo()
          playerRef.current.setVolume(100) // Remettre le volume à 100 pour les prochaines lectures
          setIsPlaying(false)
          clearInterval(checkEndInterval)
        }
      } catch (error) {
        console.error('Erreur lors du suivi de la fin:', error)
        clearInterval(checkEndInterval)
      }
    }, 50) // Vérifier toutes les 50ms

    // Nettoyer les intervalles si la lecture est arrêtée manuellement
    const cleanup = () => {
      clearInterval(fadeInIntervalId)
      clearInterval(checkEndInterval)
      if (playerRef.current) {
        playerRef.current.setVolume(100) // Remettre le volume à 100
      }
    }

    // Stocker les IDs d'intervalle pour nettoyage
    if (!window.playerFadeIntervals) {
      window.playerFadeIntervals = []
    }
    window.playerFadeIntervals.push({ fadeInIntervalId, checkEndInterval, cleanup })
  }

  const handlePlayPause = () => {
    if (!playerRef.current || !exercise) return
    if (shouldMuteForLiveStudent) {
      try {
        playerRef.current.pauseVideo?.()
      } catch (_) { /* ignoré si API YouTube indisponible */ }
      setIsPlaying(false)
      return
    }
    dismissOnboarding()
    
    if (isPlaying) {
      // Nettoyer les fondus si on pause
      if (window.playerFadeIntervals) {
        window.playerFadeIntervals.forEach(({ cleanup }) => {
          if (cleanup) cleanup()
        })
        window.playerFadeIntervals = []
      }
      playerRef.current.pauseVideo()
      setIsPlaying(false)
    } else {
      // Mode exercise : reprendre depuis la position réelle du lecteur (évite un décalage après remontage YouTube)
      const startTime = exercise.settings.startTime
      const endTime = exercise.settings.endTime
      let current = readYoutubeCurrentTime()
      if (current < startTime || current >= endTime) {
        current = syncPlayerToTime(startTime)
      } else {
        currentTimeRef.current = current
        setCurrentTime(current)
      }
      const fadeDuration = 0.5 // 0.5 secondes
      // #region agent log
      {
        const _ua = typeof navigator !== 'undefined' ? String(navigator.userAgent || '').slice(0, 160) : ''
        const _plat = typeof navigator !== 'undefined' ? String(navigator.platform || '') : ''
        const _iosNoFade = Boolean(isIOSDevice && !allowYoutubeAudioFade)
        fetch('http://127.0.0.1:7245/ingest/35ff180e-d8aa-4991-b134-f4ff94edefe8', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'cfe28f' }, body: JSON.stringify({ sessionId: 'cfe28f', runId: 'fade-debug', hypothesisId: 'H1_H5', location: 'Player.jsx:handlePlayPause:playBranch', message: 'Play branch entered', data: { isIOSDevice, allowYoutubeAudioFade, iosNoFadeBranch: _iosNoFade, uaTail: _ua.slice(-80), platform: _plat, isTeacherPreview, hasLiveSessionId: Boolean(liveSessionIdFromState) }, timestamp: Date.now() }) }).catch(() => {})
      }
      // #endregion

      if (isIOSDevice && !allowYoutubeAudioFade) {
        // #region agent log
        fetch('http://127.0.0.1:7245/ingest/35ff180e-d8aa-4991-b134-f4ff94edefe8', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'cfe28f' }, body: JSON.stringify({ sessionId: 'cfe28f', runId: 'fade-debug', hypothesisId: 'H1', location: 'Player.jsx:handlePlayPause:iosNoFade', message: 'Using iOS no-fade path', data: {}, timestamp: Date.now() }) }).catch(() => {})
        // #endregion
        ensureIOSAudiblePlayback()
        playerRef.current.playVideo()
        setIsPlaying(true)

        const checkEndInterval = setInterval(() => {
          if (!playerRef.current) {
            clearInterval(checkEndInterval)
            return
          }

          try {
            const currentTime = playerRef.current.getCurrentTime()
            if (currentTime >= endTime) {
              playerRef.current.pauseVideo()
              setIsPlaying(false)
              currentTimeRef.current = endTime
              setCurrentTime(endTime)
              clearInterval(checkEndInterval)
            }
          } catch (error) {
            console.error('Erreur iOS lors du suivi de fin:', error)
            clearInterval(checkEndInterval)
          }
        }, 50)

        const cleanup = () => {
          clearInterval(checkEndInterval)
        }
        if (!window.playerFadeIntervals) {
          window.playerFadeIntervals = []
        }
        window.playerFadeIntervals.push({ checkEndInterval, cleanup })
        return
      }

      // Toujours appliquer les fondus au début et à la fin
      // Fade-in progressif sur 0.5s
      // #region agent log
      if (typeof window !== 'undefined') {
        window.__opusFadeOutVolLogged = false
        window.__opusFadeWindowEntered = false
      }
      fetch('http://127.0.0.1:7245/ingest/35ff180e-d8aa-4991-b134-f4ff94edefe8', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'cfe28f' }, body: JSON.stringify({ sessionId: 'cfe28f', runId: 'fade-debug', hypothesisId: 'H3_H4', location: 'Player.jsx:handlePlayPause:fadePath', message: 'Using volume fade path', data: {}, timestamp: Date.now() }) }).catch(() => {})
      // #endregion
      if (isIOSDevice) ensureIOSAudiblePlayback()
      playerRef.current.setVolume(0) // Commencer à volume 0 pour le fade-in
      playerRef.current.playVideo()
      reassertYoutubeVolumeZeroAfterPlay(playerRef)
      setIsPlaying(true)

      const fadeInSteps = 10
      const fadeInInterval = (fadeDuration * 1000) / fadeInSteps
      let fadeInStep = 0

      const fadeInIntervalId = setInterval(() => {
        fadeInStep++
        const volume = Math.min(100, (fadeInStep / fadeInSteps) * 100)
        if (playerRef.current) {
          playerRef.current.setVolume(volume)
        }
        // #region agent log
        if (fadeInStep === 3 && playerRef.current) {
          let gv = null
          try { gv = typeof playerRef.current.getVolume === 'function' ? playerRef.current.getVolume() : null } catch (_) { gv = 'err' }
          fetch('http://127.0.0.1:7245/ingest/35ff180e-d8aa-4991-b134-f4ff94edefe8', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'cfe28f' }, body: JSON.stringify({ sessionId: 'cfe28f', runId: 'fade-debug', hypothesisId: 'H3', location: 'Player.jsx:handlePlayPause:fadeInStep3', message: 'After setVolume during fade-in', data: { fadeInStep, targetVolume: volume, getVolume: gv }, timestamp: Date.now() }) }).catch(() => {})
        }
        // #endregion
        if (fadeInStep >= fadeInSteps) {
          clearInterval(fadeInIntervalId)
        }
      }, fadeInInterval)

      // Surveiller la fin pour le fade-out
      const checkEndInterval = setInterval(() => {
        if (!playerRef.current) {
          clearInterval(checkEndInterval)
          return
        }

        try {
          const currentTime = playerRef.current.getCurrentTime()
          const timeUntilEnd = endTime - currentTime

          logYoutubeFadeWindowEnteredOnce(timeUntilEnd, fadeDuration, currentTime, endTime, 'handlePlayPause')
          applyYoutubeFadeOutVolume(playerRef.current, timeUntilEnd, fadeDuration)

          // Arrêter à la fin
          if (currentTime >= endTime) {
            playerRef.current.pauseVideo()
            playerRef.current.setVolume(100) // Remettre le volume à 100 pour les prochaines lectures
            setIsPlaying(false)
            // Mettre à jour la référence du temps pour qu'elle reflète la position à la fin
            currentTimeRef.current = endTime
            setCurrentTime(endTime)
            clearInterval(checkEndInterval)
          }
        } catch (error) {
          console.error('Erreur lors du suivi de la fin:', error)
          clearInterval(checkEndInterval)
        }
      }, 50) // Vérifier toutes les 50ms
      
      // Nettoyer les intervalles si la lecture est arrêtée manuellement
      const cleanup = () => {
        clearInterval(fadeInIntervalId)
        clearInterval(checkEndInterval)
        if (playerRef.current) {
          playerRef.current.setVolume(100) // Remettre le volume à 100
        }
      }
      
      // Stocker les IDs d'intervalle pour nettoyage
      if (!window.playerFadeIntervals) {
        window.playerFadeIntervals = []
      }
      window.playerFadeIntervals.push({ fadeInIntervalId, checkEndInterval, cleanup })
    }
  }

  const handleStartExercise = () => {
    if (!playerRef.current || !exercise) return
    
    const startTime = exercise.settings.startTime
    const endTime = exercise.settings.endTime
    const fadeDuration = 0.5 // 0.5 secondes
    
    setMode('exercise')
    setCurrentMarkerIndex(0)
    playerRef.current.seekTo(startTime, true)

    if (isIOSDevice && !allowYoutubeAudioFade) {
      ensureIOSAudiblePlayback()
      playerRef.current.playVideo()
      setIsPlaying(true)

      const checkEndInterval = setInterval(() => {
        if (!playerRef.current) {
          clearInterval(checkEndInterval)
          return
        }

        try {
          const currentTime = playerRef.current.getCurrentTime()
          if (currentTime >= endTime) {
            playerRef.current.pauseVideo()
            setIsPlaying(false)
            clearInterval(checkEndInterval)
          }
        } catch (error) {
          console.error('Erreur iOS lors du suivi de fin:', error)
          clearInterval(checkEndInterval)
        }
      }, 50)

      const cleanup = () => {
        clearInterval(checkEndInterval)
      }
      if (!window.playerFadeIntervals) {
        window.playerFadeIntervals = []
      }
      window.playerFadeIntervals.push({ checkEndInterval, cleanup })
      return
    }

    if (typeof window !== 'undefined') {
      window.__opusFadeOutVolLogged = false
      window.__opusFadeWindowEntered = false
    }
    if (isIOSDevice) ensureIOSAudiblePlayback()
    playerRef.current.setVolume(0) // Commencer à volume 0 pour le fade-in
    playerRef.current.playVideo()
    reassertYoutubeVolumeZeroAfterPlay(playerRef)
    setIsPlaying(true)

    // Fade-in progressif sur 0.5s
    const fadeInSteps = 10
    const fadeInInterval = (fadeDuration * 1000) / fadeInSteps
    let fadeInStep = 0

    const fadeInIntervalId = setInterval(() => {
      fadeInStep++
      const volume = Math.min(100, (fadeInStep / fadeInSteps) * 100)
      if (playerRef.current) {
        playerRef.current.setVolume(volume)
      }
      if (fadeInStep >= fadeInSteps) {
        clearInterval(fadeInIntervalId)
      }
    }, fadeInInterval)

    // Surveiller la fin pour le fade-out
    const checkEndInterval = setInterval(() => {
      if (!playerRef.current) {
        clearInterval(checkEndInterval)
        return
      }

      try {
        const currentTime = playerRef.current.getCurrentTime()
        const timeUntilEnd = endTime - currentTime

        logYoutubeFadeWindowEnteredOnce(timeUntilEnd, fadeDuration, currentTime, endTime, 'handleStartExercise')
        applyYoutubeFadeOutVolume(playerRef.current, timeUntilEnd, fadeDuration)

        // Arrêter à la fin
        if (currentTime >= endTime) {
          playerRef.current.pauseVideo()
          playerRef.current.setVolume(100) // Remettre le volume à 100 pour les prochaines lectures
          setIsPlaying(false)
          clearInterval(checkEndInterval)
        }
      } catch (error) {
        console.error('Erreur lors du suivi de la fin:', error)
        clearInterval(checkEndInterval)
      }
    }, 50) // Vérifier toutes les 50ms

    // Nettoyer les intervalles si la lecture est arrêtée manuellement
    const cleanup = () => {
      clearInterval(fadeInIntervalId)
      clearInterval(checkEndInterval)
      if (playerRef.current) {
        playerRef.current.setVolume(100) // Remettre le volume à 100
      }
    }

    // Stocker les IDs d'intervalle pour nettoyage
    if (!window.playerFadeIntervals) {
      window.playerFadeIntervals = []
    }
    window.playerFadeIntervals.push({ fadeInIntervalId, checkEndInterval, cleanup })
  }

  useEffect(() => {
    isPlayingRef.current = isPlaying
  }, [isPlaying])

  const useInlineAnswerPanel = mode === 'exercise'
  const isAnswerPanelOpen = useInlineAnswerPanel && isModalOpen

  const seekPreservingPlayback = useCallback((time) => {
    if (!playerRef.current || !exercise) return
    const wasPlaying = isPlayingRef.current
    suppressPlayStateSyncRef.current = true
    playerRef.current.seekTo(time, true)
    setCurrentTime(time)
    currentTimeRef.current = time
    window.setTimeout(() => {
      suppressPlayStateSyncRef.current = false
      if (!playerRef.current) return
      try {
        if (!wasPlaying) {
          const playerState = playerRef.current.getPlayerState()
          if (playerState === 1) playerRef.current.pauseVideo()
        }
      } catch (_) {
        if (!wasPlaying) playerRef.current.pauseVideo?.()
      }
    }, 300)
  }, [exercise])

  const openAnswerPanel = useCallback((markerIndex) => {
    if (!exercise || isSegmentLockedByParcours(markerIndex)) return
    dismissOnboarding()
    setSelectedMarkerId(markerIndex)
    setCurrentMarkerIndex(markerIndex)
    setIsModalOpen(true)
  }, [exercise, isSegmentLockedByParcours, dismissOnboarding])

  const closeAnswerPanel = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const handleSegmentActivate = useCallback((markerIndex) => {
    if (!playerRef.current || !exercise || isSegmentLockedByParcours(markerIndex)) return
    dismissOnboarding()
    setSegmentTooltipIndex(null)
    const markers = exercise.markers.map(m => typeof m === 'number' ? m : m.time || m.absoluteTime)
    const markerTime = markers[markerIndex]
    if (markerTime === undefined) return
    seekPreservingPlayback(markerTime)
    if (!isModalOpen) {
      openAnswerPanel(markerIndex)
    } else if (markerIndex !== currentMarkerIndex) {
      setSelectedMarkerId(markerIndex)
      setCurrentMarkerIndex(markerIndex)
    }
  }, [exercise, isSegmentLockedByParcours, dismissOnboarding, seekPreservingPlayback, isModalOpen, currentMarkerIndex, openAnswerPanel])

  const handleMarkerClick = handleSegmentActivate

  // Fonction pour naviguer vers un marqueur (sans ouvrir la modale)
  const handleSeekToMarker = useCallback((markerIndex) => {
    if (!playerRef.current || !exercise) return
    dismissOnboarding()

    const markers = exercise.markers.map(m => typeof m === 'number' ? m : m.time || m.absoluteTime)
    const markerTime = markers[markerIndex]

    if (markerTime !== undefined) {
      seekPreservingPlayback(markerTime)
      setSelectedMarkerId(markerIndex)
      if (isModalOpen) {
        setCurrentMarkerIndex(markerIndex)
      }
    }
  }, [exercise, dismissOnboarding, seekPreservingPlayback, isModalOpen])

  const normalizeAnswerPayload = useCallback((chordData) => {
    let finalChordData = chordData
    const isQcmPayload = progressionMode === 'qcm' && typeof chordData === 'object' && chordData !== null && 'chord' in chordData
    const isQcmString = progressionMode === 'qcm' && typeof chordData === 'string'

    if (isQcmPayload) {
      finalChordData = { qcmAnswer: chordData.chord, cadence: chordData.cadence ?? undefined, function: chordData.function ?? undefined }
    } else if (isQcmString) {
      finalChordData = { qcmAnswer: chordData }
    }
    return finalChordData
  }, [progressionMode])

  const handleAnswerChange = useCallback((chordData) => {
    const finalChordData = normalizeAnswerPayload(chordData)
    const hasContent = finalChordData && (
      finalChordData.qcmAnswer != null
      || finalChordData.selectedFunction != null
      || finalChordData.function != null
      || finalChordData.degree
      || finalChordData.specialRoot
      || finalChordData.cadence
    )
    setUserAnswers(prev => {
      if (!hasContent) {
        const next = { ...prev }
        delete next[currentMarkerIndex]
        return next
      }
      return { ...prev, [currentMarkerIndex]: finalChordData }
    })
  }, [currentMarkerIndex, normalizeAnswerPayload])

  const handleResetAnswer = useCallback(() => {
    setUserAnswers(prev => {
      const next = { ...prev }
      delete next[currentMarkerIndex]
      return next
    })
  }, [currentMarkerIndex])

  const handleChordValidate = (chordData) => {
    handleAnswerChange(chordData)
    setIsModalOpen(false)
  }

  const handleModalClose = () => {
    closeAnswerPanel()
  }

  // Fonction pour valider toutes les réponses et préparer les validations
  const validateAllAnswers = useCallback(() => {
    if (!exercise || !exercise.markers) return {}

    const validations = {}
    exercise.markers.forEach((marker, index) => {
      const userAnswer = userAnswers[index]
      const correctAnswer = pedalAwareCorrectAnswers[index] || (typeof marker === 'object' && marker.chord ? marker.chord : null)
      const isLocked = isSegmentLockedByParcours(index)
      const implicitBor =
        exercise?.markers?.length > 0
          ? getImplicitBorrowedLocalKeyForMarker(exercise.markers, index, pedalAwareCorrectAnswers, userAnswers)
          : null
      const v = computeSingleMarkerValidation({
        userAnswer,
        correctAnswer,
        index,
        progressionMode,
        exerciseMode,
        parcoursContext,
        cachedValidation: answerValidations[index],
        isLocked,
        tonality: exercise?.metadata?.tonality || 'C',
        dominantMode: getExerciseDominantMode(exercise) || 'major',
        implicitBorrowedFromDegree: implicitBor
      })
      if (v) validations[index] = v
    })

    return validations
  }, [exercise, userAnswers, answerValidations, progressionMode, isSegmentLockedByParcours, exerciseMode, parcoursContext, pedalAwareCorrectAnswers])

  // Fonction pour animer les accords séquentiellement
  const animateChordValidation = useCallback(async (validations) => {
    if (!exercise || !exercise.markers) return
    
    setIsValidating(true)
    setAnimatedChords(new Set())
    
    // Trier les marqueurs par ordre chronologique
    const sortedMarkers = exercise.markers
      .map((marker, index) => ({
        index,
        time: typeof marker === 'number' ? marker : (marker.time || marker.absoluteTime)
      }))
      .sort((a, b) => a.time - b.time)
    
    // Animer chaque accord avec un délai
    for (let i = 0; i < sortedMarkers.length; i++) {
      const marker = sortedMarkers[i]
      await new Promise(resolve => {
        setTimeout(() => {
          setAnimatedChords(prev => new Set([...prev, marker.index]))
          resolve()
        }, i === 0 ? 300 : 400) // Premier accord après 300ms, les suivants après 400ms
      })
    }
    
    // Attendre un peu avant de passer au mode review
    await new Promise(resolve => setTimeout(resolve, 800))
    setIsValidating(false)
  }, [exercise])

  const handleFinishExercise = async () => {
    const isLiveStudentSession = Boolean(liveSessionIdFromState && !isTeacherPreview)
    const isLiveSessionClosed = Boolean(isLiveStudentSession && liveSessionMeta?.status === 'closed')
    if (isLiveSessionClosed) {
      navigate(returnTo || '/student-dashboard', { replace: true })
      return
    }
    const isLiveCorrectionAvailable = Boolean(liveCorrectionReleased || liveSessionMeta?.status === 'correction_released')
    const shouldWaitForLiveCorrection = Boolean(isLiveStudentSession && !isLiveCorrectionAvailable)
    // Arrêter la lecture si elle est en cours
    if (playerRef.current && isPlaying) {
      playerRef.current.pauseVideo()
      setIsPlaying(false)
    }
    
    // Valider toutes les réponses d'abord pour avoir les validations à jour
    const allValidations = validateAllAnswers()
    setAnswerValidations(allValidations)
    
    // En aperçu prof : publier la correction live (source unique), puis capturer les rects et passer en review.
    if (isTeacherPreview && liveSessionMeta?.id && user?.uid && liveSessionMeta?.status !== 'correction_released') {
      try {
        await setLiveSessionStatus(liveSessionMeta.id, user.uid, 'correction_released')
      } catch (error) {
        console.error('Projection: impossible de publier la correction live', error)
      }
    }
    // En aperçu prof : capturer les rects des cases (pour FLIP), son, puis passage en review
    if (isTeacherPreview) {
      const container = document.querySelector('.player-video-zone .player-timeline-with-segments')
      if (container) {
        const segs = container.querySelectorAll('.player-chord-segment[data-segment-index]')
        exerciseSegmentRectsRef.current = Array.from(segs)
          .sort((a, b) => Number(a.dataset.segmentIndex) - Number(b.dataset.segmentIndex))
          .map((el) => el.getBoundingClientRect())
      } else {
        exerciseSegmentRectsRef.current = null
      }
      playRevealSound()
    } else if (!shouldWaitForLiveCorrection) {
      await animateChordValidation(allValidations)
    }
    
    // Passer en mode review sauf en session live élève (la correction reste verrouillée jusqu'au déclenchement prof).
    if (!shouldWaitForLiveCorrection) {
      setMode('review')
      // Sélectionner automatiquement le premier segment
      if (chordSegments.length > 0) {
        setSelectedSegmentIndex(0)
      }
    }
    
    // Sauvegarder la tentative si l'utilisateur est connecté, n'est pas en mode invité, et n'est pas en aperçu prof
    if (user && exercise && !isGuest && !isTeacherPreview) {
      try {
        
        // Préparer les réponses et les solutions
        const answersArray = exercise.markers.map((_, index) => userAnswers[index] || null)
        const correctAnswersArray = exercise.markers.map((marker, index) => 
          pedalAwareCorrectAnswers[index] || (typeof marker === 'object' && marker.chord ? marker.chord : null)
        )
        
        let totalScore = 0
        let maxScore = 0
        let totalCadenceBonus = 0
        
        // En mode parcours, calculer le score uniquement sur les accords remplissables (non grisés)
        if (exerciseMode === 'parcours' && nodeId && relevantChordIndices.length > 0) {
          const fillableIndices = relevantChordIndices.filter((i) => !isSegmentLockedByParcours(i))
          const nodeScore = calculateNodeScore(nodeId, answersArray, correctAnswersArray, exercise, {
            functionOnlyAvailable: progressionMode === 'functions',
            fillableIndices
          })
          const relevantCount = fillableIndices.length
          
          // Mettre à jour la progression du nœud (sur le nombre de cases à remplir)
          await updateNodeProgress(user.uid, nodeId, nodeScore, relevantCount)
          
          // Utiliser le score du nœud pour l'affichage
          totalScore = nodeScore * relevantCount
          maxScore = relevantCount * 100
        } else {
          // Mode libre : calculer le score sur tous les accords (accord + cadence si attendue)
          correctAnswersArray.forEach((correct, index) => {
            const userAnswer = answersArray[index]
            if (userAnswer && correct) {
              const validation = allValidations[index] || answerValidations[index]
              if (validation) {
                totalScore += validation.score
                // Ajouter le bonus de cadence si présent
                if (validation.cadenceBonus) {
                  totalCadenceBonus += validation.cadenceBonus
                }
              } else {
                // Fallback : validation binaire si pas de validation fonctionnelle
                const validation = validateAnswerWithFunctions(
                  userAnswer,
                  correct,
                  userAnswer.selectedFunction || userAnswer.function || null,
                  {
                    functionOnlyAvailable: progressionMode === 'functions',
                    tonality: exercise?.metadata?.tonality || 'C',
                    mode: getExerciseDominantMode(exercise) || 'major'
                  }
                )
                totalScore += validation.score
                // Ajouter le bonus de cadence si présent
                if (validation.cadenceBonus) {
                  totalCadenceBonus += validation.cadenceBonus
                }
              }
              maxScore += 100
              // La cadence compte pour le score : +10 points possibles si une cadence est attendue (et débloquée en parcours)
              if (correct.cadence) {
                const norm = normalizeCadence(correct.cadence)
                const unlc = parcoursContext?.unlockedCadenceValues
                if (exerciseMode !== 'parcours' || !Array.isArray(unlc) || unlc.length === 0 || (norm && unlc.includes(norm))) maxScore += 10
              }
            } else if (correct) {
              maxScore += 100
              if (correct.cadence) {
                const norm = normalizeCadence(correct.cadence)
                const unlc = parcoursContext?.unlockedCadenceValues
                if (exerciseMode !== 'parcours' || !Array.isArray(unlc) || unlc.length === 0 || (norm && unlc.includes(norm))) maxScore += 10
              }
            }
          })
        }

        // Score final : accord (100) + bonus cadence (10 si attendue et correcte) par marqueur
        const score = maxScore > 0 ? Math.round(((totalScore + totalCadenceBonus) / maxScore) * 100) : 0
        
        // Sauvegarder la tentative (métadonnées pour badges contenu musical)
        const saveResult = await saveAttempt(
          user.uid,
          exercise.id,
          answersArray,
          correctAnswersArray,
          score,
          getExerciseDisplayTitle(exercise, []) || exercise.metadata?.exerciseTitle || exercise.metadata?.workTitle || 'Exercice',
          {
            functionOnlyAvailable: progressionMode === 'functions',
            tonality: exercise?.metadata?.tonality || 'C',
            mode: getExerciseDominantMode(exercise) || 'major',
            assignmentId: assignmentContext?.id || null,
            liveSessionId: liveSessionIdFromState || null,
            assignmentTeacherId: assignmentContext?.teacherId || null,
            assignmentTeacherClassId: assignmentContext?.teacherClassId || null,
            authorId: exercise.authorId ?? null,
            authorName: exercise.authorName ?? null,
            progressionMode,
            exerciseMode,
            parcoursContext: parcoursContext ?? null,
            exerciseMarkers: exercise?.markers ?? null,
            exerciseMetadata: {
              composer: getComposerDisplayName(exercise.metadata) || null,
              difficulty: exercise.metadata?.difficulty ?? null,
              autoTags: exercise.autoTags ?? [],
              sourceNodeId: exerciseMode === 'parcours' ? (nodeId ?? parcoursContext?.currentNodeId ?? null) : null,
              ...(exercise.metadata?.section === 'horizons' && {
                section: 'horizons',
                musicCategory: exercise.metadata?.musicCategory ?? null
              })
            }
          }
        )
        setLastSaveFeedback({
          xpGained: saveResult?.xpGained ?? 0,
          noXpReason: saveResult?.noXpReason ?? null,
          priorBestScore: saveResult?.priorBestScore ?? null,
          score
        })
        // Vérifier et débloquer les badges, puis afficher une notification si nouveaux badges
        try {
          const allAttempts = await getAllUserAttempts(user.uid)
          const sortedAttempts = [...allAttempts].sort((a, b) => {
            const dateA = a.completedAt?.toDate?.() || new Date(a.completedAt || 0)
            const dateB = b.completedAt?.toDate?.() || new Date(b.completedAt || 0)
            return dateB - dateA
          })
          const xpAfterAttempt = (userData?.xp ?? 0) + (saveResult?.xpGained ?? 0)
          const newlyUnlocked = await checkAndUnlockBadges(user.uid, sortedAttempts, { xp: xpAfterAttempt })
          if (newlyUnlocked.length > 0) {
            setNewlyUnlockedBadges(newlyUnlocked)
          }
        } catch (badgeError) {
          console.error('Erreur lors de la vérification des badges:', badgeError)
        }
      } catch (error) {
        console.error('Erreur lors de la sauvegarde de la tentative:', error)
        // Continuer quand même pour afficher les résultats
      }
    }
    
    if (shouldWaitForLiveCorrection) {
      if (liveWaitingStorageKey) {
        try {
          if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(liveWaitingStorageKey, '1')
        } catch {}
      }
      setMode('live_waiting')
      return
    }

    // Le mode review est déjà défini plus haut, pas besoin de setMode('summary')
  }

  const handleReplay = () => {
    setNewlyUnlockedBadges([])
    setLastSaveFeedback(null)
    // Réinitialiser l'exercice
    setMode('exercise')
    setShowOnboarding(true)
    setCurrentMarkerIndex(0)
    setSelectedSegmentIndex(null) // Réinitialiser la sélection de segment
    const answers = {}
    if (exercise && exercise.markers) {
      exercise.markers.forEach((_, index) => {
        answers[index] = null
      })
    }
    setUserAnswers(answers)
    setAnswerValidations({}) // Réinitialiser les validations
    setAnimatedChords(new Set()) // Réinitialiser les accords animés
    setIsPlaying(false)
    if (playerRef.current) {
      playerRef.current.seekTo(exercise.settings.startTime, true)
    }
  }

  const handleQuitExercise = useCallback(() => {
    dismissOnboarding()
    // Après complétion (review ou attente live), la tentative est déjà enregistrée.
    if (mode === 'review' || mode === 'live_waiting') {
      if (playerRef.current && isPlaying) {
        playerRef.current.pauseVideo()
        setIsPlaying(false)
      }
      navigate(returnTo || '/student-dashboard')
      return
    }
    setIsQuitConfirmOpen(true)
  }, [dismissOnboarding, mode, isPlaying, navigate, returnTo])

  const handleCancelQuitExercise = useCallback(() => {
    setIsQuitConfirmOpen(false)
  }, [])

  const handleConfirmQuitExercise = useCallback(() => {
    setIsQuitConfirmOpen(false)
    // Arrêter la lecture si elle est en cours
    if (playerRef.current && isPlaying) {
      playerRef.current.pauseVideo()
      setIsPlaying(false)
    }
    navigate(returnTo || '/student-dashboard')
  }, [isPlaying, navigate, returnTo])

  /** Clic/tap sur la zone vidéo (overlay) : tap sur la zone du bouton Quitter → modal de sortie, sinon play/pause. */
  const handleVideoZoneTap = useCallback((e) => {
    dismissOnboarding()
    const pointX = Number.isFinite(e.clientX) ? e.clientX : null
    const pointY = Number.isFinite(e.clientY) ? e.clientY : null
    const quitRect = quitButtonRef.current?.getBoundingClientRect?.()
    const tapOnQuitButton =
      pointX != null &&
      pointY != null &&
      quitRect &&
      pointX >= quitRect.left &&
      pointX <= quitRect.right &&
      pointY >= quitRect.top &&
      pointY <= quitRect.bottom

    if (tapOnQuitButton) {
      handleQuitExercise()
    } else if (playerRef.current) {
      handlePlayPause()
    }
  }, [dismissOnboarding, handlePlayPause, handleQuitExercise])

  // Calculer les valeurs nécessaires AVANT les returns conditionnels
  // Utiliser des valeurs par défaut si exercise n'existe pas encore
  const videoId = exercise?.video?.id || ''
  const startTime = exercise?.settings?.startTime || 0
  const endTime = exercise?.settings?.endTime || 60
  const videoDuration = exercise?.video?.duration || 0
  // Les marqueurs peuvent être un tableau de timestamps ou d'objets avec {time, chord}
  const markers = exercise?.markers 
    ? exercise.markers.map(m => typeof m === 'number' ? m : m.time || m.absoluteTime)
    : []
  const totalMarkers = markers.length
  const answeredCount = Object.values(userAnswers).filter(a => a !== null).length
  
  // En mode parcours, calculer le nombre d'accords pertinents remplis (legacy, pour validateAllAnswers etc.)
  const answeredRelevantCount = useMemo(() => {
    if (exerciseMode === 'parcours' && relevantChordIndices.length > 0) {
      return relevantChordIndices.filter(index => userAnswers[index] !== null).length
    }
    return Object.values(userAnswers).filter(a => a !== null).length
  }, [exerciseMode, relevantChordIndices, userAnswers])

  const relevantChordsCount = relevantChordIndices.length

  // Cadence attendue sur le marqueur courant (obligatoire si présente)
  // En parcours : n'exiger la cadence que si la bonne réponse est dans les cadences débloquées
  const expectedCadence = useMemo(() => {
    if (!exercise?.markers?.[currentMarkerIndex]) return false
    const marker = exercise.markers[currentMarkerIndex]
    const chord = typeof marker === 'object' && marker.chord ? marker.chord : null
    const correctCadence = chord?.cadence || null
    if (!correctCadence) return false
    const unlocked = parcoursContext?.unlockedCadenceValues
    const inUnlocked = Array.isArray(unlocked) && unlocked.includes(correctCadence)
    return exerciseMode === 'parcours' ? !!inUnlocked : !!correctCadence
  }, [exercise, currentMarkerIndex, exerciseMode, parcoursContext?.unlockedCadenceValues])

  // Liste des cadences à afficher en QCM (filtrée en Parcours par unlockedCadenceValues)
  const qcmCadencesToShow = useMemo(() => {
    const unlocked = parcoursContext?.unlockedCadenceValues
    if (Array.isArray(unlocked) && unlocked.length > 0) return CADENCES.filter(c => unlocked.includes(c.value))
    return CADENCES
  }, [parcoursContext?.unlockedCadenceValues])

  // Liste d'accords pour le QCM : mémoïsée pour éviter de recréer une nouvelle référence à chaque rendu (ex. pendant la lecture vidéo), ce qui ferait re-mélanger les options en permanence
  const qcmAllChords = useMemo(() => {
    if (!exercise?.markers) return []
    return exercise.markers.map(m => typeof m === 'object' && m.chord ? m.chord : null).filter(Boolean)
  }, [exercise?.markers])

  // Élève : afficher Terminer dès qu'il y a des marqueurs (tentative partielle autorisée).
  // Aperçu prof : idem pour accéder à la correction à tout moment.
  const canShowFinishButton = totalMarkers > 0

  // Calculer le temps relatif pour l'affichage
  const relativeCurrentTime = currentTime - startTime
  const relativeEndTime = endTime - startTime
  
  // Liste des marqueurs déjà répondues
  const answeredMarkers = Object.keys(userAnswers).filter(i => userAnswers[i] !== null).map(Number)

  // Calculer les positions et largeurs des cases pour la timeline
  // Chaque segment peut avoir une fin explicite (marker.endTime) pour les accords "crop"
  const chordSegments = useMemo(() => {
    if (!markers || markers.length === 0 || !exercise) return []
    const rawMarkers = exercise.markers || []

    return markers.map((marker, index) => {
      const nextMarkerTime = index < markers.length - 1 ? markers[index + 1] : endTime
      const customEnd = typeof rawMarkers[index] === 'object' && rawMarkers[index]?.endTime != null
        ? Number(rawMarkers[index].endTime)
        : null
      const segmentEndTime = (customEnd != null && customEnd > marker && customEnd <= nextMarkerTime)
        ? customEnd
        : nextMarkerTime

      const segmentStart = marker - startTime
      const segmentEndRel = segmentEndTime - startTime
      const startPos = relativeEndTime > 0 ? (segmentStart / relativeEndTime) * 100 : 0
      const endPos = relativeEndTime > 0 ? (segmentEndRel / relativeEndTime) * 100 : 100
      const width = endPos - startPos

      return {
        index,
        startTime: marker,
        endTime: segmentEndTime,
        startPos,
        endPos,
        width,
        isAnswered: answeredMarkers.includes(index),
        chord: userAnswers[index] || null
      }
    })
  }, [markers, startTime, endTime, relativeEndTime, answeredMarkers, userAnswers, exercise])

  // Suivi du temps pendant la lecture (rAF pour curseur fluide ; setCurrentTime throttlé pour jauges)
  useEffect(() => {
    if (!isPlaying || !playerRef.current || !exercise) {
      if (playbackRafRef.current) {
        cancelAnimationFrame(playbackRafRef.current)
        playbackRafRef.current = null
      }
      return undefined
    }

    let isRunning = true

    const updatePlaybackVisualsDom = (time) => {
      const tl = playheadTimelineRef.current
      const pct = computePlayheadPositionPercent(time, tl)
      if (pct != null) {
        const left = `${pct}%`
        if (playheadRef.current) playheadRef.current.style.left = left
        if (reviewPlayheadRef.current) reviewPlayheadRef.current.style.left = left
        if (timelineRef.current) {
          timelineRef.current.style.setProperty('--playhead-percent', left)
        }
      }

      if (!timelineRef.current) return
      const segs = tl.chordSegments || []
      for (let i = 0; i < segs.length; i++) {
        const segment = segs[i]
        const el = timelineRef.current.querySelector(`[data-segment-progress="${segment.index}"]`)
        if (!el) continue
        const segDuration = segment.endTime - segment.startTime
        let widthPct = 0
        if (segDuration > 0) {
          if (time >= segment.startTime && time <= segment.endTime) {
            widthPct = ((time - segment.startTime) / segDuration) * 100
          } else if (time > segment.endTime) {
            widthPct = 100
          }
        }
        el.style.width = `${widthPct}%`
      }
    }

    const tick = () => {
      if (!isRunning || !isPlaying || !playerRef.current) return
      try {
        const time = playerRef.current.getCurrentTime()
        currentTimeRef.current = time
        updatePlaybackVisualsDom(time)

        const now = Date.now()
        if (now - lastSetStateTimeRef.current >= 50) {
          lastSetStateTimeRef.current = now
          setCurrentTime(time)
        }

        if (time >= exercise.settings.endTime) {
          playerRef.current.pauseVideo()
          setIsPlaying(false)
          currentTimeRef.current = exercise.settings.endTime
          setCurrentTime(exercise.settings.endTime)
          updatePlaybackVisualsDom(exercise.settings.endTime)
          return
        }
      } catch (error) {
        console.error('Erreur lors de la récupération du temps:', error)
      }
      playbackRafRef.current = requestAnimationFrame(tick)
    }

    lastSetStateTimeRef.current = 0
    playbackRafRef.current = requestAnimationFrame(tick)

    return () => {
      isRunning = false
      if (playbackRafRef.current) {
        cancelAnimationFrame(playbackRafRef.current)
        playbackRafRef.current = null
      }
    }
  }, [isPlaying, exercise])

  const desktopResolvedTimeline = useMemo(() => {
    if (isMobile || chordSegments.length === 0) {
      return { segments: [], contentWidthPx: 0 }
    }

    const fallbackWidth = chordSegments.length * DESKTOP_MIN_SEGMENT_PX
    const baseWidthPx = Math.max(timelineViewportWidth, fallbackWidth, 1)
    const rawFirstStartPx = Math.max(0, ((chordSegments[0]?.startPos ?? 0) / 100) * baseWidthPx)
    const rawLastEndPx = Math.max(0, ((chordSegments[chordSegments.length - 1]?.endPos ?? 100) / 100) * baseWidthPx)
    const leadingGapPx = rawFirstStartPx
    const trailingGapPx = Math.max(0, baseWidthPx - rawLastEndPx)
    const rawSpanPx = Math.max(0, rawLastEndPx - rawFirstStartPx)
    let totalDisplayWidthPx = 0

    const resolvedWidths = chordSegments.map((segment) => {
      const rawWidthPx = Math.max(0, (segment.width / 100) * baseWidthPx)
      const displayWidthPx = Math.max(rawWidthPx, DESKTOP_MIN_SEGMENT_PX)
      totalDisplayWidthPx += displayWidthPx
      return { rawWidthPx, displayWidthPx }
    })

    // Trous (gaps) entre segments : quand un accord est crop, réserver l'espace jusqu'au prochain
    const relativeEndTime = endTime - startTime
    let totalGapPx = 0
    const gapPxPerSegment = chordSegments.map((segment, i) => {
      if (i >= chordSegments.length - 1) return 0
      const gapDuration = Math.max(0, chordSegments[i + 1].startTime - segment.endTime)
      const gapPx = relativeEndTime > 0 ? (gapDuration / relativeEndTime) * baseWidthPx : 0
      totalGapPx += gapPx
      return gapPx
    })

    const extraNeededPx = Math.max(0, totalDisplayWidthPx - rawSpanPx)
    const availableGapPx = leadingGapPx + trailingGapPx
    const overflowBeyondGapsPx = Math.max(0, extraNeededPx - availableGapPx)
    const gapShrinkRatio = availableGapPx > 0 ? Math.min(1, extraNeededPx / availableGapPx) : 0
    const adjustedLeadingGapPx = Math.max(0, leadingGapPx * (1 - gapShrinkRatio))
    const adjustedTrailingGapPx = Math.max(0, trailingGapPx * (1 - gapShrinkRatio))
    const contentWidthPx = Math.max(baseWidthPx + overflowBeyondGapsPx + totalGapPx, 1)
    let cursorPx = adjustedLeadingGapPx
    const resolvedSegments = chordSegments.map((segment, index) => {
      const { rawWidthPx, displayWidthPx } = resolvedWidths[index]
      const displayStartPx = cursorPx
      const displayEndPx = displayStartPx + displayWidthPx
      cursorPx = displayEndPx + (gapPxPerSegment[index] || 0)
      return {
        ...segment,
        displayStartPx,
        displayEndPx,
        displayWidthPx,
        displayStartPos: (displayStartPx / contentWidthPx) * 100,
        displayEndPos: (displayEndPx / contentWidthPx) * 100,
        displayWidthPercent: (displayWidthPx / contentWidthPx) * 100,
        isMinWidthClamped: displayWidthPx - rawWidthPx > 0.5
      }
    })

    return { segments: resolvedSegments, contentWidthPx }
  }, [isMobile, chordSegments, timelineViewportWidth, DESKTOP_MIN_SEGMENT_PX, startTime, endTime])

  const desktopSegments = desktopResolvedTimeline.segments
  const desktopTimelineMinWidthPx = desktopResolvedTimeline.contentWidthPx
  const desktopTimelineContentWidthPx = useMemo(
    () => Math.max(Math.ceil(desktopTimelineMinWidthPx), Math.ceil(timelineViewportWidth || 0), chordSegments.length * DESKTOP_MIN_SEGMENT_PX),
    [desktopTimelineMinWidthPx, timelineViewportWidth, chordSegments.length, DESKTOP_MIN_SEGMENT_PX]
  )
  const desktopTimelineContentStyle = useMemo(
    () => (chordSegments.length && !isMobile
      ? { width: `${desktopTimelineContentWidthPx}px`, minWidth: `${desktopTimelineContentWidthPx}px` }
      : undefined),
    [chordSegments.length, isMobile, desktopTimelineContentWidthPx]
  )
  /** En mobile : même largeur que la timeline pour que le bracket de cadence (left/width en %) aligne avec les segments. */
  const timelineContentFrameStyle = useMemo(
    () => (isMobile && chordSegments.length ? { '--segment-count': chordSegments.length } : desktopTimelineContentStyle),
    [isMobile, chordSegments.length, desktopTimelineContentStyle]
  )

  const getSegmentRange = useCallback((startIndex, endIndex) => {
    if (chordSegments.length === 0) return null
    if (isMobile) {
      const X = chordSegments.length
      return {
        leftPos: (startIndex / X) * 100,
        width: ((endIndex - startIndex + 1) / X) * 100
      }
    }
    const startSegment = desktopSegments[startIndex]
    const endSegment = desktopSegments[endIndex]
    if (!startSegment || !endSegment) {
      const rawStartSegment = chordSegments[startIndex]
      const rawEndSegment = chordSegments[endIndex]
      if (!rawStartSegment || !rawEndSegment) return null
      return {
        leftPos: rawStartSegment.startPos,
        width: rawEndSegment.endPos - rawStartSegment.startPos
      }
    }
    return {
      leftPos: startSegment.displayStartPos,
      width: endSegment.displayEndPos - startSegment.displayStartPos
    }
  }, [isMobile, chordSegments.length, desktopSegments])

  const pedalTimelineChords = useMemo(() => {
    const markersList = exercise?.markers ?? []
    const out = mode === 'review'
      ? markersList.map((m) => (typeof m === 'object' && m.chord ? m.chord : null))
      : markersList.map((m, i) => {
          if (isSegmentLockedByParcours(i)) {
            return typeof m === 'object' && m.chord ? m.chord : null
          }
          return normalizeQcmAnswerToChordLike(userAnswers[i] ?? null)
        })
    return out
  }, [exercise?.markers, mode, userAnswers, isSegmentLockedByParcours])

  // Groupes consécutifs emprunt / pédale (pour une seule parenthèse et un seul trait par groupe sur la timeline)
  // En mode review : basé sur la solution (marqueurs). En mode exercice : basé sur les réponses de l'élève (pas de spoiler).
  // Pédale : même logique que l'éditeur (computePedalGroups = degrés effectifs + propagation implicite) pour cohérence agrégée.
  // Enrichissement : si un accord n'a pas pedalDegree mais que son chiffrage affiché contient " / I|II|… " (ex. V7/I), on déduit la pédale pour l'affichage (mode élève / correction / anciens exercices).
  const { borrowedGroups, pedalGroups } = useMemo(() => {
    const chords = pedalTimelineChords
    const enrichedForPedal = chords.map((c, idx) => {
      if (!c || typeof c !== 'object' || c.pedalBlocked === true) return c
      if (c.pedalDegree != null && String(c.pedalDegree).trim() !== '') return c
      const secondaryTarget = c.borrowedFromDegree ?? c.ofDegree
      if (secondaryTarget != null && String(secondaryTarget).trim() !== '') return c
      // Déduire la pédale depuis le chiffrage affiché (objet complet) ou depuis displayLabel (ex. réponses / anciens formats)
      const str = typeof c.displayLabel === 'string' && c.displayLabel.trim() !== ''
        ? c.displayLabel.trim()
        : formatChordDetailed(c)
      const parsed = parseChordDisplayString(str)
      if (parsed.pedalDegree) return { ...c, pedalDegree: parsed.pedalDegree }
      return c
    })
    const runGroups = (predicate) => {
      const out = []
      let start = null
      for (let i = 0; i < chords.length; i++) {
        if (predicate(chords[i])) {
          if (start === null) start = i
        } else {
          if (start !== null) {
            out.push({ startIndex: start, endIndex: i - 1 })
            start = null
          }
        }
      }
      if (start !== null) out.push({ startIndex: start, endIndex: chords.length - 1 })
      return out
    }
    const { groups: pedalGroupsFromUtil } = computePedalGroups(enrichedForPedal, exercisePedalOptions)
    return {
      borrowedGroups: runGroups(c => c?.isBorrowed === true),
      pedalGroups: pedalGroupsFromUtil
    }
  }, [pedalTimelineChords, exercisePedalOptions])

  // Position du curseur : projection visuelle (desktop résolu, mobile bandes égales), y compris dans les gaps
  const playheadPositionPercent = useMemo(() => computePlayheadPositionPercent(currentTime, {
    startTime,
    endTime,
    isMobile,
    chordSegments,
    desktopSegments,
  }), [currentTime, startTime, endTime, isMobile, chordSegments, desktopSegments])

  useEffect(() => {
    playheadTimelineRef.current = { isMobile, chordSegments, desktopSegments, startTime, endTime }
  }, [isMobile, chordSegments, desktopSegments, startTime, endTime])

  // Trouver les cadences pour l'affichage en mode review (avec statut correct/incorrect)
  const getCadences = useMemo(() => {
    if (mode !== 'review' || !exercise || !exercise.markers) return []
    const unlockedScores = parcoursContext?.unlockedCadenceValues
    const onlyUnlockedCadenceForMax = exerciseMode === 'parcours' && Array.isArray(unlockedScores) && unlockedScores.length > 0

    const cadences = []
    exercise.markers.forEach((marker, index) => {
      // En mode review, afficher la cadence de la solution correcte
      const correctAnswer = typeof marker === 'object' && marker.chord ? marker.chord : null
      const userAnswer = userAnswers[index]
      const validation = answerValidations[index]

      // Cadence attendue (même règle que maxScore +10)
      const normCad = correctAnswer?.cadence ? normalizeCadence(correctAnswer.cadence) : null
      const cadenceExpected = !!(correctAnswer?.cadence && (
        !onlyUnlockedCadenceForMax || (normCad && unlockedScores.includes(normCad))
      ))
      const cadenceCorrect = cadenceExpected
        ? !!(validation?.cadenceBonus > 0)
        : undefined

      // Déterminer quelle cadence afficher :
      // - Si l'élève a mis une cadence correcte, afficher celle de l'élève
      // - Sinon, afficher la cadence de la solution
      let cadenceToDisplay = null
      if (validation && validation.cadenceBonus > 0 && userAnswer?.cadence) {
        cadenceToDisplay = userAnswer.cadence
      } else if (correctAnswer?.cadence) {
        cadenceToDisplay = correctAnswer.cadence
      }

      if (cadenceToDisplay) {
        cadences.push({
          index,
          cadence: cadenceToDisplay,
          startMarker: index > 0 ? index - 1 : null,
          endMarker: index,
          cadenceExpected,
          cadenceCorrect
        })
      }
    })
    return cadences
  }, [mode, exercise, userAnswers, answerValidations, exerciseMode, parcoursContext?.unlockedCadenceValues])

  // Labels des cadences (partagé pour accolade au-dessus de la timeline)
  const cadenceLabelsMap = useMemo(() => ({
    perfect: 'Parfaite',
    imperfect: 'Imparfaite',
    plagal: 'Plagale',
    rompue: 'Rompue',
    évitée: 'Évitée',
    'demi-cadence': 'Demi-cadence',
    half: 'Demi-cadence'
  }), [])

  // Liste des cadences pour l'accolade au-dessus de la timeline (exercice + review)
  // En parcours : n'afficher l'accolade que pour les cadences débloquées
  const getCadencesForBrace = useMemo(() => {
    if (mode === 'review') {
      return getCadences.map(c => ({
        ...c,
        cadenceLabel: cadenceLabelsMap[c.cadence] || c.cadence
      }))
    }
    // Mode exercice : marqueurs qui attendent une cadence (débloquée en parcours)
    const unlocked = parcoursContext?.unlockedCadenceValues
    const onlyUnlocked = exerciseMode === 'parcours' && Array.isArray(unlocked) && unlocked.length > 0
    const list = []
    ;(exercise?.markers ?? []).forEach((marker, index) => {
      const chord = typeof marker === 'object' && marker.chord ? marker.chord : null
      if (!chord?.cadence) return
      if (onlyUnlocked) {
        const normalized = normalizeCadence(chord.cadence)
        if (!normalized || !unlocked.includes(normalized)) return
      }
      const val = userAnswers[index]?.cadence
      list.push({
        startMarker: index > 0 ? index - 1 : null,
        endMarker: index,
        cadence: val,
        cadenceLabel: val ? (cadenceLabelsMap[val] || val) : 'Cadence'
      })
    })
    return list
  }, [mode, getCadences, exercise?.markers, userAnswers, cadenceLabelsMap, exerciseMode, parcoursContext?.unlockedCadenceValues])

  // Trouver l'accord actuel (celui au-dessus du curseur de lecture)
  const findCurrentChordSegment = useMemo(() => {
    if (!chordSegments || chordSegments.length === 0) return null
    
    // Si un marqueur est explicitement sélectionné (via navigation avec flèches), utiliser celui-ci
    if (selectedMarkerId !== null && selectedMarkerId >= 0 && selectedMarkerId < chordSegments.length) {
      return chordSegments[selectedMarkerId]
    }
    
    // Sinon, trouver le segment qui contient le currentTime
    // À la frontière entre deux segments (t = endTime du précédent = startTime du suivant),
    // on préfère le segment qui commence à cet instant (accord « suivant »)
    for (let i = 0; i < chordSegments.length; i++) {
      const segment = chordSegments[i]
      if (currentTime === segment.startTime) return segment
      if (currentTime > segment.startTime && currentTime <= segment.endTime) {
        const next = chordSegments[i + 1]
        if (currentTime === segment.endTime && next && next.startTime === currentTime) return next
        return segment
      }
    }
    
    // Si on est avant le premier marqueur, retourner le premier segment
    if (chordSegments.length > 0 && currentTime < chordSegments[0].startTime) {
      return chordSegments[0]
    }
    
    // Si on est après le dernier marqueur, retourner le dernier segment
    if (chordSegments.length > 0 && currentTime > chordSegments[chordSegments.length - 1].endTime) {
      return chordSegments[chordSegments.length - 1]
    }
    
    return null
  }, [chordSegments, currentTime, selectedMarkerId])

  // Résoudre l'index du segment sous un timestamp donné
  const resolveSegmentIndexAtTime = useCallback((time) => {
    if (!chordSegments?.length || typeof time !== 'number') return null
    let segment = null
    for (let i = 0; i < chordSegments.length; i++) {
      const seg = chordSegments[i]
      if (time === seg.startTime) { segment = seg; break }
      if (time > seg.startTime && time <= seg.endTime) {
        const next = chordSegments[i + 1]
        if (time === seg.endTime && next && next.startTime === time) { segment = next; break }
        segment = seg
        break
      }
    }
    if (!segment) {
      if (time < chordSegments[0].startTime) segment = chordSegments[0]
      else if (time > chordSegments[chordSegments.length - 1].endTime) segment = chordSegments[chordSegments.length - 1]
    }
    return segment?.index ?? null
  }, [chordSegments])

  // Fonction pour ouvrir la modale pour l'accord actuel (utilise getCurrentTime() au clic pour éviter state stale sur mobile)
  const handleOpenCurrentChordModal = useCallback(() => {
    if (!chordSegments?.length || !playerRef.current) return
    if (liveSessionIdFromState && !isTeacherPreview && liveSessionMeta?.status === 'correction_released') return
    const t = playerRef.current.getCurrentTime()
    if (typeof t !== 'number') return
    const idx = resolveSegmentIndexAtTime(t)
    if (idx === null || isSegmentLockedByParcours(idx)) return
    if (!isModalOpen) {
      openAnswerPanel(idx)
    } else if (idx !== currentMarkerIndex) {
      setSelectedMarkerId(idx)
      setCurrentMarkerIndex(idx)
    }
  }, [chordSegments, resolveSegmentIndexAtTime, isSegmentLockedByParcours, liveSessionIdFromState, isTeacherPreview, liveSessionMeta?.status, isModalOpen, currentMarkerIndex, openAnswerPanel])

  const seekFromTimelineClientX = useCallback((clientX) => {
    if (!playerRef.current || !exercise || !timelineRef.current) return
    dismissOnboarding()
    const scrollContainer = timelineOverlayRef.current || timelineViewportRef.current
    const time = resolveTimeFromTimelineX(clientX, scrollContainer, timelineRef.current, {
      isMobile,
      chordSegments,
      desktopSegments,
      startTime,
      endTime,
    })
    if (time == null) return
    seekPreservingPlayback(time)
  }, [exercise, isMobile, chordSegments, desktopSegments, startTime, endTime, dismissOnboarding, seekPreservingPlayback])

  const handleTimelineScrubPointerDown = useCallback((e) => {
    if (!playerRef.current || !exercise) return
    if (e.target.closest?.('.player-chord-segment')) return
    if (isMobile && !e.target.closest?.('.timeline-scrub-rail')) return
    scrubDragRef.current = {
      active: true,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
    }
    e.currentTarget.setPointerCapture?.(e.pointerId)
    dismissOnboarding()
  }, [exercise, isMobile, dismissOnboarding])

  const handleTimelineScrubPointerMove = useCallback((e) => {
    const drag = scrubDragRef.current
    if (!drag.active || drag.pointerId !== e.pointerId) return
    const dx = Math.abs(e.clientX - drag.startX)
    const dy = Math.abs(e.clientY - drag.startY)
    if (!drag.moved && dx < 5 && dy < 5) return
    drag.moved = true
    e.preventDefault()
    seekFromTimelineClientX(e.clientX)
  }, [seekFromTimelineClientX])

  const handleTimelineScrubPointerUp = useCallback((e) => {
    const drag = scrubDragRef.current
    if (!drag.active || drag.pointerId !== e.pointerId) return
    if (!drag.moved) {
      seekFromTimelineClientX(e.clientX)
    }
    scrubDragRef.current = { active: false, pointerId: null, startX: 0, startY: 0, moved: false }
    e.currentTarget.releasePointerCapture?.(e.pointerId)
  }, [seekFromTimelineClientX])

  const handleTimelineScrubPointerCancel = useCallback((e) => {
    if (scrubDragRef.current.pointerId === e.pointerId) {
      scrubDragRef.current = { active: false, pointerId: null, startX: 0, startY: 0, moved: false }
    }
    e.currentTarget.releasePointerCapture?.(e.pointerId)
  }, [])

  // Panneau ouvert : synchroniser la question active avec le curseur pendant la lecture
  useEffect(() => {
    if (!isAnswerPanelOpen || !chordSegments?.length) return
    const idx = resolveSegmentIndexAtTime(currentTime)
    if (idx === null || isSegmentLockedByParcours(idx) || idx === currentMarkerIndex) return
    setCurrentMarkerIndex(idx)
    setSelectedMarkerId(idx)
  }, [currentTime, isAnswerPanelOpen, chordSegments, resolveSegmentIndexAtTime, isSegmentLockedByParcours, currentMarkerIndex])

  // Highlight segment actif : scroll horizontal si hors viewport
  useEffect(() => {
    if (!isAnswerPanelOpen || !timelineRef.current) return
    const el = timelineRef.current.querySelector(`[data-segment-index="${currentMarkerIndex}"]`)
    if (!el) return
    const overlay = timelineOverlayRef.current
    if (overlay) {
      const elRect = el.getBoundingClientRect()
      const overlayRect = overlay.getBoundingClientRect()
      const inView = elRect.left >= overlayRect.left && elRect.right <= overlayRect.right
      if (inView) return
    }
    el.scrollIntoView({ inline: 'nearest', behavior: 'smooth', block: 'nearest' })
  }, [currentMarkerIndex, isAnswerPanelOpen])

  // Recalcul taille lecteur YouTube quand le panneau overlay s'ouvre/ferme (évite le rétrécissement visuel)
  useEffect(() => {
    const t = window.setTimeout(() => {
      window.dispatchEvent(new Event('resize'))
    }, 80)
    return () => clearTimeout(t)
  }, [isAnswerPanelOpen, progressionMode])

  // Gestion des raccourcis clavier
  useEffect(() => {
    const keyboardBlockedByOverlay = isModalOpen && !useInlineAnswerPanel

    const handleKeyDown = (e) => {
      // Ne pas intercepter si on est dans un input ou textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return
      }

      // Espace : Play/Pause (déjà géré par YouTube, mais on peut le forcer)
      if (e.key === ' ' && !keyboardBlockedByOverlay) {
        e.preventDefault()
        handlePlayPause()
      }

      // Entrée : Ouvrir la modale pour l'accord actuel
      if (e.key === 'Enter' && !keyboardBlockedByOverlay && findCurrentChordSegment) {
        if (
          !isSegmentLockedByParcours(findCurrentChordSegment.index)
          && !(liveSessionIdFromState && !isTeacherPreview && liveSessionMeta?.status === 'correction_released')
        ) {
          e.preventDefault()
          handleOpenCurrentChordModal()
        }
      }

      // Échap : Fermer le panneau / modale
      if (e.key === 'Escape' && isModalOpen) {
        e.preventDefault()
        closeAnswerPanel()
      }

      // Échap : masquer le coachmark si le panneau est déjà fermé
      if (e.key === 'Escape' && !isModalOpen && showOnboarding) {
        e.preventDefault()
        dismissOnboarding()
      }

      // Flèche gauche : Marqueur précédent
      if (e.key === 'ArrowLeft' && !keyboardBlockedByOverlay) {
        e.preventDefault()
        const currentPos = currentTimeRef.current || currentTime
        const prevIndex = findPreviousMarker(currentPos)
        if (prevIndex !== null) {
          handleSeekToMarker(prevIndex)
        }
      }

      // Flèche droite : Marqueur suivant
      if (e.key === 'ArrowRight' && !keyboardBlockedByOverlay) {
        e.preventDefault()
        const currentPos = currentTimeRef.current || currentTime
        const nextIndex = findNextMarker(currentPos)
        if (nextIndex !== null) {
          handleSeekToMarker(nextIndex)
        } else if (markers.length > 0) {
          handleSeekToMarker(0)
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, useInlineAnswerPanel, showOnboarding, findCurrentChordSegment, currentTime, markers.length, isSegmentLockedByParcours, dismissOnboarding, closeAnswerPanel, handleOpenCurrentChordModal, handleSeekToMarker])

  // Calculer les scores pour le dashboard (en mode review)
  // IMPORTANT: Ce hook doit être AVANT tous les returns conditionnels
  const calculateScores = useMemo(() => {
    if (!exercise || !exercise.markers) {
      return { score: 0, perfectCount: 0, partialCount: 0, totalCount: 0, scorePercentage: 0, cadenceCorrectCount: 0, cadenceTotalCount: 0 }
    }

    const results = exercise.markers.map((marker, index) => {
      const userAnswer = userAnswers[index]
      const validation = answerValidations[index]
      const isPerfect = validation && validation.level === 1
      const isPartiallyCorrect = validation && (validation.level === 0.5 || validation.level === 2 || validation.level === 3)
      return { isPerfect, isPartiallyCorrect }
    })

    // En mode parcours : ne compter que les cases remplissables (non grisées) dans le décompte
    const indicesToCount = exerciseMode === 'parcours'
      ? exercise.markers.map((_, i) => i).filter(i => !isSegmentLockedByParcours(i))
      : exercise.markers.map((_, i) => i)

    const perfectCount = indicesToCount.filter(i => results[i].isPerfect).length
    const partialCount = indicesToCount.filter(i => results[i].isPartiallyCorrect && !results[i].isPerfect).length
    const totalCount = indicesToCount.length

    let totalScore = 0
    let maxScore = 0
    let cadenceCorrectCount = 0
    let cadenceTotalCount = 0
    const unlockedScores = parcoursContext?.unlockedCadenceValues
    const onlyUnlockedCadenceForMax = exerciseMode === 'parcours' && Array.isArray(unlockedScores) && unlockedScores.length > 0
    indicesToCount.forEach((index) => {
      const marker = exercise.markers[index]
      const correctAnswer = typeof marker === 'object' && marker.chord ? marker.chord : null
      maxScore += 100
      if (correctAnswer?.cadence) {
        const normCad = normalizeCadence(correctAnswer.cadence)
        if (!onlyUnlockedCadenceForMax || (normCad && unlockedScores.includes(normCad))) {
          maxScore += 10
          cadenceTotalCount += 1
        }
      }
      const validation = answerValidations[index]
      if (validation?.cadenceBonus > 0) cadenceCorrectCount += 1
      if (validation) {
        totalScore += validation.score + (validation.cadenceBonus || 0)
      } else {
        const r = results[index]
        totalScore += r?.isPerfect ? 100 : 0
      }
    })
    const scorePercentage = maxScore > 0 ? Math.round((totalScore / maxScore) * 100) : 0
    const score = Math.round(scorePercentage / 10)

    return { score, perfectCount, partialCount, totalCount, scorePercentage, cadenceCorrectCount, cadenceTotalCount }
  }, [exercise, userAnswers, answerValidations, exerciseMode, parcoursContext?.unlockedCadenceValues, isSegmentLockedByParcours])

  // FLIP : lier tailles/positions des cases exercice → correction (aperçu prof).
  // getBoundingClientRect() donne la position réelle rendue (cadences, pédales, etc. déjà pris en compte).
  // On mesure la vue correction après un rAF pour que le layout (accolades cadence, bandeau pédale) soit terminé.
  useEffect(() => {
    if (mode !== 'review' || !isTeacherPreview || chordSegments.length === 0) {
      setSegmentFlipTransforms(null)
      return
    }
    const initialRects = exerciseSegmentRectsRef.current
    if (!initialRects || initialRects.length !== chordSegments.length) {
      setSegmentFlipTransforms(null)
      return
    }
    const run = () => {
      const container = document.querySelector('.player-review-integrated-timeline .player-timeline-with-segments')
      if (!container) return
      const segs = container.querySelectorAll('.player-chord-segment--flip-reveal[data-segment-index]')
      if (segs.length !== chordSegments.length) return
      const finalRects = Array.from(segs)
        .sort((a, b) => Number(a.dataset.segmentIndex) - Number(b.dataset.segmentIndex))
        .map((el) => el.getBoundingClientRect())
      const transforms = finalRects.map((final, i) => {
        const initial = initialRects[i]
        if (!initial || final.width < 1 || final.height < 1) return { scaleX: 1, scaleY: 1, translateX: 0, translateY: 0 }
        return {
          scaleX: initial.width / final.width,
          scaleY: initial.height / final.height,
          translateX: initial.left - final.left,
          translateY: initial.top - final.top
        }
      })
      setSegmentFlipTransforms(transforms)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setSegmentFlipTransforms(transforms.map(() => ({ scaleX: 1, scaleY: 1, translateX: 0, translateY: 0 })))
        })
      })
    }
    const raf = requestAnimationFrame(() => run())
    return () => cancelAnimationFrame(raf)
  }, [mode, isTeacherPreview, chordSegments.length])

  // Sélectionner automatiquement le premier segment en mode review
  // IMPORTANT: Ce hook doit être AVANT tous les returns conditionnels
  useEffect(() => {
    if (mode === 'review' && selectedSegmentIndex === null && chordSegments.length > 0) {
      setSelectedSegmentIndex(0)
    }
  }, [mode, selectedSegmentIndex, chordSegments.length])

  // Au passage en mode review, le lecteur YouTube est remonté : resynchroniser la position sur l'extrait.
  useEffect(() => {
    const enteredReview = mode === 'review' && prevModeRef.current !== 'review'
    prevModeRef.current = mode
    if (!enteredReview || !exercise) return

    const seg = chordSegments[0]
    const target = seg?.startTime ?? exercise.settings?.startTime ?? 0
    playerSyncOnReadyRef.current = target
    currentTimeRef.current = target
    setCurrentTime(target)
    if (playerRef.current) {
      try {
        playerRef.current.seekTo(target, true)
        playerSyncOnReadyRef.current = null
      } catch (_) { /* lecteur pas encore prêt */ }
    }
  }, [mode, exercise, chordSegments])

  // En mode review : mettre à jour le segment affiché quand la lecture avance (libre + parcours)
  useEffect(() => {
    if (mode !== 'review' || !isPlaying || !chordSegments?.length) return
    for (let i = 0; i < chordSegments.length; i++) {
      const seg = chordSegments[i]
      if (currentTime >= seg.startTime && currentTime <= seg.endTime) {
        setSelectedSegmentIndex(i)
        return
      }
      // Curseur dans un trou (gap) entre deux segments : ne surligner aucun segment
      if (i < chordSegments.length - 1 && currentTime > seg.endTime && currentTime < chordSegments[i + 1].startTime) {
        setSelectedSegmentIndex(null)
        return
      }
      if (i === chordSegments.length - 1 && currentTime > seg.endTime) {
        setSelectedSegmentIndex(i)
        return
      }
    }
    if (currentTime < chordSegments[0].startTime) setSelectedSegmentIndex(0)
  }, [mode, isPlaying, currentTime, chordSegments])

  // Casting émotionnel : tirer une fois par exercice quand le film est activé
  useEffect(() => {
    if (mode !== 'review' || !emotionFeedbackEnabled || !exercise?.id) return
    const solutions = (pedalAwareCorrectAnswers || []).map((c, i) => {
      if (c) return c
      const m = exercise.markers?.[i]
      return m && typeof m === 'object' && m.chord ? m.chord : null
    })
    const hasSolutions = solutions.some(Boolean)
    if (!hasSolutions) return
    const alreadyForExercise = emotionCast
      && emotionCastExerciseIdRef.current === exercise.id
      && Object.keys(emotionCast).length > 0
    if (alreadyForExercise) return
    const keys = collectEmotionKeysFromSolutions(solutions)
    setEmotionCast(buildEmotionCast(keys))
    emotionCastExerciseIdRef.current = exercise.id
  }, [mode, emotionFeedbackEnabled, exercise?.id, exercise?.markers, pedalAwareCorrectAnswers, emotionCast])

  // Nouvel exercice → invalider le cast
  useEffect(() => {
    if (!exercise?.id) return
    if (emotionCastExerciseIdRef.current && emotionCastExerciseIdRef.current !== exercise.id) {
      setEmotionCast(null)
      emotionCastExerciseIdRef.current = null
    }
  }, [exercise?.id])

  const toggleEmotionFeedback = useCallback(() => {
    setEmotionFeedbackEnabled((prev) => {
      const next = !prev
      try {
        window.localStorage.setItem(EMOTION_FEEDBACK_STORAGE_KEY, next ? '1' : '0')
      } catch (_) { /* ignore */ }
      return next
    })
  }, [])

  let safeLiveTimerRemainingSecForDeps = '__UNSET__'
  try {
    safeLiveTimerRemainingSecForDeps = liveTimerRemainingSec
  } catch {
    safeLiveTimerRemainingSecForDeps = '__TDZ__'
  }

  useEffect(() => {
    if (!LIVE_REMOTE_ENABLED || !isTeacherPreview || !liveSessionMeta?.id || !user?.uid || !exercise?.settings) return
    const command = liveRuntimeState?.command
    const seq = Number(command?.seq || 0)
    if (!Number.isFinite(seq) || seq <= 0 || seq <= lastHandledRemoteSeqRef.current) {
      return
    }
    lastHandledRemoteSeqRef.current = seq

    const commandType = String(command?.type || '').trim()
    const payload = command?.payload && typeof command.payload === 'object' ? command.payload : {}

    const run = async () => {
      let status = 'handled'
      try {
        if (commandType === 'play') {
          const start = Number(exercise.settings.startTime || 0)
          const end = Number(exercise.settings.endTime || 0)
          const endExclusive = end > start ? Math.max(start, end - 0.05) : start
          const remembered = Number(currentTimeRef.current || currentTime || start)
          const boundedRemembered = Number.isFinite(remembered)
            ? Math.max(start, Math.min(endExclusive, remembered))
            : start
          playerRef.current?.seekTo?.(boundedRemembered, true)
          setCurrentTime(boundedRemembered)
          currentTimeRef.current = boundedRemembered
          // Evite qu'un onStateChange PLAYING transitoire recale a tort sur startTime.
          skipNextPlayNormalizationRef.current = true
          playerRef.current?.playVideo?.()
          setIsPlaying(true)
        } else if (commandType === 'pause') {
          playerRef.current?.pauseVideo?.()
          setIsPlaying(false)
        } else if (commandType === 'seek_start') {
          if (playerRef.current) {
            const start = Number(exercise.settings.startTime || 0)
            playerRef.current.seekTo?.(start, true)
            setCurrentTime(start)
            currentTimeRef.current = start
          }
        } else if (commandType === 'marker_prev' || commandType === 'marker_next') {
          if (playerRef.current && Array.isArray(chordSegments) && chordSegments.length > 0) {
            const currentPos = Number(currentTimeRef.current || currentTime || 0)
            const markerIndex = commandType === 'marker_prev'
              ? (() => {
                for (let i = chordSegments.length - 1; i >= 0; i -= 1) {
                  if (chordSegments[i].startTime < currentPos) return i
                }
                return 0
              })()
              : (() => {
                for (let i = 0; i < chordSegments.length; i += 1) {
                  if (chordSegments[i].startTime > currentPos) return i
                }
                return chordSegments.length - 1
              })()
            const target = chordSegments[Math.max(0, Math.min(chordSegments.length - 1, markerIndex))]
            if (target) {
              playerRef.current.seekTo?.(target.startTime, true)
              setCurrentTime(target.startTime)
              currentTimeRef.current = target.startTime
              setSelectedSegmentIndex(Math.max(0, Math.min(chordSegments.length - 1, markerIndex)))
            }
          }
        } else if (commandType === 'seek_relative' || commandType === 'seek_absolute') {
          if (playerRef.current) {
            const start = Number(exercise.settings.startTime || 0)
            const end = Number(exercise.settings.endTime || 0)
            const current = Number(currentTimeRef.current || currentTime || start)
            const requested = commandType === 'seek_absolute'
              ? Number(payload?.positionSec || current)
              : current + Number(payload?.deltaSec || 0)
            const bounded = Math.max(start, Math.min(end, Number.isFinite(requested) ? requested : current))
            playerRef.current.seekTo?.(bounded, true)
            setCurrentTime(bounded)
            currentTimeRef.current = bounded
          }
        } else if (commandType === 'show_correction') {
          if (mode !== 'review') await handleFinishExercise()
        } else if (commandType === 'next_exercise' || commandType === 'prev_exercise') {
          const queue = Array.isArray(liveSessionMeta.exerciseQueue) ? liveSessionMeta.exerciseQueue : []
          const currentIndex = Number(liveSessionMeta.currentExerciseIndex || 0)
          const offset = commandType === 'next_exercise' ? 1 : -1
          const targetIndex = currentIndex + offset
          const target = queue[targetIndex]
          const targetExerciseId = String(target?.exerciseId || '').trim()
          if (!targetExerciseId) throw new Error('Exercice live indisponible.')
          if (commandType === 'next_exercise') await advanceLiveSessionExercise(liveSessionMeta.id, user.uid)
          else await rewindLiveSessionExercise(liveSessionMeta.id, user.uid)
          setLiveSessionMeta((prev) => {
            if (!prev) return prev
            return {
              ...prev,
              currentExerciseIndex: Math.max(0, Number(prev.currentExerciseIndex || 0) + offset),
              status: 'collecting'
            }
          })
          navigate(`/play/${targetExerciseId}`, {
            state: {
              teacherPreview: isTeacherPreview,
              liveSessionId: liveSessionIdFromState || liveSessionMeta.id || null,
              liveCorrectionReleased: false,
              returnTo: returnTo || '/dashboard/live-sessions'
            }
          })
        } else if (commandType === 'timer_start') {
          const durationSec = Math.max(10, Number(liveSessionMeta?.timer?.durationSec || liveTimerControlSec || 120))
          updateLiveTimer({
            enabled: true,
            durationSec,
            remainingSec: durationSec,
            paused: false,
            startedAt: new Date()
          })
        } else if (commandType === 'timer_pause') {
          const durationSec = Math.max(10, Number(liveSessionMeta?.timer?.durationSec || liveTimerControlSec || 120))
          updateLiveTimer({
            enabled: true,
            durationSec,
            remainingSec: Number.isFinite(liveTimerRemainingSec) ? liveTimerRemainingSec : durationSec,
            paused: true,
            startedAt: null
          })
        } else if (commandType === 'timer_reset') {
          const durationSec = Math.max(10, Number(liveTimerControlSec || liveSessionMeta?.timer?.durationSec || 120))
          updateLiveTimer({
            enabled: true,
            durationSec,
            remainingSec: durationSec,
            paused: true,
            startedAt: null
          })
        } else if (commandType === 'timer_hide') {
          const durationSec = Math.max(10, Number(liveSessionMeta?.timer?.durationSec || liveTimerControlSec || 120))
          updateLiveTimer({
            enabled: false,
            durationSec,
            remainingSec: Number.isFinite(liveTimerRemainingSec) ? liveTimerRemainingSec : durationSec,
            paused: true,
            startedAt: null
          })
        } else if (commandType === 'timer_set_duration') {
          const durationSec = Math.max(10, Number(payload?.durationSec || 120))
          setLiveTimerControlSec(durationSec)
          updateLiveTimer({
            enabled: true,
            durationSec,
            remainingSec: durationSec,
            paused: true,
            startedAt: null
          })
        } else {
          status = 'ignored'
        }
      } catch (_) {
        status = 'error'
      } finally {
        ackLiveRuntimeCommand(liveSessionMeta.id, user.uid, command, { status }).catch(() => {})
      }
    }

    run()
  }, [
    currentTime,
    chordSegments,
    exercise?.settings,
    isTeacherPreview,
    liveSessionIdFromState,
    liveSessionMeta?.currentExerciseIndex,
    liveSessionMeta?.exerciseQueue,
    liveSessionMeta?.id,
    liveSessionMeta?.timer?.durationSec,
    liveRuntimeState?.command?.payload,
    liveRuntimeState?.command?.seq,
    liveRuntimeState?.command?.type,
    liveTimerControlSec,
    safeLiveTimerRemainingSecForDeps,
    mode,
    navigate,
    returnTo,
    updateLiveTimer,
    user?.uid
  ])

  const opts = useMemo(() => {
    const origin = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : undefined
    return {
      height: '100%',
      width: '100%',
      playerVars: {
        autoplay: 0,
        controls: 0,
        modestbranding: 1,
        rel: 0,
        enablejsapi: 1,
        ...(origin ? { origin } : {}),
        ...(isIOSDevice ? { playsinline: 1 } : {})
      }
    }
  }, [isIOSDevice])

  ytOptsDebugRef.current = opts.playerVars

  if (loading) {
    return (
      <div className="app">
        <div className="container">
          <div className="dashboard-loading">
            <div className="spinner"></div>
            <p>Chargement de l'exercice...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !exercise) {
    const isBetaWall = error === 'BETA_RESTRICTED'
    return (
      <div className="app">
        <div className="container">
          <div className="dashboard-empty player-beta-wall">
            <div className="dashboard-empty-icon">{isBetaWall ? '🔒' : '⚠️'}</div>
            <h2>{isBetaWall ? 'Accès réservé' : (error || 'Exercice introuvable')}</h2>
            <p>
              {isBetaWall
                ? (betaPrivateActive && !hasFullCloudAccess
                  ? "Cet exercice ne fait pas partie de la vitrine beta ou votre compte n'est pas autorisé."
                  : "L'exercice demandé n'est pas accessible.")
                : "L'exercice demandé n'existe pas ou n'est pas accessible."}
            </p>
            <div className="player-beta-wall-actions">
              <button type="button" className="cta-primary" onClick={() => navigate('/')}>
                Retour à l’accueil
              </button>
              {!user && (
                <button type="button" className="player-beta-wall-secondary" onClick={() => navigate('/')}>
                  Se connecter
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Format du temps relatif (pour le timer général)
  const formatRelativeTime = (seconds) => {
    if (!exercise) return '00:00.0'
    const relativeTime = Math.max(0, seconds - startTime)
    return formatTimeDetailed(relativeTime)
  }

  const formatTimeDisplay = (seconds) => {
    if (!seconds || seconds < 0) return '00:00'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const formatTimeDetailed = (seconds) => {
    if (!seconds || seconds < 0) return '00:00.0'
    const mins = Math.floor(seconds / 60)
    const secs = (seconds % 60).toFixed(1)
    return `${mins.toString().padStart(2, '0')}:${secs.padStart(4, '0')}`
  }

  // Trouver le prochain marqueur à répondre (pour l'effet visuel)
  const getNextMarkerIndex = () => {
    for (let i = 0; i < markers.length; i++) {
      if (!answeredMarkers.includes(i)) {
        return i
      }
    }
    return null
  }
  const nextMarkerIndex = getNextMarkerIndex()
  
  // Fonctions de formatage d'accords (inspirées de Editor.jsx)
  const SPECIAL_ROOT_TO_FUNCTION = {
    'N': 'SD',   // Sixte napolitaine → Sous-Dominante
    'It': 'D',   // Sixte italienne → Dominante
    'Fr': 'D',   // Sixte française → Dominante
    'Gr': 'D'    // Sixte allemande → Dominante
  }

  /** Extrait la fonction T/SD/D depuis une chaîne qcmAnswer (ex. "I5", "II♭6", "cad6/4") */
  const getFunctionFromQcmAnswer = (qcmAnswer) => {
    if (!qcmAnswer || typeof qcmAnswer !== 'string') return null
    const s = qcmAnswer.trim()
    if (s.startsWith('It+6') || s.startsWith('Fr+6') || s.startsWith('Gr+6')) return 'D'
    if (s.startsWith('II♭') || s.startsWith('IIb')) return 'SD'
    if (s.startsWith('cad') || s.startsWith('Cad.')) return 'D' // 64 de cadence = fonction dominante
    const degreeMatch = s.match(/^(III|VII|II|IV|VI|I|V)(♭|♯|b|#)?/)
    const degree = degreeMatch ? degreeMatch[1] : null
    if (degree) {
      const functions = DEGREE_TO_FUNCTIONS[degree] || []
      return functions.length > 0 ? functions[0] : null
    }
    return null
  }

  const getChordFunction = (chord, markerIndex = null) => {
    if (!chord) return null
    
    const { degree, specialRoot, selectedFunction, function: qcmFunction, qcmAnswer } = chord
    const chordContext = markerIndex != null && exercise?.markers
      ? buildChordContext(
          exercise.markers.map((m) => (typeof m === 'object' && m.chord ? m.chord : null)),
          markerIndex
        )
      : {}
    
    // 1. Réponse QCM avec fonction seule (T/SD/D)
    if (qcmFunction && ['T', 'SD', 'D'].includes(qcmFunction)) {
      return qcmFunction
    }
    // 2. Si une fonction est explicitement sélectionnée, l'utiliser
    if (selectedFunction) {
      return selectedFunction
    }
    
    // 3. Si c'est une racine spéciale, utiliser le mapping
    if (specialRoot) {
      return SPECIAL_ROOT_TO_FUNCTION[specialRoot] || null
    }
    
    // 4. I64 de passage ou cadence (V64 / cad64) → D
    if (degree === 'I' && chord.figure === '64' && (chord.sixFourVariant === 'passing' || chord.sixFourVariant === 'cadential')) {
      return 'D'
    }
    // 5. Degré ambigu ou simple : inférence contextuelle (ex. vi avant V → SD)
    if (degree) {
      return getDefaultFunctionForChord(chord, chordContext)
    }
    
    // 6. Réponse QCM avec accord (qcmAnswer) : dériver la fonction depuis la chaîne (ex. "I5" → T, "cad6/4" → D)
    if (qcmAnswer) {
      return getFunctionFromQcmAnswer(qcmAnswer)
    }
    
    return null
  }

  const getDegreeLabel = getDegreeLabelForMode

  const formatChordLabel = (chord) => {
    if (!chord) return null
    
    // En mode élève, utiliser le mode depuis localStorage élève
    let degreeMode = chord.degreeMode
    if (!degreeMode) {
      const savedMode = localStorage.getItem('chordSelectorDegreeModeStudent')
      degreeMode = savedMode || 'generic'
    }
    const { degree, accidental, quality, figure, isBorrowed, specialRoot, selectedFunction, sixFourVariant } = chord
    
    // Si seule une fonction est sélectionnée (sans degré)
    if (selectedFunction && !degree && !specialRoot) {
      return { 
        function: selectedFunction,
        isFunctionOnly: true
      }
    }
    
    if (specialRoot) {
      if (specialRoot === 'N') {
        const napolitaineLabel = getDegreeLabel('II', degreeMode)
        const hasDiminished = napolitaineLabel.includes('°')
        const degreeWithoutSymbol = napolitaineLabel.replace('°', '')
        return { 
          degree: degreeWithoutSymbol, 
          accidental: '♭', 
          figure: '6',
          quality: hasDiminished ? '°' : ''
        }
      } else {
        const special = { 'It': 'It', 'Fr': 'Fr', 'Gr': 'Gr' }[specialRoot]
        return { degree: special, figure: '+6' }
      }
    }
    
    if (!degree) return null
    
    // I64 dépendant du contexte : V64 (passage), cad64 (cadence), I64 (avancé)
    const isI64 = degree === 'I' && figure === '64'
    const displayDegreeRaw = isI64 && sixFourVariant === 'passing' ? 'V' : isI64 && sixFourVariant === 'cadential' ? 'cad' : degree
    const displayDegree = displayDegreeRaw === 'cad' ? 'Cad.' : getDegreeLabel(displayDegreeRaw, degreeMode)
    const hasDiminished = displayDegree.includes('°')
    const degreeWithoutSymbol = displayDegree.replace('°', '').trim()
    
    let finalQuality = quality
    if (hasDiminished && displayDegreeRaw !== 'cad') {
      if (!quality || quality === '°') {
        finalQuality = '°'
      }
    } else if (!quality) {
      finalQuality = ''
    }
    
    return {
      degree: degreeWithoutSymbol,
      accidental: displayDegreeRaw !== 'cad' ? (accidental === 'b' ? '♭' : accidental === '#' ? '♯' : accidental === 'natural' ? '♮' : '') : '',
      quality: finalQuality,
      figure: figure && figure !== '5' ? figure : '',
      isBorrowed
    }
  }


  // Trouver le marqueur précédent par rapport à la position actuelle
  const findPreviousMarker = (currentTimePos) => {
    if (!markers || markers.length === 0) return null
    // Trouver le dernier marqueur qui est avant la position actuelle
    for (let i = markers.length - 1; i >= 0; i--) {
      if (markers[i] < currentTimePos) {
        return i
      }
    }
    return null // Aucun marqueur avant la position actuelle
  }

  // Trouver le marqueur suivant par rapport à la position actuelle
  const findNextMarker = (currentTimePos) => {
    if (!markers || markers.length === 0) return null
    // Trouver le premier marqueur qui est après la position actuelle
    for (let i = 0; i < markers.length; i++) {
      if (markers[i] > currentTimePos) {
        return i
      }
    }
    return null // Aucun marqueur après la position actuelle
  }

  // Handlers pour le mode review
  const handleNextLevel = () => {
    navigate(returnTo || '/student-dashboard')
  }

  const handleBackToDashboard = () => {
    navigate(returnTo || '/student-dashboard')
  }

  function openLiveExercise(targetExerciseId, options = {}) {
    const nextExerciseId = String(targetExerciseId || '').trim()
    if (!nextExerciseId) return
    const forceCorrectionReleased = options?.forceCorrectionReleased === true
    navigate(`/play/${nextExerciseId}`, {
      state: {
        teacherPreview: isTeacherPreview,
        liveSessionId: liveSessionIdFromState || liveSessionMeta?.id || null,
        // Ne jamais hériter implicitement du statut de correction lors d'un changement d'exercice.
        liveCorrectionReleased: forceCorrectionReleased,
        returnTo: returnTo || (isTeacherPreview ? '/dashboard/live-sessions' : '/student-dashboard')
      }
    })
  }

  const handleLiveAdvanceToNextExercise = async () => {
    if (!isTeacherPreview || !liveSessionMeta?.id || !user?.uid || liveAdvanceBusy) return
    const queue = Array.isArray(liveSessionMeta.exerciseQueue) ? liveSessionMeta.exerciseQueue : []
    const currentIndex = Number(liveSessionMeta.currentExerciseIndex || 0)
    const next = queue[currentIndex + 1]
    const nextExerciseId = String(next?.exerciseId || '').trim()
    if (!nextExerciseId) return
    setLiveAdvanceBusy(true)
    try {
      await advanceLiveSessionExercise(liveSessionMeta.id, user.uid)
      setLiveSessionMeta((prev) => {
        if (!prev) return prev
        return {
          ...prev,
          currentExerciseIndex: Number(prev.currentExerciseIndex || 0) + 1,
          status: 'collecting'
        }
      })
      openLiveExercise(nextExerciseId, { forceCorrectionReleased: false })
    } catch (err) {
      setError(err?.message || 'Impossible de passer à l’exercice live suivant.')
    } finally {
      setLiveAdvanceBusy(false)
    }
  }

  const handleReviewSegmentClick = (segmentIndex) => {
    if (mode === 'review') {
      setSelectedSegmentIndex(segmentIndex)
      // Positionner la vidéo au début du segment (sécurisé : lecteur peut être indisponible)
      if (playerRef.current && chordSegments[segmentIndex]) {
        const segment = chordSegments[segmentIndex]
        try {
          playerRef.current.seekTo(segment.startTime, true)
          setCurrentTime(segment.startTime)
          currentTimeRef.current = segment.startTime
        } catch (e) {
          console.warn('Player: seekTo indisponible', e?.message)
        }
      }
    }
  }

  const isLiveProjectionTeacher = Boolean(isTeacherPreview && liveSessionIdFromState && liveSessionMeta?.id)
  const isLiveTimerEnabled = Boolean(liveSessionMeta?.timer?.enabled)
  const liveTimerRemainingSec = getTimerRemainingSeconds(liveSessionMeta?.timer, liveTimerNowMs)
  const liveIsLastExercise = liveQueue.length > 0 && liveCurrentIndex >= (liveQueue.length - 1)
  const liveRankingLabel = (liveSessionMeta?.status === 'correction_released' && liveIsLastExercise)
    ? 'Classement final'
    : 'Classement provisoire'
  const liveRankingSessionId = String(liveSessionMeta?.id || liveSessionIdFromState || '').trim()
  const liveRankingUrl = liveRankingSessionId
    ? `/dashboard/live-sessions?view=ranking&session=${encodeURIComponent(liveRankingSessionId)}`
    : '/dashboard/live-sessions?view=ranking'

  const handleLiveTimerStart = () => {
    const durationSec = Math.max(10, Number(liveSessionMeta?.timer?.durationSec || liveTimerControlSec || 120))
    updateLiveTimer({
      enabled: true,
      durationSec,
      remainingSec: durationSec,
      paused: false,
      startedAt: new Date()
    })
  }

  const handleLiveTimerPause = () => {
    const durationSec = Math.max(10, Number(liveSessionMeta?.timer?.durationSec || liveTimerControlSec || 120))
    updateLiveTimer({
      enabled: true,
      durationSec,
      remainingSec: Number.isFinite(liveTimerRemainingSec) ? liveTimerRemainingSec : durationSec,
      paused: true,
      startedAt: null
    })
  }

  const handleLiveTimerReset = () => {
    const durationSec = Math.max(10, Number(liveTimerControlSec || liveSessionMeta?.timer?.durationSec || 120))
    updateLiveTimer({
      enabled: true,
      durationSec,
      remainingSec: durationSec,
      paused: true,
      startedAt: null
    })
  }

  const handleLiveTimerHide = () => {
    const durationSec = Math.max(10, Number(liveSessionMeta?.timer?.durationSec || liveTimerControlSec || 120))
    updateLiveTimer({
      enabled: false,
      durationSec,
      remainingSec: Number.isFinite(liveTimerRemainingSec) ? liveTimerRemainingSec : durationSec,
      paused: true,
      startedAt: null
    })
  }

  const handleLiveTimerPresetClick = (sec) => {
    const durationSec = Math.max(10, Number(sec))
    setLiveTimerControlSec(durationSec)
    updateLiveTimer({
      enabled: true,
      durationSec,
      remainingSec: durationSec,
      paused: true,
      startedAt: null
    })
  }

  const handleLiveTimerDurationInputChange = (e) => {
    setLiveTimerDurationInput(e.target.value.replace(/[^\d]/g, '').slice(0, 4))
  }

  const handleLiveTimerDurationInputBlur = () => {
    const raw = liveTimerDurationInput.replace(/\D/g, '')
    if (raw === '') {
      setLiveTimerDurationInput(String(liveTimerControlSec))
      return
    }
    const n = parseInt(raw, 10)
    const clamped = Math.max(10, Math.min(900, n))
    setLiveTimerControlSec(clamped)
    setLiveTimerDurationInput(String(clamped))
  }

  // Mode Review : un seul layout pour libre et parcours (vidéo floutée, overlay, carte feedback, timeline)
  if (mode === 'review') {
    const segIdx = selectedSegmentIndex != null && chordSegments[selectedSegmentIndex] ? selectedSegmentIndex : 0
    const validation = answerValidations[segIdx]
    const userAnswer = userAnswers[segIdx]
    const markerSeg = exercise.markers[segIdx]
    const correctAnswer = pedalAwareCorrectAnswers[segIdx]
      || (markerSeg && typeof markerSeg === 'object' && markerSeg.chord ? markerSeg.chord : null)
    const codexEntriesSeg = (validation?.level === 0 || validation?.level === 0.5 || validation?.level === 2 || validation?.level === 3) && correctAnswer ? getCodexEntriesForCorrection(correctAnswer, nodeId) : []
    const codexEntrySegForCorrection = isTeacherPreview && correctAnswer ? getCodexEntriesForCorrection(correctAnswer, nodeId) : []
    const codexEntrySeg = (codexEntriesSeg.length > 0 ? codexEntriesSeg[0] : null) || (codexEntrySegForCorrection.length > 0 ? codexEntrySegForCorrection[0] : null)
    const isSegLocked = exerciseMode === 'parcours' && isSegmentLockedByParcours(segIdx)
    const playerContextLinks = exercise ? getCodexContextLinksForExercise(exercise) : []
    const showCorrectionOnly = isTeacherPreview && !userAnswer
    const liveErrorsSeg = getLiveErrorsForSegment(segIdx)
    const emotionResolved = (emotionFeedbackEnabled && correctAnswer)
      ? resolveChordEmotionWithCast(correctAnswer, emotionCast || {})
      : null
    const emotionDegreeLabel = emotionResolved?.displayAsFunction
      ? (emotionResolved.functionBorder || '—')
      : (correctAnswer ? formatChordDetailed(correctAnswer) : '—')

    const emotionUserAnswerNode = userAnswer
      ? ((userAnswer.selectedFunction != null || userAnswer.function != null) && !userAnswer.degree && !userAnswer.specialRoot && userAnswer.qcmAnswer == null
          ? userAnswerToLabel(userAnswer)
          : <ChordLabel chord={userAnswer} displayString={userAnswerToLabel(userAnswer)} />)
      : 'Non répondu'

    const emotionAnswerIsWrong = !showCorrectionOnly && validation?.level !== 1

    const emotionFilmBody = emotionResolved ? (
      <div
        className={`player-emotion-film-in-card${emotionResolved.isBorrowed ? ' player-emotion-film-in-card--borrowed' : ''}`}
      >
        <div className="player-emotion-film-hero">
          <EmotionFace emotionResolved={emotionResolved} />
          <div className="player-emotion-film-meta">
            <span className="player-emotion-film-correction-label">Correction</span>
            <div className="player-emotion-film-degree">
              {emotionResolved.displayAsFunction
                ? emotionDegreeLabel
                : (correctAnswer ? <ChordLabel chord={correctAnswer} /> : emotionDegreeLabel)}
            </div>
            {emotionResolved.label && (
              <div className="player-emotion-film-label">{emotionResolved.label}</div>
            )}
            {emotionResolved.functionBorder && (
              <span
                className={`player-emotion-film-fn player-emotion-film-fn--${emotionResolved.functionBorder.toLowerCase()}`}
              >
                {emotionResolved.functionBorder}
              </span>
            )}
          </div>
        </div>
        <div
          className={`player-emotion-film-answer${
            showCorrectionOnly || !emotionAnswerIsWrong
              ? ' player-emotion-film-answer--correct'
              : ' player-emotion-film-answer--wrong'
          }`}
          aria-hidden={showCorrectionOnly || !emotionAnswerIsWrong}
        >
          <span className="player-emotion-film-answer-label">Votre réponse</span>
          <span className="player-emotion-film-answer-value">
            {showCorrectionOnly ? '\u00A0' : emotionUserAnswerNode}
          </span>
        </div>
      </div>
    ) : null

    return (
      <div className={`player-immersive player-review-integrated${isTeacherPreview ? ' player-review-integrated--teacher-preview' : ''}`}>
        {lastSaveFeedback && !newlyUnlockedBadges.length && lastSaveFeedback.xpGained > 0 ? (
          <div className="player-save-feedback-banner" role="status">
            <span>Nouveau record !</span>
          </div>
        ) : null}
        {newlyUnlockedBadges.length > 0 && (
          <div className="player-badge-unlocked-banner" role="alert">
            <div className="player-badge-unlocked-content">
              <span className="player-badge-unlocked-title">
                {newlyUnlockedBadges.length === 1 ? 'Badge débloqué' : `${newlyUnlockedBadges.length} badges débloqués`}
              </span>
              <div className="player-badge-unlocked-list">
                {newlyUnlockedBadges.map((b) => (
                  <span key={b.id} className="player-badge-unlocked-item">
                    <span className="player-badge-unlocked-emoji">{b.emoji}</span>
                    <span>{b.name}</span>
                  </span>
                ))}
              </div>
            </div>
            <button type="button" className="player-badge-unlocked-dismiss" onClick={() => setNewlyUnlockedBadges([])} aria-label="Fermer">×</button>
          </div>
        )}
        <div className="player-video-zone player-review-integrated-video-zone">
          <div className="player-video-zone-actions">
            {videoId && (
              <a
                href={`https://www.youtube.com/watch?v=${videoId}${startTime > 0 ? `&t=${Math.round(startTime)}` : ''}`}
                target="_blank"
                rel="noopener noreferrer"
                className="player-open-full-video-btn"
                aria-label="Voir la vidéo complète sur YouTube"
                title="Voir la vidéo complète sur YouTube"
              >
                <ExternalLink className="w-4 h-4 md:w-[18px] md:h-[18px]" aria-hidden />
                <span>Vidéo complète</span>
              </a>
            )}
            <button ref={quitButtonRef} className="player-quit-btn" onClick={handleQuitExercise} aria-label="Quitter l'exercice">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
              <span>Quitter</span>
            </button>
          </div>
          {isLiveProjectionTeacher && isLiveTimerEnabled && (
            <div className={`player-live-timer-overlay${liveTimerRemainingSec === 0 ? ' player-live-timer-overlay--zero' : ''}`}>
              <span className="player-live-timer-overlay-label">TIMER</span>
              <span className="player-live-timer-overlay-value">{liveProjectionTimerLabel}</span>
            </div>
          )}
          <div className="player-video-wrapper player-review-integrated-video-blur" style={{ pointerEvents: 'none' }}>
            <YouTube videoId={videoId} opts={opts} onReady={handleReady} onStateChange={handleStateChange} className="player-youtube" />
          </div>
          <div className={`player-review-integrated-overlay${emotionFeedbackEnabled ? ' player-review-integrated-overlay--emotions' : ''}`} style={{ pointerEvents: 'auto' }}>
            <div className="player-review-integrated-score-row">
              <div className="player-review-integrated-score">
                {showCorrectionOnly
                  ? 'Aperçu correction'
                  : `${calculateScores.perfectCount + calculateScores.partialCount}/${calculateScores.totalCount} • ${calculateScores.scorePercentage}%`}
              </div>
              <button
                type="button"
                className={`player-emotion-toggle${emotionFeedbackEnabled ? ' player-emotion-toggle--on' : ''}`}
                onClick={toggleEmotionFeedback}
                aria-pressed={emotionFeedbackEnabled}
                title="Courbe émotionnelle"
              >
                <Smile className="player-emotion-toggle-icon" aria-hidden />
                <span>Émotions</span>
              </button>
            </div>
            {chordSegments[segIdx] && (
              isSegLocked && !userAnswer ? (
                <div className="player-feedback-card player-feedback-card--level-0">
                  {emotionFilmBody || (
                    <p className="player-feedback-card-message">Cet accord n&apos;était pas à remplir dans ce niveau.</p>
                  )}
                </div>
              ) : showCorrectionOnly ? (
                <div className={`player-feedback-card player-feedback-card--correction-only${emotionFilmBody ? ' player-feedback-card--emotions' : ''}`}>
                  {emotionFilmBody || (
                    <>
                  <p className="player-feedback-card-message">Correction</p>
                  <div className="player-feedback-card-comparison">
                    <div className="player-feedback-card-row">
                      <span className="player-feedback-card-label">Solution</span>
                      <span className="player-feedback-card-value">
                        {correctAnswer ? <ChordLabel chord={correctAnswer} /> : '—'}
                      </span>
                    </div>
                    {correctAnswer?.cadence && (
                      <div className="player-feedback-card-row">
                        <span className="player-feedback-card-label">Cadence</span>
                        <span className="player-feedback-card-value">
                          {cadenceLabelsMap[correctAnswer.cadence] || correctAnswer.cadence}
                        </span>
                      </div>
                    )}
                  </div>
                    </>
                  )}
                  {isTeacherPreview && liveSessionMeta?.status === 'correction_released' && liveErrorsSeg && (
                    <div className="player-live-errors-block">
                      <p className="player-live-errors-title">Erreurs fréquentes sur cet accord</p>
                      {liveErrorsSeg.wrong.length === 0 ? (
                        <p className="player-live-errors-empty">Aucune erreur fréquente relevée.</p>
                      ) : (
                        <ul className="player-live-errors-list">
                          {liveErrorsSeg.wrong.map((row) => (
                            <li key={`${segIdx}-${row.label}`} className="player-live-errors-item">
                              <span className="player-live-errors-label">{row.label}</span>
                              <span className="player-live-errors-bar-wrap">
                                <span className="player-live-errors-bar" style={{ width: `${Math.max(6, Math.round((row.count / (liveErrorsSeg.maxCount || 1)) * 100))}%` }} />
                              </span>
                              <strong className="player-live-errors-value">{row.percent}%</strong>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  {!emotionFilmBody && codexEntrySeg && (
                    <button type="button" className="player-feedback-card-codex" onClick={() => navigate(`/student-dashboard?tab=codex&fiche=${codexEntrySeg.id}`)}>
                      Revoir la fiche : {codexEntrySeg.title}
                    </button>
                  )}
                  {!emotionFilmBody && playerContextLinks.length > 0 && (
                    <div className="player-feedback-card-context">
                      <span className="player-feedback-card-context-icon" aria-hidden="true">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                        </svg>
                      </span>
                      <span className="player-feedback-card-context-label">Contexte :</span>
                      {playerContextLinks.map((link) => (
                        <button
                          key={link.type}
                          type="button"
                          className="player-feedback-card-context-link"
                          onClick={() => navigate(getCodexUrlForEntry(link.codexEntryId, link.sectionId))}
                        >
                          {link.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className={`player-feedback-card player-feedback-card--level-${validation?.level ?? 0}${emotionFilmBody ? ' player-feedback-card--emotions' : ''}`}>
                  {emotionFilmBody || (
                    <>
                  <p className="player-feedback-card-message">{validation?.feedback ?? '—'}</p>
                  <div className="player-feedback-card-comparison">
                    <div className="player-feedback-card-row">
                      <span className="player-feedback-card-label">Votre réponse</span>
                      <span className="player-feedback-card-value">
                        {userAnswer ? ((userAnswer.selectedFunction != null || userAnswer.function != null) && !userAnswer.degree && !userAnswer.specialRoot && userAnswer.qcmAnswer == null ? userAnswerToLabel(userAnswer) : <ChordLabel chord={userAnswer} displayString={userAnswerToLabel(userAnswer)} />) : 'Non répondu'}
                      </span>
                    </div>
                    <div className="player-feedback-card-row">
                      <span className="player-feedback-card-label">Solution</span>
                      <span className="player-feedback-card-value">
                        {correctAnswer ? <ChordLabel chord={correctAnswer} /> : '—'}
                      </span>
                    </div>
                  </div>
                  {correctAnswer?.cadence && userAnswer?.cadence && normalizeCadence(userAnswer.cadence) !== normalizeCadence(correctAnswer.cadence) && (
                    <p className="player-feedback-card-cadence">
                      Cadence : vous avez indiqué <strong>{cadenceLabelsMap[userAnswer.cadence] || userAnswer.cadence}</strong>, la solution est <strong>{cadenceLabelsMap[correctAnswer.cadence] || correctAnswer.cadence}</strong>.
                    </p>
                  )}
                  {(validation?.score != null) && (
                    <p className="player-feedback-card-score">
                      {validation.score} %
                    </p>
                  )}
                    </>
                  )}
                  {!emotionFilmBody && isTeacherPreview && liveSessionMeta?.status === 'correction_released' && liveErrorsSeg && (
                    <div className="player-live-errors-block">
                      <p className="player-live-errors-title">Erreurs fréquentes sur cet accord</p>
                      {liveErrorsSeg.wrong.length === 0 ? (
                        <p className="player-live-errors-empty">Aucune erreur fréquente relevée.</p>
                      ) : (
                        <ul className="player-live-errors-list">
                          {liveErrorsSeg.wrong.map((row) => (
                            <li key={`${segIdx}-${row.label}`} className="player-live-errors-item">
                              <span className="player-live-errors-label">{row.label}</span>
                              <span className="player-live-errors-bar-wrap">
                                <span className="player-live-errors-bar" style={{ width: `${Math.max(6, Math.round((row.count / (liveErrorsSeg.maxCount || 1)) * 100))}%` }} />
                              </span>
                              <strong className="player-live-errors-value">{row.percent}%</strong>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  {!emotionFilmBody && codexEntrySeg && (
                    <button type="button" className="player-feedback-card-codex" onClick={() => navigate(`/student-dashboard?tab=codex&fiche=${codexEntrySeg.id}`)}>
                      Revoir la fiche : {codexEntrySeg.title}
                    </button>
                  )}
                  {!emotionFilmBody && playerContextLinks.length > 0 && (
                    <div className="player-feedback-card-context">
                      <span className="player-feedback-card-context-icon" aria-hidden="true">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                        </svg>
                      </span>
                      <span className="player-feedback-card-context-label">Contexte :</span>
                      {playerContextLinks.map((link) => (
                        <button
                          key={link.type}
                          type="button"
                          className="player-feedback-card-context-link"
                          onClick={() => navigate(getCodexUrlForEntry(link.codexEntryId, link.sectionId))}
                        >
                          {link.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )
            )}
          </div>
            <div className={`player-timeline-overlay player-review-integrated-timeline ${getCadencesForBrace.length > 0 ? 'player-timeline-overlay-has-cadences' : ''} ${(borrowedGroups.length > 0 || pedalGroups.some(g => g.endIndex > g.startIndex)) ? 'player-timeline-overlay-has-borrowed-pedal' : ''}`}>
            <div ref={timelineViewportRef} className="player-timeline-content-viewport">
            <div className="player-timeline-content-frame" style={timelineContentFrameStyle}>
            {getCadencesForBrace.length > 0 && (
              <div className="player-timeline-cadence-braces player-timeline-cadence-braces-review">
                {getCadencesForBrace.map((cadence, idx) => {
                  if (cadence.startMarker === null || cadence.endMarker === null) return null
                  const range = getSegmentRange(cadence.startMarker, cadence.endMarker)
                  if (!range) return null
                  const braceClass = isTeacherPreview
                    ? 'player-timeline-cadence-brace player-cadence-brace-preview'
                    : (cadence.cadenceExpected && cadence.cadenceCorrect === true ? 'player-timeline-cadence-brace player-cadence-brace-correct' : cadence.cadenceExpected && cadence.cadenceCorrect === false ? 'player-timeline-cadence-brace player-cadence-brace-incorrect' : 'player-timeline-cadence-brace')
                  return (
                    <div key={idx} className={braceClass} style={{ left: `${range.leftPos}%`, width: `${range.width}%` }}>
                      <div className="player-timeline-cadence-brace-bar" />
                      <div className="player-timeline-cadence-brace-label">{cadence.cadenceLabel}</div>
                    </div>
                  )
                })}
              </div>
            )}
            {borrowedGroups.length > 0 && (
              <div className="player-timeline-borrowed-braces">
                {borrowedGroups.map((g, idx) => {
                  const range = getSegmentRange(g.startIndex, g.endIndex)
                  if (!range) return null
                  return <div key={idx} className="player-timeline-borrowed-brace" style={{ left: `${range.leftPos}%`, width: `${range.width}%` }} aria-hidden />
                })}
              </div>
            )}
            <div
              ref={timelineRef}
              className={`player-timeline-with-segments player-review-timeline${isMobile ? ' player-timeline-equal-width' : ''}`}
              style={
                !isMobile
                  ? { width: '100%' }
                  : chordSegments.length
                    ? { '--segment-count': chordSegments.length }
                    : undefined
              }
            >
              {currentTime >= startTime && currentTime <= endTime && playheadPositionPercent != null && (
                <div
                  ref={reviewPlayheadRef}
                  className={`player-timeline-playhead${!isPlaying ? ' player-timeline-playhead--paused' : ''}`}
                  style={{ left: `${playheadPositionPercent}%`, willChange: 'left' }}
                >
                  <div className="player-timeline-playhead-indicator"></div>
                </div>
              )}
              {chordSegments.map((segment) => {
                const borrowedGroup = borrowedGroups.find(g => segment.index >= g.startIndex && segment.index <= g.endIndex)
                const borrowedGroupRole = borrowedGroup == null ? null : borrowedGroup.startIndex === borrowedGroup.endIndex ? null : segment.index === borrowedGroup.startIndex ? 'start' : segment.index === borrowedGroup.endIndex ? 'end' : 'middle'
                const pedalGroup = pedalGroups.find(g => segment.index >= g.startIndex && segment.index <= g.endIndex)
                const pedalGroupRole = pedalGroup && pedalGroup.endIndex > pedalGroup.startIndex
                  ? (segment.index === pedalGroup.startIndex ? 'start' : segment.index === pedalGroup.endIndex ? 'end' : 'middle')
                  : null
                const pedalGroupDegree = pedalGroup?.degree ?? null
                const val = answerValidations[segment.index]
                const isCorrect = val && val.level === 1
                const isIncorrect = val && val.level === 0
                const isPartial = val && (val.level === 0.5 || val.level === 2 || val.level === 3)
                const isSelected = selectedSegmentIndex === segment.index
                const segLocked = isSegmentLockedByParcours(segment.index)
                let chordToDisplay = segment.chord
                const m = exercise.markers[segment.index]
                const correct = m && typeof m === 'object' && m.chord ? m.chord : null
                if (isIncorrect || isPartial) { if (correct) chordToDisplay = correct }
                else if (segLocked && correct) chordToDisplay = correct
                const chordLabel = formatChordLabel(chordToDisplay)
                const qcmText = (progressionMode === 'qcm' && chordToDisplay && ('qcmAnswer' in chordToDisplay || 'function' in chordToDisplay)) ? (chordToDisplay.qcmAnswer ?? chordToDisplay.function ?? null) : null
                const qcmTextDisplay = (qcmText && pedalGroupRole != null) ? stripTrailingPedalSlash(qcmText) : qcmText
                const showIntuition = progressionMode === 'functions'
                const tlFunc = showIntuition && chordToDisplay ? getChordFunction(chordToDisplay, segment.index) : null
                const fullText = chordToDisplay ? (chordToDisplay.degree || chordToDisplay.specialRoot ? formatChordDetailed(chordToDisplay) : (chordToDisplay.selectedFunction && PRIMARY_DEGREES[chordToDisplay.selectedFunction] ? (PRIMARY_DEGREES[chordToDisplay.selectedFunction][0] || formatChordDetailed(chordToDisplay)) : formatChordDetailed(chordToDisplay))) : ''
                const reviewFunc = isTeacherPreview && chordToDisplay ? getChordFunction(chordToDisplay, segment.index) : null
                const functionClassPreview = isTeacherPreview && reviewFunc === 'T' ? 'player-chord-segment-t' : isTeacherPreview && reviewFunc === 'SD' ? 'player-chord-segment-sd' : isTeacherPreview && reviewFunc === 'D' ? 'player-chord-segment-d' : ''
                let validationClass = ''
                if (!isTeacherPreview) {
                  if (isCorrect) validationClass = 'player-chord-segment-correct'
                  else if (isIncorrect) validationClass = 'player-chord-segment-incorrect'
                  else if (isPartial) validationClass = 'player-chord-segment-partial'
                }
                const givenNotToFill = segLocked && (chordLabel || qcmText)
                const range = getSegmentRange(segment.index, segment.index)
                if (!range) return null
                const segW = range.width
                const segL = range.leftPos
                const segmentContent = (
                  <>
                    {showIntuition && tlFunc ? (
                      <>
                        <span className={`player-chord-function ${tlFunc === 'T' ? 'player-chord-function-t' : tlFunc === 'SD' ? 'player-chord-function-sd' : 'player-chord-function-d'}`}>{tlFunc}</span>
                        {fullText && <span className="player-chord-segment-full-hint">{fullText}</span>}
                      </>
                    ) : qcmTextDisplay ? (
                      ['T', 'SD', 'D'].includes(qcmTextDisplay) ? (
                        <span className={`player-chord-function ${qcmTextDisplay === 'T' ? 'player-chord-function-t' : qcmTextDisplay === 'SD' ? 'player-chord-function-sd' : 'player-chord-function-d'}`}>{qcmTextDisplay}</span>
                      ) : (
                        <div className="player-chord-label"><ChordLabel displayString={qcmTextDisplay} hidePedalInLabel={pedalGroupRole != null} /></div>
                      )
                    ) : chordLabel ? (
                      chordLabel.isFunctionOnly ? (
                        <span className={`player-chord-function ${chordLabel.function === 'T' ? 'player-chord-function-t' : chordLabel.function === 'SD' ? 'player-chord-function-sd' : 'player-chord-function-d'}`}>{chordLabel.function}</span>
                      ) : (
                        <div className="player-chord-label"><ChordLabel chord={chordToDisplay} borrowedGroupRole={borrowedGroupRole} hidePedalInLabel={pedalGroupRole != null} /></div>
                      )
                    ) : (
                      <div className="player-chord-empty"><span className="player-chord-empty-icon">?</span></div>
                    )}
                  </>
                )
                const flipClass = isTeacherPreview ? ' player-chord-segment--flip-reveal' : ''
                const flipActiveClass = segmentFlipTransforms != null ? ' player-chord-segment--flip-flip-active' : ''
                const flipT = segmentFlipTransforms && segmentFlipTransforms[segment.index]
                const isFlipIdentity = flipT && flipT.scaleX === 1 && flipT.scaleY === 1 && flipT.translateX === 0 && flipT.translateY === 0
                const flipDelay = segment.index < 11 ? segment.index * 0.06 : 0.66
                const flipStyle = flipT
                  ? {
                      transform: `translate(${flipT.translateX}px, ${flipT.translateY}px) scale(${flipT.scaleX}, ${flipT.scaleY})`,
                      transformOrigin: '0 0',
                      transition: isFlipIdentity ? `transform 0.65s cubic-bezier(0.32, 0.72, 0.45, 1) ${flipDelay}s` : 'none'
                    }
                  : {}
                return (
                  <div
                    key={segment.index}
                    data-segment-index={segment.index}
                    className={`player-chord-segment player-review-segment ${functionClassPreview} ${validationClass} ${isSelected ? 'player-segment-selected' : ''} ${givenNotToFill ? 'player-chord-segment-given' : ''} ${showIntuition ? 'player-chord-segment-intuition' : ''}${flipClass}${flipActiveClass}`}
                    style={{ left: `${segL}%`, width: `${segW}%`, ...flipStyle }}
                    onClick={(e) => { e.stopPropagation(); handleReviewSegmentClick(segment.index); setSelectedSegmentIndex(segment.index) }}
                  >
                    {isTeacherPreview ? (
                      <div className="player-chord-segment-flip-inner">
                        <div className="player-chord-segment-flip-front" aria-hidden>
                          <span className="player-chord-segment-flip-front-icon"><Eye className="player-chord-segment-flip-front-svg" strokeWidth={2} /></span>
                        </div>
                        <div className="player-chord-segment-flip-back">
                          <div className="player-chord-segment-content">{segmentContent}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="player-chord-segment-content">{segmentContent}</div>
                    )}
                  </div>
                )
              })}
            </div>
            {/* Conteneur dédié pédale (review) : un bloc par groupe, trait à droite du label */}
            {pedalGroups.filter(g => g.endIndex > g.startIndex).length > 0 && (
              <div className="player-pedal-strip player-pedal-strip-review" aria-hidden>
                {pedalGroups.filter(g => g.endIndex > g.startIndex).map((g, idx) => {
                  const range = getSegmentRange(g.startIndex, g.endIndex)
                  if (!range) return null
                  return (
                    <div key={idx} className="player-pedal-strip-group" style={{ left: `${range.leftPos}%`, width: `${range.width}%` }} aria-label={`Pédale sur degré ${g.degree}`}>
                      <span className="player-pedal-strip-label"><span className="player-pedal-strip-label-word">Ped.</span> <span className="player-pedal-strip-label-degree">{g.degree}</span></span>
                      <span className="player-pedal-strip-line" />
                    </div>
                  )
                })}
              </div>
            )}
            </div>
            </div>
          </div>
        </div>
        <div className="player-control-zone bg-zinc-900/80 backdrop-blur-xl border-t border-white/5">
          <div className="flex items-center justify-center gap-4">
            <button onClick={() => { if (playerRef.current && exercise) { playerRef.current.seekTo(startTime, true); setCurrentTime(startTime); currentTimeRef.current = startTime; setSelectedMarkerId(null) } }} className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-zinc-800 border border-white/10 hover:border-white/20 hover:bg-zinc-700 transition-all duration-200 flex items-center justify-center shadow-lg active:scale-95" title="Revenir au début de l'extrait">
              <RotateCcw className="w-5 h-5 md:w-6 md:h-6 text-white/70" />
            </button>
            <button onClick={() => { const currentPos = currentTimeRef.current || currentTime; const prevIndex = findPreviousMarker(currentPos); if (prevIndex !== null) { handleSeekToMarker(prevIndex); setSelectedSegmentIndex(prevIndex) } else if (playerRef.current && exercise) { playerRef.current.seekTo(startTime, true); setCurrentTime(startTime); currentTimeRef.current = startTime; setSelectedSegmentIndex(0) } }} className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-zinc-800 border border-white/10 hover:border-white/20 hover:bg-zinc-700 transition-all duration-200 flex items-center justify-center shadow-lg active:scale-95" title="Marqueur précédent">
              <SkipBack className="w-5 h-5 md:w-6 md:h-6 text-white/70" />
            </button>
            <div className="flex flex-col items-center gap-2">
              <div className="px-4 py-2 rounded-xl bg-zinc-800/50 backdrop-blur-sm border border-white/10">
                <div className="text-lg md:text-xl font-mono text-white font-bold text-center">{formatRelativeTime(currentTime)}</div>
              </div>
            {!shouldMuteForLiveStudent && (
              <button onClick={handlePlayPause} className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 border-4 border-white/10 shadow-2xl shadow-indigo-500/40 hover:shadow-indigo-500/60 transition-all duration-200 flex items-center justify-center active:scale-95" title="Play/Pause (Espace)" aria-label={isPlaying ? 'Pause' : 'Lecture'}>
                {isPlaying ? <Pause className="w-8 h-8 md:w-10 md:h-10 text-white" /> : <Play className="w-8 h-8 md:w-10 md:h-10 text-white ml-1" />}
              </button>
            )}
            </div>
            <button onClick={() => { const currentPos = currentTimeRef.current || currentTime; const nextIndex = findNextMarker(currentPos); if (nextIndex !== null) { handleSeekToMarker(nextIndex); setSelectedSegmentIndex(nextIndex) } else if (markers.length > 0) { handleSeekToMarker(0); setSelectedSegmentIndex(0) } else if (playerRef.current && exercise) { playerRef.current.seekTo(endTime, true); setCurrentTime(endTime); currentTimeRef.current = endTime } }} className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-zinc-800 border border-white/10 hover:border-white/20 hover:bg-zinc-700 transition-all duration-200 flex items-center justify-center shadow-lg active:scale-95" title="Marqueur suivant">
              <SkipForward className="w-5 h-5 md:w-6 md:h-6 text-white/70" />
            </button>
            {!shouldMuteForLiveStudent && (
              <button onClick={handleReplay} className="player-review-integrated-btn-replay rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold shadow-lg shadow-indigo-500/40 hover:shadow-indigo-500/60 transition-all duration-200 active:scale-95 flex items-center justify-center" aria-label="Rejouer l'exercice" title="Rejouer l'exercice">
                {isMobile ? <RotateCcw className="w-6 h-6" /> : 'Rejouer'}
              </button>
            )}
            {isTeacherPreview && isViewingCurrentLiveExercise && liveHasNextExercise && liveSessionMeta?.status === 'correction_released' && (
              <button
                type="button"
                onClick={handleLiveAdvanceToNextExercise}
                disabled={liveAdvanceBusy}
                className="player-review-integrated-btn-next rounded-xl bg-emerald-700/90 border border-emerald-300/30 hover:bg-emerald-600 text-white font-semibold transition-all duration-200 active:scale-95 flex items-center justify-center"
                aria-label="Passer à l'exercice live suivant"
                title="Passer à l'exercice live suivant"
              >
                {liveAdvanceBusy ? '...' : (isMobile ? <SkipForward className="w-6 h-6" /> : 'Live suivant')}
              </button>
            )}
            {!isTeacherPreview && liveSessionMovedToAnotherExercise && (
              <button
                type="button"
                onClick={() => openLiveExercise(liveCurrentExerciseId)}
                className="player-review-integrated-btn-next rounded-xl bg-amber-700/90 border border-amber-200/30 hover:bg-amber-600 text-white font-semibold transition-all duration-200 active:scale-95 flex items-center justify-center"
                aria-label="Rejoindre l'exercice live en cours"
                title="Le professeur est passé à l'exercice suivant"
              >
                {isMobile ? <SkipForward className="w-6 h-6" /> : 'Rejoindre live'}
              </button>
            )}
            {exerciseMode === 'parcours' && (
              <button onClick={handleNextLevel} className="player-review-integrated-btn-next rounded-xl bg-zinc-800 border border-white/10 hover:border-white/20 hover:bg-zinc-700 text-white font-semibold transition-all duration-200 active:scale-95 flex items-center justify-center" aria-label="Niveau suivant" title="Niveau suivant">
                {isMobile ? <SkipForward className="w-6 h-6" /> : 'Niveau suivant'}
              </button>
            )}
            <button onClick={handleBackToDashboard} className="player-review-integrated-btn-back rounded-xl bg-zinc-800 border border-white/10 hover:border-white/20 hover:bg-zinc-700 text-white font-semibold transition-all duration-200 active:scale-95 flex items-center justify-center" aria-label="Retour au tableau de bord" title="Retour au tableau de bord">
              {isMobile ? <Home className="w-6 h-6" /> : 'Retour Dashboard'}
            </button>
            {isLiveProjectionTeacher && (
              <div className="player-live-timer-controls player-live-timer-controls--inline">
              <div className="player-live-timer-controls-row">
                <button type="button" onClick={handleLiveTimerStart} disabled={liveTimerBusy}>Start</button>
                <button type="button" onClick={handleLiveTimerPause} disabled={liveTimerBusy || !isLiveTimerEnabled}>Pause</button>
                <button type="button" onClick={handleLiveTimerReset} disabled={liveTimerBusy || !isLiveTimerEnabled}>Reset</button>
                <button type="button" onClick={handleLiveTimerHide} disabled={liveTimerBusy || !isLiveTimerEnabled}>Masquer</button>
              </div>
              <div className="player-live-timer-controls-row">
                {[30, 60, 120, 180].map((sec) => (
                  <button key={sec} type="button" className={liveTimerControlSec === sec ? 'active' : ''} onClick={() => handleLiveTimerPresetClick(sec)} disabled={liveTimerBusy}>
                    {sec >= 60 ? `${Math.round(sec / 60)}m` : `${sec}s`}
                  </button>
                ))}
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="player-live-timer-duration-input"
                  aria-label="Durée du timer en secondes"
                  value={liveTimerDurationInput}
                  onChange={handleLiveTimerDurationInputChange}
                  onBlur={handleLiveTimerDurationInputBlur}
                />
              </div>
              </div>
            )}
          </div>
        </div>
        <ConfirmExitModal
          isOpen={isQuitConfirmOpen}
          onClose={handleCancelQuitExercise}
          onConfirm={handleConfirmQuitExercise}
          title="⚠️ Quitter l'exercice"
          message="Vous allez quitter l'exercice en cours."
          warning="Votre progression de cette tentative ne sera pas sauvegardée."
          confirmLabel="Quitter"
        />
      </div>
    )
  }

  if (mode === 'live_waiting') {
    const waitingReleased = liveCorrectionReleased || liveSessionMeta?.status === 'correction_released'
    const waitingMovedToAnotherExercise = liveSessionMovedToAnotherExercise
    return (
      <div className="player-immersive player-live-waiting">
        <div className="player-live-waiting-card">
          <h2>Reponses envoyees</h2>
          {waitingMovedToAnotherExercise ? (
            <p>Le professeur a ouvert un autre exercice de la session live.</p>
          ) : waitingReleased ? (
            <p>La correction vient d'etre publiee. Ouverture en cours…</p>
          ) : (
            <p>Attends la validation du professeur. La correction s'affichera automatiquement ici.</p>
          )}
          {waitingMovedToAnotherExercise && (
            <button type="button" className="player-live-waiting-btn" onClick={() => openLiveExercise(liveCurrentExerciseId)}>
              Rejoindre l'exercice en cours
            </button>
          )}
          <button type="button" className="player-live-waiting-btn" onClick={() => navigate(returnTo || '/student-dashboard')}>
            Retour dashboard
          </button>
        </div>
      </div>
    )
  }

  // Mode Exercise : Interface normale
  return (
    <div className="player-immersive">
      {/* Zone Vidéo (Haut - 80-85%) avec Timeline superposée */}
      <div className="player-video-zone">
        {isLiveProjectionTeacher && isLiveTimerEnabled && (
          <div className={`player-live-timer-overlay${liveTimerRemainingSec === 0 ? ' player-live-timer-overlay--zero' : ''}`}>
            <span className="player-live-timer-overlay-label">TIMER</span>
            <span className="player-live-timer-overlay-value">{liveProjectionTimerLabel}</span>
          </div>
        )}
        <div className="player-video-zone-actions">
          {videoId && !isAnswerPanelOpen && (
          <a
            href={`https://www.youtube.com/watch?v=${videoId}${startTime > 0 ? `&t=${Math.round(startTime)}` : ''}`}
            target="_blank"
            rel="noopener noreferrer"
            className="player-open-full-video-btn"
            onClick={dismissOnboarding}
            aria-label="Voir la vidéo complète sur YouTube"
            title="Voir la vidéo complète sur YouTube"
          >
            <ExternalLink className="w-4 h-4 md:w-[18px] md:h-[18px]" aria-hidden />
            <span>Vidéo complète</span>
          </a>
          )}
          {!isAnswerPanelOpen && (
          <button
            ref={quitButtonRef}
            className="player-quit-btn"
            onClick={handleQuitExercise}
            aria-label="Quitter l'exercice"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
            <span>Quitter</span>
          </button>
          )}
        </div>
        <div className="player-video-wrapper">
            <YouTube
              videoId={videoId}
              opts={opts}
              onReady={handleReady}
              onStateChange={handleStateChange}
              className="player-youtube"
            />
          </div>
        {isAnswerPanelOpen && (
          <div
            className="player-video-dim"
            onClick={closeAnswerPanel}
            aria-hidden
          />
        )}
        {!isAnswerPanelOpen && (
        <div
          ref={videoZoneOverlayRef}
          className="player-video-tap-overlay"
          onClick={handleVideoZoneTap}
          aria-hidden
        />
        )}
        {isAnswerPanelOpen && (
          <div className={`player-answer-overlay${progressionMode === 'full' ? ' player-answer-overlay--full' : ''}`} role="dialog" aria-modal="true" aria-label="Panneau de réponse">
            <div
              className={`player-answer-panel${progressionMode === 'full' ? ' player-answer-panel--full' : ''}`}
              role="region"
              onClick={(e) => e.stopPropagation()}
            >
            <ChordSelectorModal
              isOpen={isAnswerPanelOpen}
              onClose={handleModalClose}
              onValidate={handleAnswerChange}
              onResetAnswer={handleResetAnswer}
              initialChord={userAnswers[currentMarkerIndex] || null}
              studentMode={true}
              progressionMode={progressionMode}
              parcoursContext={parcoursContext}
              expectedCadence={expectedCadence}
              currentQuestion={currentMarkerIndex + 1}
              totalQuestions={totalMarkers}
              presentation="inline"
              autoSave={true}
              hideValidateButton={true}
              hidePreview={true}
              qcmCorrectChord={progressionMode === 'qcm' && exercise?.markers?.[currentMarkerIndex] && typeof exercise.markers[currentMarkerIndex] === 'object' && exercise.markers[currentMarkerIndex].chord ? exercise.markers[currentMarkerIndex].chord : null}
              qcmAllChords={progressionMode === 'qcm' ? qcmAllChords : null}
              qcmQuestionId={currentMarkerIndex}
              nodeProgress={progressionMode === 'qcm' ? nodeStatsForQCM : null}
            />
            </div>
          </div>
        )}
      </div>

      {/* Section timeline + pédale : entre vidéo et contrôles, jamais recouverte par la vidéo */}
      <div className={`player-timeline-section ${getCadencesForBrace.length > 0 ? 'player-timeline-section-has-cadences' : ''} ${pedalGroups.some(g => g.endIndex > g.startIndex) ? 'player-timeline-section-has-pedal' : ''}`}>
        {showOnboarding && (
          <div className="player-onboarding-coachmark" role="status" aria-live="polite">
            <span className="player-onboarding-coachmark-dot" aria-hidden />
            <span className="player-onboarding-coachmark-label">Astuce</span>
            <p>Les <strong>cases</strong> : clique pour répondre. La <strong>barre</strong> en dessous : glisse pour te positionner dans l&apos;extrait.</p>
            <button
              type="button"
              className="player-onboarding-coachmark-close"
              onClick={dismissOnboarding}
              aria-label="Masquer l'aide"
            >
              ×
            </button>
          </div>
        )}
        <div ref={timelineOverlayRef} className="player-timeline-overlay">
          <div ref={timelineViewportRef} className="player-timeline-content-viewport">
          <div className="player-timeline-content-frame" style={timelineContentFrameStyle}>
          {/* Accolades de cadences au-dessus de la timeline (comme en mode édition prof) */}
          {getCadencesForBrace.length > 0 && (
            <div className="player-timeline-cadence-braces">
              {getCadencesForBrace.map((cadence, idx) => {
                if (cadence.startMarker === null || cadence.endMarker === null) return null
                const range = getSegmentRange(cadence.startMarker, cadence.endMarker)
                if (!range) return null
                return (
                  <div
                    key={idx}
                    className="player-timeline-cadence-brace"
                    style={{ left: `${range.leftPos}%`, width: `${range.width}%` }}
                  >
                    <div className="player-timeline-cadence-brace-bar" />
                    <div className="player-timeline-cadence-brace-label">
                      {cadence.cadenceLabel}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          <div
            ref={timelineRef}
            className={`player-timeline-stack${pedalGroups.some(g => g.endIndex > g.startIndex) ? ' player-timeline-stack-has-pedal' : ''}${isMobile ? ' player-timeline-equal-width' : ''}${isPlaying ? ' player-timeline-stack--playing' : ''}`}
            style={{
              ...(!isMobile
                ? { width: '100%' }
                : chordSegments.length
                  ? { '--segment-count': chordSegments.length }
                  : {}),
              '--playhead-percent': playheadPositionPercent != null ? `${playheadPositionPercent}%` : '0%',
            }}
            onPointerDown={!isMobile ? handleTimelineScrubPointerDown : undefined}
            onPointerMove={!isMobile ? handleTimelineScrubPointerMove : undefined}
            onPointerUp={!isMobile ? handleTimelineScrubPointerUp : undefined}
            onPointerCancel={!isMobile ? handleTimelineScrubPointerCancel : undefined}
          >
            <div className="player-timeline-zone-labels" aria-hidden="true">
              <span className="player-timeline-zone-label">Accords</span>
              <span className="player-timeline-zone-label">Position</span>
            </div>
            <div className="player-timeline-track">
            {/* Curseur de lecture — même référentiel % que cases et rail */}
            {currentTime >= startTime && currentTime <= endTime && playheadPositionPercent != null && (
              <div
                ref={playheadRef}
                className={`player-timeline-playhead${!isPlaying ? ' player-timeline-playhead--paused' : ''}`}
                style={{
                  left: `${playheadPositionPercent}%`,
                  willChange: 'left'
                }}
              >
                <div className="player-timeline-playhead-indicator"></div>
              </div>
            )}

            <div className="timeline-segments-row">
            <div className="player-timeline-segments-container">
            {/* Cases d'accords */}
            {chordSegments.map((segment) => {
              const borrowedGroup = borrowedGroups.find(g => segment.index >= g.startIndex && segment.index <= g.endIndex)
              const borrowedGroupRole = borrowedGroup == null ? null : borrowedGroup.startIndex === borrowedGroup.endIndex ? null : segment.index === borrowedGroup.startIndex ? 'start' : segment.index === borrowedGroup.endIndex ? 'end' : 'middle'
              const pedalGroup = pedalGroups.find(g => segment.index >= g.startIndex && segment.index <= g.endIndex)
              const pedalGroupRole = pedalGroup && pedalGroup.endIndex > pedalGroup.startIndex
                ? (segment.index === pedalGroup.startIndex ? 'start' : segment.index === pedalGroup.endIndex ? 'end' : 'middle')
                : null
              const pedalGroupDegree = pedalGroup?.degree ?? null
              const chordLabel = formatChordLabel(segment.chord)
              const qcmAnswerText = (progressionMode === 'qcm' && segment.chord && ('qcmAnswer' in segment.chord || 'function' in segment.chord))
                ? (segment.chord.qcmAnswer ?? segment.chord.function ?? null)
                : null
              const qcmAnswerTextDisplay = (qcmAnswerText && pedalGroupRole != null) ? stripTrailingPedalSlash(qcmAnswerText) : qcmAnswerText
              const chordFunction = getChordFunction(segment.chord, segment.index)
              const isNext = nextMarkerIndex === segment.index
              const isLocked = isSegmentLockedByParcours(segment.index)
              
              // Obtenir la validation et la solution correcte si on est en mode validation
              const validation = isValidating || animatedChords.has(segment.index) ? answerValidations[segment.index] : null
              const isCorrect = validation && validation.level === 1
              const isIncorrect = validation && validation.level === 0
              const isAnimated = animatedChords.has(segment.index)
              
              // Obtenir la solution correcte (scope visible pour tout le segment, dont ChordLabel plus bas)
              let correctAnswerLabel = null
              let correctAnswerForDisplay = null
              if (isValidating || isAnimated) {
                const marker = exercise.markers[segment.index]
                const correctAnswer = typeof marker === 'object' && marker.chord ? marker.chord : null
                correctAnswerForDisplay = correctAnswer
                if (correctAnswer) {
                  correctAnswerLabel = formatChordLabel(correctAnswer)
                }
              }
              
              // Déterminer les classes de couleur selon la fonction et le résultat
              let functionColorClasses = ''
              let validationClass = ''
              
              if (isAnimated) {
                if (isCorrect) {
                  validationClass = 'player-chord-segment-correct'
                } else if (isIncorrect) {
                  validationClass = 'player-chord-segment-incorrect'
                } else if (validation && (validation.level === 0.5 || validation.level === 2 || validation.level === 3)) {
                  validationClass = 'player-chord-segment-partial'
                }
              }
              if (chordFunction === 'T') {
                functionColorClasses = segment.isAnswered 
                  ? 'player-chord-segment-answered player-chord-segment-t' 
                  : 'player-chord-segment-t'
              } else if (chordFunction === 'SD') {
                functionColorClasses = segment.isAnswered 
                  ? 'player-chord-segment-answered player-chord-segment-sd' 
                  : 'player-chord-segment-sd'
              } else if (chordFunction === 'D') {
                functionColorClasses = segment.isAnswered 
                  ? 'player-chord-segment-answered player-chord-segment-d' 
                  : 'player-chord-segment-d'
              } else {
                functionColorClasses = (segment.isAnswered || qcmAnswerText)
                  ? 'player-chord-segment-answered'
                  : 'player-chord-segment-empty'
              }

              // Calculer la progression dans cette case
              const segmentRelativeStart = segment.startTime - startTime
              const segmentRelativeEnd = segment.endTime - startTime
              const segmentDuration = segmentRelativeEnd - segmentRelativeStart
              const progressInSegment = currentTime >= segment.startTime && currentTime <= segment.endTime
                ? ((currentTime - segment.startTime) / segmentDuration) * 100
                : currentTime > segment.endTime
                ? 100
                : 0

              const range = getSegmentRange(segment.index, segment.index)
              if (!range) return null
              const segmentStyle = { left: `${range.leftPos}%`, width: `${range.width}%` }
              return (
                <div
                  key={segment.index}
                  data-segment-index={segment.index}
                  className={`player-chord-segment ${functionColorClasses} ${validationClass} ${isNext && !segment.isAnswered && !isValidating ? 'player-chord-segment-next' : ''} ${isAnswerPanelOpen && currentMarkerIndex === segment.index ? 'player-chord-segment-active' : ''} ${isAnimated ? 'player-chord-segment-animated' : ''} ${isLocked ? 'player-chord-segment-locked' : ''} ${segment.chord?.isBorrowed ? 'player-chord-segment-borrowed' : ''}`}
                  style={segmentStyle}
                  onClick={(e) => {
                    const isLiveStudentCorrectionLocked = Boolean(
                      liveSessionIdFromState
                      && !isTeacherPreview
                      && liveSessionMeta?.status === 'correction_released'
                    )
                    if (!isValidating && !isLocked && !isLiveStudentCorrectionLocked) {
                      e.stopPropagation()
                      dismissOnboarding()
                      setSegmentTooltipIndex(null)
                      handleSegmentActivate(segment.index)
                    }
                  }}
                >
                  <div className="player-chord-segment-body">
                  {/* Jauge de progression à l'intérieur de la case */}
                  {isPlaying && !isValidating && (
                    <div
                      className="player-chord-segment-progress"
                      data-segment-progress={segment.index}
                      style={{ width: `${progressInSegment}%` }}
                    />
                  )}

                  {/* Solution correcte au-dessus (si incorrect) */}
                  {isAnimated && isIncorrect && correctAnswerLabel && (
                    <div className="player-chord-solution-overlay">
                      <div className="player-chord-solution-label">
                        {correctAnswerLabel.isFunctionOnly ? (
                          <>
                            <span className={`player-chord-function ${
                              correctAnswerLabel.function === 'T' ? 'player-chord-function-t' :
                              correctAnswerLabel.function === 'SD' ? 'player-chord-function-sd' :
                              'player-chord-function-d'
                            }`}>
                              {correctAnswerLabel.function}
                            </span>
                            {(() => {
                              const marker = exercise.markers[segment.index]
                              const correctAnswer = typeof marker === 'object' && marker.chord ? marker.chord : null
                              const principalDegree = correctAnswer?.selectedFunction && PRIMARY_DEGREES[correctAnswer.selectedFunction] ? PRIMARY_DEGREES[correctAnswer.selectedFunction][0] : null
                              return principalDegree ? <span className="player-chord-segment-full-hint"> ({principalDegree})</span> : null
                            })()}
                          </>
                        ) : (
                          <div className="player-chord-label">
                            <ChordLabel chord={correctAnswerForDisplay} borrowedGroupRole={borrowedGroupRole} hidePedalInLabel={pedalGroupRole != null} className={correctAnswerForDisplay?.isBorrowed ? 'chord-label-with-parens' : ''} />
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Contenu de l'accord */}
                  <div className="player-chord-segment-content">
                    {qcmAnswerTextDisplay ? (
                      ['T', 'SD', 'D'].includes(qcmAnswerTextDisplay) ? (
                        <span className={`player-chord-function ${
                          qcmAnswerTextDisplay === 'T' ? 'player-chord-function-t' :
                          qcmAnswerTextDisplay === 'SD' ? 'player-chord-function-sd' :
                          'player-chord-function-d'
                        }`}>
                          {qcmAnswerTextDisplay}
                        </span>
                      ) : (
                        <div className="player-chord-label">
                          <ChordLabel displayString={qcmAnswerTextDisplay} hidePedalInLabel={pedalGroupRole != null} />
                        </div>
                      )
                    ) : chordLabel ? (
                      <>
                        {chordLabel.isFunctionOnly ? (
                          <span className={`player-chord-function ${
                            chordLabel.function === 'T' ? 'player-chord-function-t' :
                            chordLabel.function === 'SD' ? 'player-chord-function-sd' :
                            'player-chord-function-d'
                          }`}>
                            {chordLabel.function}
                          </span>
                        ) : (
                          <div className="player-chord-label">
                            <ChordLabel chord={segment.chord} borrowedGroupRole={segment.chord != null ? borrowedGroupRole : null} hidePedalInLabel={pedalGroupRole != null} className={segment.chord?.isBorrowed ? 'chord-label-with-parens' : ''} />
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="player-chord-empty">
                        <span className="player-chord-empty-icon">?</span>
                      </div>
                    )}
                  </div>
                </div>
                </div>
              )
            })}
            </div>
            </div>

            <div
              className="timeline-scrub-rail"
              role="slider"
              aria-label="Position dans l'extrait"
              aria-valuemin={startTime}
              aria-valuemax={endTime}
              aria-valuenow={currentTime}
              onPointerDown={isMobile ? handleTimelineScrubPointerDown : undefined}
              onPointerMove={isMobile ? handleTimelineScrubPointerMove : undefined}
              onPointerUp={isMobile ? handleTimelineScrubPointerUp : undefined}
              onPointerCancel={isMobile ? handleTimelineScrubPointerCancel : undefined}
            >
            </div>
            </div>
          </div>

          {/* Conteneur dédié pédale : un bloc par groupe, trait à droite du label */}
          {pedalGroups.filter(g => g.endIndex > g.startIndex).length > 0 && (
            <div className="player-pedal-strip" aria-hidden>
              {pedalGroups.filter(g => g.endIndex > g.startIndex).map((g, idx) => {
                const range = getSegmentRange(g.startIndex, g.endIndex)
                if (!range) return null
                return (
                  <div key={idx} className="player-pedal-strip-group" style={{ left: `${range.leftPos}%`, width: `${range.width}%` }} aria-label={`Pédale sur degré ${g.degree}`}>
                    <span className="player-pedal-strip-label"><span className="player-pedal-strip-label-word">Ped.</span> <span className="player-pedal-strip-label-degree">{g.degree}</span></span>
                    <span className="player-pedal-strip-line" />
                  </div>
                )
              })}
            </div>
          )}
          </div>
          </div>
        </div>
      </div>

      {/* Zone de Contrôle (Bas - 15-20%) */}
      <div className="player-control-zone bg-zinc-900/80 backdrop-blur-xl border-t border-white/5">
        <div className="player-control-layout">
        <div className="player-control-primary flex items-center justify-center gap-4">
          {/* Bouton Revenir au début */}
          <button
            onClick={() => {
              if (playerRef.current && exercise) {
                playerRef.current.seekTo(startTime, true)
                setCurrentTime(startTime)
                currentTimeRef.current = startTime
                setSelectedMarkerId(null)
              }
            }}
            className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-zinc-800 border border-white/10 hover:border-white/20 hover:bg-zinc-700 transition-all duration-200 flex items-center justify-center shadow-lg active:scale-95"
            title="Revenir au début de l'extrait"
          >
            <RotateCcw className="w-5 h-5 md:w-6 md:h-6 text-white/70" />
          </button>

          {/* Bouton Prev */}
          <button
            onClick={() => {
              const currentPos = currentTimeRef.current || currentTime
              const prevIndex = findPreviousMarker(currentPos)
              
              if (prevIndex !== null) {
                // Aller au marqueur précédent
                handleSeekToMarker(prevIndex)
              } else {
                // Si pas de marqueur précédent, revenir au début de l'extrait
                if (playerRef.current && exercise) {
                  playerRef.current.seekTo(startTime, true)
                  setCurrentTime(startTime)
                  currentTimeRef.current = startTime
                  setSelectedMarkerId(null)
                }
              }
            }}
            className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-zinc-800 border border-white/10 hover:border-white/20 hover:bg-zinc-700 transition-all duration-200 flex items-center justify-center shadow-lg active:scale-95"
            title="Marqueur précédent"
          >
            <SkipBack className="w-5 h-5 md:w-6 md:h-6 text-white/70" />
          </button>

          {/* Timer général + Bouton PLAY Héros */}
          <div className="flex flex-col items-center gap-2">
            {/* Timer général */}
            <div className="px-4 py-2 rounded-xl bg-zinc-800/50 backdrop-blur-sm border border-white/10">
              <div className="text-lg md:text-xl font-mono text-white font-bold text-center">
                {formatRelativeTime(currentTime)}
              </div>
            </div>
            {/* Bouton PLAY Héros */}
            {!shouldMuteForLiveStudent && (
              <button
                onClick={handlePlayPause}
                className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 border-4 border-white/10 shadow-2xl shadow-indigo-500/40 hover:shadow-indigo-500/60 transition-all duration-200 flex items-center justify-center active:scale-95"
                title="Play/Pause (Espace)"
                aria-label={isPlaying ? 'Pause' : 'Lecture'}
              >
                {isPlaying ? (
                  <Pause className="w-8 h-8 md:w-10 md:h-10 text-white" />
                ) : (
                  <Play className="w-8 h-8 md:w-10 md:h-10 text-white ml-1" />
                )}
              </button>
            )}
          </div>

          {/* Bouton Next */}
          <button
            onClick={() => {
              const currentPos = currentTimeRef.current || currentTime
              const nextIndex = findNextMarker(currentPos)
              
              if (nextIndex !== null) {
                // Aller au marqueur suivant
                handleSeekToMarker(nextIndex)
              } else if (markers.length > 0) {
                // Si pas de marqueur suivant mais qu'il y a des marqueurs, aller au premier
                handleSeekToMarker(0)
              } else {
                // Si pas de marqueurs, aller à la fin
                if (playerRef.current && exercise) {
                  playerRef.current.seekTo(endTime, true)
                  setCurrentTime(endTime)
                  currentTimeRef.current = endTime
                }
              }
            }}
            className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-zinc-800 border border-white/10 hover:border-white/20 hover:bg-zinc-700 transition-all duration-200 flex items-center justify-center shadow-lg active:scale-95"
            title="Marqueur suivant"
          >
            <SkipForward className="w-5 h-5 md:w-6 md:h-6 text-white/70" />
          </button>
        </div>

        <div className="player-control-secondary flex items-center justify-center gap-3 flex-wrap">
          {/* Bouton pour ouvrir la modale de l'accord actuel (masqué si panneau déjà ouvert) */}
          {findCurrentChordSegment && !isAnswerPanelOpen && (
            <button
              onClick={handleOpenCurrentChordModal}
              disabled={isSegmentLockedByParcours(findCurrentChordSegment.index) || (liveSessionIdFromState && !isTeacherPreview && liveSessionMeta?.status === 'correction_released')}
              className={`w-12 h-12 md:w-14 md:h-14 rounded-xl border border-white/10 transition-all duration-200 flex items-center justify-center active:scale-95 ${(isSegmentLockedByParcours(findCurrentChordSegment.index) || (liveSessionIdFromState && !isTeacherPreview && liveSessionMeta?.status === 'correction_released')) ? 'bg-zinc-700 cursor-not-allowed opacity-60' : 'bg-gradient-to-br from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 hover:border-white/20 shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50'}`}
              title={(liveSessionIdFromState && !isTeacherPreview && liveSessionMeta?.status === 'correction_released') ? 'Correction en cours - saisie verrouillée' : (isSegmentLockedByParcours(findCurrentChordSegment.index) ? "Accord non débloqué dans ce parcours" : "Remplir l'accord actuel (Entrée)")}
              aria-label={(liveSessionIdFromState && !isTeacherPreview && liveSessionMeta?.status === 'correction_released') ? 'Saisie verrouillée en correction live' : (isSegmentLockedByParcours(findCurrentChordSegment.index) ? "Accord non débloqué" : "Ouvrir le sélecteur d'accord pour l'accord actuel")}
            >
              <Pencil className={`w-5 h-5 md:w-6 md:h-6 ${isSegmentLockedByParcours(findCurrentChordSegment.index) ? 'text-zinc-400' : 'text-white'}`} />
            </button>
          )}

          {/* Bouton Terminer / Voir la correction (aperçu prof) */}
          {canShowFinishButton && (
            <button
              className="px-4 py-2 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold shadow-lg shadow-indigo-500/40 hover:shadow-indigo-500/60 transition-all duration-200 active:scale-95"
              onClick={handleFinishExercise}
              aria-label={isTeacherPreview ? 'Afficher la correction' : "Terminer l'exercice"}
            >
              {isTeacherPreview ? 'Voir la correction' : 'Terminer'}
            </button>
          )}
          {isLiveProjectionTeacher && (
            <div className="player-live-controls-stack">
              <button
                type="button"
                className="player-live-ranking-compact-btn"
                onClick={() => window.open(`#${liveRankingUrl}`, '_blank', 'noopener,noreferrer')}
                title={`Voir ${liveRankingLabel}`}
                aria-label={`Voir ${liveRankingLabel}`}
              >
                {liveRankingLabel}
              </button>
              <div className="player-live-timer-controls player-live-timer-controls--inline">
                <div className="player-live-timer-controls-row">
                  <button type="button" onClick={handleLiveTimerStart} disabled={liveTimerBusy}>Start</button>
                  <button type="button" onClick={handleLiveTimerPause} disabled={liveTimerBusy || !isLiveTimerEnabled}>Pause</button>
                  <button type="button" onClick={handleLiveTimerReset} disabled={liveTimerBusy || !isLiveTimerEnabled}>Reset</button>
                  <button type="button" onClick={handleLiveTimerHide} disabled={liveTimerBusy || !isLiveTimerEnabled}>Masquer</button>
                </div>
                  <div className="player-live-timer-controls-row">
                  {[30, 60, 120, 180].map((sec) => (
                    <button key={sec} type="button" className={liveTimerControlSec === sec ? 'active' : ''} onClick={() => handleLiveTimerPresetClick(sec)} disabled={liveTimerBusy}>
                      {sec >= 60 ? `${Math.round(sec / 60)}m` : `${sec}s`}
                    </button>
                  ))}
                  <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="player-live-timer-duration-input"
                  aria-label="Durée du timer en secondes"
                  value={liveTimerDurationInput}
                  onChange={handleLiveTimerDurationInputChange}
                  onBlur={handleLiveTimerDurationInputBlur}
                />
                </div>
            </div>
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Tooltip au tap sur un segment : même carte feedback que mode libre (message, comparaison, cadence, score, codex) */}
      {segmentTooltipIndex !== null && chordSegments[segmentTooltipIndex] && exercise?.markers?.[segmentTooltipIndex] && (() => {
        const validation = answerValidations[segmentTooltipIndex]
        const marker = exercise.markers[segmentTooltipIndex]
        const correctChord = typeof marker === 'object' && marker.chord ? marker.chord : null
        const userAnswerSeg = userAnswers[segmentTooltipIndex]
        if (!validation) return null
        const codexEntriesSeg = (validation.level === 0 || validation.level === 0.5 || validation.level === 2 || validation.level === 3) && correctChord ? getCodexEntriesForCorrection(correctChord, nodeId) : []
        const codexEntrySeg = codexEntriesSeg.length > 0 ? codexEntriesSeg[0] : null
        const tooltipContextLinks = exercise ? getCodexContextLinksForExercise(exercise) : []
        return (
          <div className="player-segment-tooltip" role="tooltip" aria-live="polite">
            <div className={`player-feedback-card player-feedback-card--level-${validation.level ?? 0}`}>
              <p className="player-feedback-card-message">{validation.feedback ?? '—'}</p>
              <div className="player-feedback-card-comparison">
                <div className="player-feedback-card-row">
                  <span className="player-feedback-card-label">Votre réponse</span>
                  <span className="player-feedback-card-value">
                    {userAnswerSeg ? ((userAnswerSeg.selectedFunction != null || userAnswerSeg.function != null) && !userAnswerSeg.degree && !userAnswerSeg.specialRoot && userAnswerSeg.qcmAnswer == null ? userAnswerToLabel(userAnswerSeg) : <ChordLabel chord={userAnswerSeg} displayString={userAnswerToLabel(userAnswerSeg)} />) : 'Non répondu'}
                  </span>
                </div>
                <div className="player-feedback-card-row">
                  <span className="player-feedback-card-label">Solution</span>
                  <span className="player-feedback-card-value">
                    {correctChord ? <ChordLabel chord={correctChord} /> : '—'}
                  </span>
                </div>
              </div>
              {correctChord?.cadence && userAnswerSeg?.cadence && normalizeCadence(userAnswerSeg.cadence) !== normalizeCadence(correctChord.cadence) && (
                <p className="player-feedback-card-cadence">
                  Cadence : vous avez indiqué <strong>{cadenceLabelsMap[userAnswerSeg.cadence] || userAnswerSeg.cadence}</strong>, la solution est <strong>{cadenceLabelsMap[correctChord.cadence] || correctChord.cadence}</strong>.
                </p>
              )}
              {(validation.score != null) && (
                <p className="player-feedback-card-score">
                  {validation.score} %
                </p>
              )}
              {codexEntrySeg && (
                <button type="button" className="player-feedback-card-codex" onClick={() => navigate(`/student-dashboard?tab=codex&fiche=${codexEntrySeg.id}`)}>
                  Revoir la fiche : {codexEntrySeg.title}
                </button>
              )}
              {tooltipContextLinks.length > 0 && (
                <div className="player-feedback-card-context">
                  <span className="player-feedback-card-context-icon" aria-hidden="true">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                    </svg>
                  </span>
                  <span className="player-feedback-card-context-label">Contexte :</span>
                  {tooltipContextLinks.map((link) => (
                    <button
                      key={link.type}
                      type="button"
                      className="player-feedback-card-context-link"
                      onClick={() => navigate(getCodexUrlForEntry(link.codexEntryId, link.sectionId))}
                    >
                      {link.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )
      })()}
      <ConfirmExitModal
        isOpen={isQuitConfirmOpen}
        onClose={handleCancelQuitExercise}
        onConfirm={handleConfirmQuitExercise}
        title="⚠️ Quitter l'exercice"
        message="Vous allez quitter l'exercice en cours."
        warning="Votre progression de cette tentative ne sera pas sauvegardée."
        confirmLabel="Quitter"
      />
    </div>
  )
}

export default Player

